/**
 * Release Notes data — displayed on the Release Notes page.
 *
 * IMPORTANT: When adding a new entry, follow the guidelines in /RELEASE_NOTES_GUIDE.md
 * - Add new entries to the TOP of the array
 * - Lead with the user benefit, not the technical change
 * - Group minor UI improvements into a single "UI improvements" line
 * - Do not include infrastructure-only or developer-only changes
 */

const releaseNotes = [
  {
    version: '1.205.0',
    date: '2026-10-29',
    title: 'Clearer Defender device alerts',
    changes: [
      'Defender device alerts (such as Local Administrators Group Change) now show the Source Event as labelled sections: the device, the account, who made the change and any extra fields Microsoft recorded.',
      'On a local group change, the User column now shows who made the change and who was added or removed, for example "admin.local added S-1-5-21-…-1003". Copying it copies the full account.',
      'Session Trace and Token Use no longer appear on device alerts, where there is no sign-in session to look up.',
    ],
  },
  {
    version: '1.204.6',
    date: '2026-10-28',
    title: 'Executive Report available again',
    changes: [
      'The Executive Report button on a case is available again. It now states the same facts as the Executive Brief. The Technical Report is still withheld pending review.',
    ],
  },
  {
    version: '1.204.5',
    date: '2026-10-28',
    title: 'Mitigation steps start when the attack began',
    changes: [
      'Fixed: a password reset or sign-in method change made before the attack began was listed under "Mitigation steps taken" in the Executive Report and Executive Brief.',
    ],
  },
  {
    version: '1.204.4',
    date: '2026-10-28',
    title: 'Containment shown from when the attack began',
    changes: [
      'Fixed: a password change, account disable or session sign-out made before the attack began was shown as containment in the reports and the Attacker Window panel.',
      'The Executive Report may now say who carried out a containment step.',
    ],
  },
  {
    version: '1.204.3',
    date: '2026-10-28',
    title: 'Executive Report reliability',
    changes: [
      'Fixed: an Executive Report could fail to generate when it described deleting sent messages, or when a long case ran out of writing room.',
    ],
  },
  {
    version: '1.204.2',
    date: '2026-10-28',
    title: 'Executive Report checks, refined again',
    changes: [
      'Fixed: an Executive Report could fail to generate when it mentioned the Deleted Items folder, Microsoft removing a phishing email, or a recommendation to keep the account disabled.',
      'The Executive Report and Executive Brief can no longer say the account was disabled when no disabling is recorded.',
    ],
  },
  {
    version: '1.204.1',
    date: '2026-10-28',
    title: 'Executive Report checks, refined',
    changes: [
      'Fixed: an Executive Report could fail to generate when it mentioned the phishing email being sent to the user, the attacker accessing the account, or attachments on emails the attacker read.',
    ],
  },
  {
    version: '1.204.0',
    date: '2026-10-28',
    title: 'Executive Report and Executive Brief agree',
    changes: [
      'The Executive Report now shows the same checklist, title, containment steps and "Still exposed" note as the Executive Brief, so the two documents state every finding the same way.',
      'The Executive Report lists the files and emails tied to the attacker separately from the rest of the reviewed activity, instead of putting every item under "the attacker accessed".',
      'Mail figures in the Executive Report count only attacker-attributed activity, so they cannot disagree with the checklist.',
      'Every paragraph the Executive Report\'s writer produces is now checked against the checklist, not just the summary.',
      'Fixed: Executive Reports could fail to generate on cases with very long timelines or long output, and a correct Executive Brief summary could be rejected when it said "no evidence that the attacker…" about several things at once.',
    ],
  },
  {
    version: '1.203.1',
    date: '2026-10-27',
    title: 'Executive Brief summary check, refined',
    changes: [
      'Fixed: an Executive Brief could fail to generate when its summary described the Sent folder ("read received and sent email") on a case where the attacker sent nothing.',
      'The summary can no longer say an attempt "gave the attacker access" when every sign-in attempt was refused.',
    ],
  },
  {
    version: '1.203.0',
    date: '2026-10-27',
    title: 'Client Report and Executive Brief agree on every case',
    changes: [
      'The Client Report now says how the attack started when the analyst identified the phishing email or link, in the same words as the Executive Brief. A release of the email from quarantine is stated there too.',
      'Where no URL-click records exist for a case, both reports say so instead of saying no malicious link was clicked.',
      'Key Findings no longer counts every malicious address against the attacker\'s sign-ins, no longer lists an application count that disagreed with the checklist, and no longer says directory sync re-enabled an account that an administrator re-enabled.',
      'A single attacker event shortly after a password reset or account disable is no longer reported as a separate, weeks-long second episode.',
      'Refused sign-in attempts are never described as separate compromises.',
      'The Executive Brief\'s summary paragraph can no longer claim the attacker did something its own checklist answers No to; the summary is rewritten instead.',
      'The Executive Brief lists every attacker address with its figures, busiest first, on cases with more than 50.',
      'Fixed: the case page\'s investigation period showed both dates one day early and counted one day short.',
    ],
  },
  {
    version: '1.202.0',
    date: '2026-10-27',
    title: 'Client Report tells the same story as the Executive Brief',
    changes: [
      'When every attacker sign-in attempt was refused, the Client Report now says so ("Sign-ins Refused", or "Sign-in Blocked by MFA" when the password was entered correctly) instead of saying the account was compromised.',
      'A new "What the Attacker Did" table gives the same nine Yes / No / Not determined answers as the Executive Brief. Key Findings no longer lists mail and file counts that were not tied to the attacker.',
      'Actions Taken lists every session revoke and password change, and says when there is no record of the account being disabled.',
      'When no lure email or link click was identified, the report says how it started was not established.',
      'Fixed: "Reviewed logs" showed both dates one day early in Client and Technical reports.',
    ],
  },
  {
    version: '1.201.0',
    date: '2026-10-26',
    title: 'Domain joins no longer raise Local Group Change alerts',
    changes: [
      'When a computer joins the domain, Windows adds Domain Admins to its local Administrators group. That no longer raises a Local Administrators Group Change alert; the suppressed alerts are counted on Investigations Avoided under Device.',
      'The rule\'s edit drawer has a "Suppress domain joins" switch, on by default.',
      'A Local Administrators Group Change alert now shows only the plain-language summary. The "Technical details" list that repeated it is gone; SIDs are still in the Raw Event JSON panel.',
    ],
  },
  {
    version: '1.200.0',
    date: '2026-10-26',
    title: 'Local Group Change alerts in plain language',
    changes: [
      'A Local Administrators Group Change alert now opens with what happened in a sentence, for example "On computer dp02079, the domain group "Domain Admins" was ADDED to the computer\'s local Administrators group", followed by who did it and what it means for the group\'s members.',
      'When the change matches a computer being joined to the domain, a "Likely a domain join" line explains the second change Windows makes at the same moment.',
      'SIDs and the full list of details are under "Technical details".',
    ],
  },
  {
    version: '1.199.1',
    date: '2026-10-26',
    title: 'Local Group Change alerts: clearer group member list',
    changes: [
      'When a group is added to or removed from a local group, its member list is now labelled "Members of <group> today", so it reads as the group\'s current members rather than a list of who was added or removed.',
      'The group chip now says whether its members gained or lost the rights.',
    ],
  },
  {
    version: '1.199.0',
    date: '2026-10-26',
    title: 'Local Administrators Group Change alerts: who was added',
    changes: [
      'A Local Administrators Group Change alert now has a "Local Group Change" section in its details: the account or group that was added or removed, what kind of member it is and whether it belongs to the client\'s own domain, the local group, the device, and who made the change.',
      'When the member added is a group, the alert lists the group\'s members when we have them, or says plainly when we do not (for example, Domain Admins membership is not available to the portal).',
      'Changes that match a computer joining the domain are marked as such, so they can be closed quickly.',
    ],
  },
  {
    version: '1.198.1',
    date: '2026-10-26',
    title: 'Executive Brief: partner technicians named plainly',
    changes: [
      'When a partner technician contained an account (for example, re-enabled it after a password reset), the Executive Brief now says "PathForward IT technician" — or "partner technician" for another firm — instead of printing the long generated sign-in name Microsoft records for them.',
    ],
  },
  {
    version: '1.198.0',
    date: '2026-10-23',
    title: 'Executive Brief: password captured, sign-in blocked by MFA',
    changes: [
      'When the attacker had the password but multi-factor authentication refused every sign-in attempt, the Executive Brief now says so: it is titled "Microsoft 365 Credential Exposure — Sign-in Blocked by MFA" instead of "Microsoft 365 Account Compromise", its summary describes the approval requests that were never approved, and "Still exposed" says that only MFA stands between the attacker and the account until the password is changed.',
      'The severity no longer counts mailbox reads the attacker did not make. A case where every attempt was refused could previously read "Critical" after a re-run.',
      'The attempt times now cover every refused attempt, and Addendum D counts sign-in attempts ("6 sign-in attempts, all refused") instead of failed log rows.',
      'When "When it was contained" shows "no record", the Brief now says how long before it was generated the logs were retrieved, so a containment action taken around that time is not mistaken for one that never happened.',
    ],
  },
  {
    version: '1.197.1',
    date: '2026-10-21',
    title: 'IP Search: sort accounts',
    changes: [
      'IP Search results can be sorted by account name, first seen or last seen, in either direction, as well as by most activity (the default). The sort applies within each tenant group.',
    ],
  },
  {
    version: '1.197.0',
    date: '2026-10-21',
    title: 'IP Search: filter and group results by tenant',
    changes: [
      'IP Search results now have a filter box that matches on account, app, tenant or MSP.',
      'Results are grouped by tenant, with each tenant showing its account and sign-in totals. When the results span more than one tenant the groups start collapsed; open one to see its accounts, or use Expand all. Turn off "Group by tenant" for a single list.',
    ],
  },
  {
    version: '1.196.1',
    date: '2026-10-21',
    title: 'IP Search: clearer coverage note',
    changes: [
      'When a search starts before the earliest sign-in records, IP Search now says simply which records start later, instead of suggesting the other kind of sign-in covers the gap.',
    ],
  },
  {
    version: '1.196.0',
    date: '2026-10-21',
    title: 'IP Search: every account that used an address',
    changes: [
      'A new IP Search page in the sidebar finds every account that signed in from an IP address, or whose sign-in token was used from it, over a date range you choose (up to a year), across the tenants you can access.',
      'Each account shows its tenant, how many interactive and non-interactive sign-ins came from the address, and when it was first and last seen. Accounts in tenants outside your access are shown as a count only.',
    ],
  },
  {
    version: '1.195.0',
    date: '2026-10-21',
    title: 'Alerts: what Microsoft found unfamiliar about a sign-in',
    changes: [
      'Microsoft “Unfamiliar sign-in properties” alerts now list each property Microsoft flagged, such as the network, IP address, location or device, beside what this user normally uses over the last 7 and 30 days.',
      'Investigations Avoided now counts mailbox reads by an approved backup app under Allowlist instead of Other.',
    ],
  },
  {
    version: '1.194.12',
    date: '2026-10-15',
    title: 'Reports: other recipients of the phishing email',
    changes: [
      'When other people in the organization received the very same phishing email, the Executive Brief and Client Report now say so, and they call out anyone else who clicked a link in it.',
      'The phishing email details no longer show the sending server’s address, which was often your own mail filter and could be mistaken for the attacker’s.',
    ],
  },
  {
    version: '1.194.11',
    date: '2026-10-15',
    title: 'Executive Brief: re-enabled accounts',
    changes: [
      'An account re-enabled after its password was changed is no longer listed under “Still exposed”, and directory synchronization is named as such wherever it made a change.',
    ],
  },
  {
    version: '1.194.10',
    date: '2026-10-15',
    title: 'Executive Brief: password changes',
    changes: [
      'The Executive Brief’s containment line is now “Password changed”, and it counts a password changed on-premises (synchronized to Microsoft 365) as well as an administrator reset.',
    ],
  },
  {
    version: '1.194.9',
    date: '2026-10-15',
    title: 'Executive Brief fits on one page again',
    changes: [
      'The Executive Brief’s first page fits on one printed sheet again; some checklist notes and the re-enabled-account note were made shorter.',
    ],
  },
  {
    version: '1.194.8',
    date: '2026-10-15',
    title: 'Executive Brief generation fix',
    changes: [
      'Fixed an Executive Brief failing to generate when every attacker sign-in attempt had been refused.',
    ],
  },
  {
    version: '1.194.7',
    date: '2026-10-15',
    title: 'Executive Brief: more accurate about what the attacker achieved',
    changes: [
      'When every attacker sign-in attempt was refused and no sign-in token was issued, the Executive Brief, the reports and the Attacker Window say so, and no longer describe the attempts as access to the account. Such a case is rated Medium rather than High.',
      'If the account was enabled again after it was disabled (for example by directory synchronization), the Brief shows each disable and re-enable and does not tick "Account disabled".',
      'A new "Still exposed" line names what remains open, only when the records show it: the attacker has the password (when no password change is recorded since), the user approved a sign-in request they did not start, or the phishing email came inside an existing conversation and the sender’s mailbox may be compromised.',
      'When the clicked link’s records name the flagged phishing email, the Brief says the link was in that message.',
      'Attacker-address counts include every record from the address, with failed sign-ins shown. A phishing page recorded in place of an application is no longer listed as an application.',
    ],
  },
  {
    version: '1.194.6',
    date: '2026-10-14',
    title: 'Maintenance release',
    changes: [
      'No user-visible changes.',
    ],
  },
  {
    version: '1.194.5',
    date: '2026-10-14',
    title: 'The flagged phishing email stays in the timeline',
    changes: [
      'The “Before window” filter no longer hides an email you flagged as phishing, or a URL click you classified as Confirmed or Suspected. These are how the attacker got in, so they always come before the attacker window. Dismissing them still hides them.',
    ],
  },
  {
    version: '1.194.4',
    date: '2026-10-13',
    title: 'UI improvements',
    changes: [
      'Alerts shown in a case’s All Events timeline are now labelled “Blue 365 Signal Alert”, with “Blue 365 Signal” as the source.',
    ],
  },
  {
    version: '1.194.3',
    date: '2026-10-13',
    title: 'UI improvements',
    changes: [
      'The “app-only” label under an event’s address now shows a separator dot instead of a text code.',
    ],
  },
  {
    version: '1.194.2',
    date: '2026-10-13',
    title: 'Refused phishing attempts are described as refused',
    changes: [
      'When a phishing kit relayed the user’s sign-in but Conditional Access refused every attempt, the Attacker Window and the Client and Technical reports now say so, and that no session token was issued. They no longer describe the attempts as successful sign-ins or as access to the account.',
      'The timeline search now finds events by a “possibly” IP address, and by the name of an app shown as “app-only”.',
    ],
  },
  {
    version: '1.194.1',
    date: '2026-10-13',
    title: 'Guessed IP addresses are labelled as guesses',
    changes: [
      'Some mailbox and SharePoint events reach Microsoft through its own services and carry no user session, so their source address is unknown. The timeline used to show a guessed address for them as if it had been recorded. It now shows the recorded address, with the guess beneath it as “possibly …”. Guesses no longer colour the event or count toward that address, and do not mark the event as the attacker’s.',
      'Reads made by a third-party app on its own authority, such as an email-security scanner, now say “app-only” and name the app, instead of being given one of the user’s addresses.',
      'Re-run an investigation to apply the improved address matching to its results.',
    ],
  },
  {
    version: '1.194.0',
    date: '2026-10-13',
    title: 'Portal alerts in the case timeline',
    changes: [
      'The All Events timeline on a case now includes the portal’s detection alerts about the case user within the case dates, as “Portal Alert” rows at the time of the activity that raised them. Each shows its severity and status; expand it for the details and an “Open in Alerts” link. Alerts that only name the user, such as one about access to their mailbox by someone else, say whose alert they are.',
      'Alert rows can be dismissed like any other event, and the IP, session and safe-app filters do not hide them. They are not included in the reports.',
    ],
  },
  {
    version: '1.193.0',
    date: '2026-10-06',
    title: 'Baseline Comparison matches the alert notification',
    changes: [
      'The Baseline Comparison panel on Detection Alerts and Test Run Results now shows the comparison made when the alert fired, the same one used in the Teams, email and ticket notifications. Alerts from before 1 October are still compared in the portal, the same way. A note under the table says which applies.',
      'After “Refresh IP enrichment”, the Country, Provider, State and City rows are re-checked, each changed row is marked “updated after IP refresh”, and the heading keeps the result from alert time beside the new one.',
      'An IP on the same network as one the user normally uses now shows as partial and is no longer counted as a deviation in the heading, matching its colour.',
    ],
  },
  {
    version: '1.192.0',
    date: '2026-10-06',
    title: 'Tenant deletion: notification records included',
    changes: [
      'When a tenant is deleted together with its log data, its alert notification records are now deleted too: the delivery log, ticket links and pending ticket closures. The Edit Tenant drawer lists them as a fourth item, “Notification Dispatcher”, and the tenant stays listed until that item also reads Done.',
    ],
  },
  {
    version: '1.191.6',
    date: '2026-10-02',
    title: 'Mailbox access alerts: folder syncs',
    changes: [
      'When another user’s mailbox was reached by Outlook synchronizing its folders, the alert showed “0 items read”. Microsoft records only the folder for a sync, not the messages in it. These mailboxes now read “Folder sync · messages not listed by Microsoft” and list the folders that were synced.',
    ],
  },
  {
    version: '1.191.5',
    date: '2026-10-02',
    title: 'Set the report view from the filter bar',
    changes: [
      '“Set the report view” is now also at the start of the filter buttons on a case, so you can switch every filter to the report’s view without opening a report. When the filters already match, it reads “Report view set”.',
    ],
  },
  {
    version: '1.191.4',
    date: '2026-10-02',
    title: 'User Guide update',
    changes: [
      'Small wording change to the User Guide section on closing an alert.',
    ],
  },
  {
    version: '1.191.3',
    date: '2026-10-02',
    title: 'Close-alert shortcut',
    changes: [
      'The close-alert dialog has a “Client not monitored” button that fills in that resolution note.',
    ],
  },
  {
    version: '1.191.2',
    date: '2026-10-02',
    title: 'All Events toolbar',
    changes: [
      'Reveal subjects is now one button at the top of the All Events timeline, beside the search box, instead of in each expanded row.',
      'Hiding Teams meetings joined only from familiar devices is now the “Familiar meetings” button in the same place. It explains what qualifies and asks before hiding.',
    ],
  },
  {
    version: '1.191.1',
    date: '2026-10-02',
    title: 'Executive Brief severity after removal',
    changes: [
      'When the logs show the attacker’s added authenticator method was removed, and nothing else the attacker set up remains, the severity now reads “contained” instead of “contained; verify persistence removed”.',
    ],
  },
  {
    version: '1.191.0',
    date: '2026-10-02',
    title: 'Executive Brief: removed sign-in methods',
    changes: [
      'When an administrator removes the authenticator app the attacker registered, the Executive Brief now says so: the mitigation step names it as the attacker’s method, and the sign-in methods line on page 1 says it has since been removed.',
      'This covers authenticator apps. Re-run the investigation on an existing case to pick it up.',
    ],
  },
  {
    version: '1.190.0',
    date: '2026-10-02',
    title: 'Mark a session as the attacker’s',
    changes: [
      'Beside a session ID in an expanded event you can now choose Attacker, with a reason. Use it when the attacker created the session, for example through a phishing relay, so its activity starts at the sign-in that created it rather than when it was first used from a malicious address.',
      'Every event on a session marked this way is counted as the attacker’s, including the sign-in that created it, and none of it is hidden as earlier legitimate use. The attacker window and the Executive Brief start there. No IP address is classified by the mark.',
      'The Executive Brief states that the reviewing analyst identified the session, with the reason given.',
    ],
  },
  {
    version: '1.189.8',
    date: '2026-10-01',
    title: 'Mark a single event Safe',
    changes: [
      'An expanded event now offers Not marked, Safe and Attacker. Choose Safe to set aside one event, such as one recorded on a Microsoft service address, without marking that address Safe for the whole case.',
      'An event marked Safe is hidden with the Safe IPs, listed on the Safe IP Events tab and never counted as the attacker’s. Its address keeps its own classification.',
    ],
  },
  {
    version: '1.189.7',
    date: '2026-10-01',
    title: 'Risk column shows a dash when there is no score',
    changes: [
      'Timeline rows with no risk score now show a dash in the Risk column, instead of the text “\\u2014”.',
    ],
  },
  {
    version: '1.189.6',
    date: '2026-10-01',
    title: 'Easier-to-read sign-ins beside a Teams meeting',
    changes: [
      'In an expanded Teams meeting or call, background sign-ins now show the whole period they cover, and long application lists are shortened, with the full list on hover.',
    ],
  },
  {
    version: '1.189.5',
    date: '2026-10-01',
    title: 'Teams calls shown one row per call',
    changes: [
      'Teams calls now appear as one row per call, at the time of the call, like meetings. The row says who else was on the call and whether it completed, was missed or went to voicemail.',
      'When some of a meeting’s records are hidden by a filter or were hidden earlier, the meeting row now says so. Hiding or restoring a meeting acts on all of its records.',
    ],
  },
  {
    version: '1.189.4',
    date: '2026-10-01',
    title: 'Teams meetings shown one row per meeting',
    changes: [
      'Teams meetings now appear as one row per meeting, at the time the meeting happened. Before, each attendee showed as a separate row under the organizer’s name, at the time Microsoft recorded it, often hours later.',
      'Expanding a meeting shows the case user’s own attendance separately from the other attendees. For each attendance it shows the device, Teams app and local network address, whether that combination was seen in meetings before the attacker’s first recorded activity, and the user’s sign-ins in the same minutes.',
      '“Hide this meeting” hides all of a meeting’s records at once. When every attendance by the user came from a device seen before the attacker arrived, a prompt above All Events offers to hide those meetings together, and asks before hiding anything.',
    ],
  },
  {
    version: '1.189.3',
    date: '2026-10-01',
    title: 'Attacker-activity marking matches the IP buttons',
    changes: [
      'Marking an event as attacker activity now uses the same small buttons as setting an IP’s classification: choose “Attacker” or “Not marked”, and the current choice is filled in with a tick.',
      'When you mark an event, “Use example” fills in the suggested reason. You can still edit it before saving.',
    ],
  },
  {
    version: '1.189.2',
    date: '2026-10-01',
    title: 'Risk detections filter shows its count when on',
    changes: [
      'The “Risk detections” filter button now shows how many rows it hides when it is on, as the other filter buttons do. Before, the count disappeared once the filter was turned on.',
    ],
  },
  {
    version: '1.189.1',
    date: '2026-10-01',
    title: 'Fixes to attacker-activity marks and the Executive Brief',
    changes: [
      'The time shown on an attacker-activity mark is now correct. It was showing your local time labelled as UTC.',
      'When an administrator removes a sign-in method, the Executive Brief now lists it under the mitigation steps taken.',
    ],
  },
  {
    version: '1.189.0',
    date: '2026-10-01',
    title: 'Mark an event as attacker activity',
    changes: [
      'Some account changes are recorded with a Microsoft service’s address rather than the attacker’s. For example, a sign-in method the attacker registered through a stolen session shows My Signins’ address. You can now open the event in the timeline and choose “Mark as attacker activity”, with a short reason. The address keeps its own classification.',
      'A marked event is counted as the attacker’s everywhere: it gets an “attacker · analyst” badge, it can move the attacker window, and the Executive Brief and Report list it under what the attacker changed, not under mitigation steps, with your reason as the basis.',
      'The portal records who marked each event and when. You can edit the reason or remove the mark at any time.',
      'Adding a case’s malicious addresses to the shared blocklist now warns when one of them belongs to Microsoft service infrastructure.',
    ],
  },
  {
    version: '1.188.2',
    date: '2026-10-01',
    title: 'Clearer wording on MFA steps linked to more than one session',
    changes: [
      'When an MFA step is linked to several sessions, its note now names only the sessions that were also used from a malicious address, and reads correctly for one session or several.',
    ],
  },
  {
    version: '1.188.1',
    date: '2026-10-01',
    title: 'Attacker window: stricter linking of MFA steps to their sign-in',
    changes: [
      'An MFA step is now linked only to sign-in records of the same person within 10 minutes of it. Microsoft sometimes reuses one transaction id across hours, across users on a shared computer, or as a placeholder, and evidence from those unrelated records no longer counts against the step.',
      'A home connection that signs in over IPv4 and IPv6 from the same provider at the same moment is no longer mistaken for a relaying proxy.',
    ],
  },
  {
    version: '1.188.0',
    date: '2026-09-30',
    title: 'Attacker window: MFA steps logged without a username are no longer counted as the attacker',
    changes: [
      'Microsoft records many multi-factor sign-in steps without a username. The portal used to count every one of them as attacker activity, which could start the attacker window days too early and add a second compromise episode that never happened. Each step is now counted only when its own sign-in links it to the attacker: it ran from an address you classified malicious, it started from several addresses within seconds (the pattern of a relaying proxy), or it belongs to a session that was also used from a malicious address.',
      'The “recovered · no UPN” label on these rows now says which session the step belongs to and whether that session was also used from a malicious address, instead of a general phishing warning. Steps that belong to the user’s own sessions are hidden when their address is marked Safe, like any other sign-in.',
      'Reports and the Executive Brief count the same steps as the screen. The phishing-kit section lists only sign-ins through a phishing-kit application, and labels each address by your classification.',
      'Older investigations link each step to the nearest matching sign-in instead, and say so on the row. Re-running the investigation links them exactly.',
    ],
  },
  {
    version: '1.187.0',
    date: '2026-09-30',
    title: 'Deleting a client’s logs: progress from each data team',
    changes: [
      'When a tenant is scheduled for deletion together with its log data, the Edit panel now shows each data team’s progress (Log Collection, Silver and Pipeline): not reported yet, done, failed and retrying, or refused and needing review. The tenant stays listed until all three confirm.',
      'Log deletions are now scheduled one tenant per day. If another tenant already holds the date you pick, the dialog says so and offers the next free date. The default date moves to a free day on its own.',
      'The deletion wording now says that cost history and operational records are kept. Only log data is deleted.',
    ],
  },
  {
    version: '1.186.0',
    date: '2026-09-29',
    title: 'Offboarding a client: choose Paused, Retained or Pending deletion',
    changes: [
      'Deactivating a tenant now asks what should happen next. Paused means collection will resume later. Retained means collection stops but the logs are kept, and the tenant can still be selected for new investigations. Pending deletion means the tenant is deleted on a date you choose.',
      'A pending deletion defaults to 30 days after deactivation, and can be no sooner than 2 days after it. You choose whether the tenant’s log data is deleted too, or only its portal records. Scheduling a deletion requires a global admin.',
      'The tenant list shows each state as Paused, Retained or “Deletes in N days”. The Edit panel shows the deactivation and deletion dates. Use “Change state” to postpone or cancel a deletion, and Reactivate to turn collection back on.',
    ],
  },
  {
    version: '1.185.0',
    date: '2026-09-29',
    title: 'Inbox-rule alerts now spell out what the rule does',
    changes: [
      'Alerts about inbox-rule changes now open with a plain summary of the rule, with highlights for risky actions such as forwarding or deleting mail, followed by its conditions, exceptions and actions. This appears on the Alerts page and on test-run results.',
      'Rules created in Outlook desktop now show the name of the folder they move mail to, which the audit record itself leaves out. A redirect is shown as a redirect rather than a forward.',
      'Older alerts, and alert types without this summary, display as before.',
    ],
  },
  {
    version: '1.184.5',
    date: '2026-09-21',
    title: 'Behind the scenes: releases are now tested and staged automatically',
    changes: [
      'No change to what you see or how the portal works. Every release now passes an automated test suite before it reaches the test environment, and production updates are scheduled outside business hours (weekday evenings) unless an urgent fix is needed.',
    ],
  },
  {
    version: '1.184.4',
    date: '2026-09-21',
    title: 'Slow-start guard now checks the right things before signing in to Azure',
    changes: [
      'Another day of measurements showed the service\'s slow Azure sign-in is not caused by workers starting together (a single worker starting alone stalled just the same). The pre-flight check now asks the identity service for the exact credential the sign-in needs and confirms Microsoft\'s sign-in directory is reachable — both within a hard 3-second limit — before attempting the sign-in, and a worker that does stall backs off progressively instead of re-stalling on every scheduled task.',
      'No change to what you see: the "slow to respond — retrying" notice and automatic retry remain.',
    ],
  },
  {
    version: '1.184.3',
    date: '2026-09-20',
    title: 'Slow-start fix retargeted at the cause the first day of data revealed',
    changes: [
      'The 1.184.2 slow-start guard was measured against a day of real traffic and found to be aimed at the wrong step: the service\'s sign-in to Azure was stalling because several workers start at once and contend for a shared credential cache, not because the identity service was unreachable. The service now uses a per-process credential context (the configuration Microsoft recommends for this hosting model), and a stalled sign-in can no longer chain into the next one.',
      'No change to what you see: the "slow to respond — retrying" notice and automatic retry from 1.184.2 remain.',
    ],
  },
  {
    version: '1.184.2',
    date: '2026-09-19',
    title: 'Portal no longer hangs for minutes when the service is slow to start',
    changes: [
      'Opening the portal used to show an endless spinner — sometimes for several minutes — when the service behind it was slow to start, and reloading made it worse. The service now bounds that slow start to a few seconds and recovers on its own, so the portal opens promptly.',
      'If the service is still slow, the loading screen now says so ("slow to respond — retrying, attempt 2 of 3") and retries automatically instead of spinning silently; if it cannot be reached after three attempts you see the existing "Can\'t reach the service" screen with a Retry button.',
    ],
  },
  {
    version: '1.184.1',
    date: '2026-09-16',
    title: 'Executive Brief: readable per-day containment, and multi-episode briefs no longer split across pages',
    changes: [
      'The "When it was contained" list now groups each action\'s times by day, one day per line, so a run of containment timestamps is easy to scan instead of running together on a single line.',
      'On a case with more than one compromise episode, the exposure and session cut-off notes now sit full-width beneath the three columns, so the one-page brief no longer overflows and splits across two sheets when printed or exported to PDF.',
    ],
  },
  {
    version: '1.184.0',
    date: '2026-09-16',
    title: 'Executive Brief now shows the full containment for every episode',
    changes: [
      'On a case with more than one compromise, the "When it was contained" panel used to show only the first episode\'s account disable, token revoke and password reset — a second episode\'s own containment (for example a later account disable and password reset) was left off. It now lists every occurrence of each action, so the containment for each episode is shown.',
      'The wording around multiple episodes was tidied to match: the exposure window reads in the plural, the session cut-off names the most recent episode and notes that each earlier episode ended at its own containment, and the "credentials captured" note reads per episode. The AI-written bottom line is now told to describe separate compromises rather than one continuous intrusion.',
    ],
  },
  {
    version: '1.183.0',
    date: '2026-09-16',
    title: 'Cases with two separate compromises no longer read as one continuous window',
    changes: [
      'When a case contains more than one compromise — for example a phishing-kit sign-in that is remediated, then a fresh one weeks later — the Attacker Window and the reports now describe each episode separately (when it began and how it was contained) instead of a single span that implied continuous access the whole time. A token revoke on its own does not start a new episode; only disabling the account or resetting the password does.',
      'The Attacker Window panel shows the episode count and lists each episode with its containment; the Client and Technical reports and the Executive Brief narrate the episodes rather than one long duration.',
    ],
  },
  {
    version: '1.182.1',
    date: '2026-09-16',
    title: 'The Executive Brief now works on AiTM cases, and reflects “no attacker access found”',
    changes: [
      'The AI Executive Brief can now be generated on an attacker-in-the-middle case that has no separate attacker IP — previously it refused, because the compromise is a phishing-kit sign-in on the user’s own address. A successful phishing-kit sign-in in the investigation now anchors the report.',
      'When a case is marked “No attacker access found”, the Brief describes a window of exposure rather than confirmed access, discloses it as the reviewing analyst’s determination, and never states that the attacker accessed, read, or took anything from the account.',
    ],
  },
  {
    version: '1.182.0',
    date: '2026-09-16',
    title: 'Record “credentials captured, but no attacker access found” on AiTM cases',
    changes: [
      'On an attacker-in-the-middle case the credentials and session token are captured through a phishing-kit sign-in on the user’s own IP — sometimes with no evidence the captured access was ever used. A global admin can now record “No attacker access found” on such a case. The Client Report and the Attacker Window then describe a window of exposure rather than confirmed attacker access, stated as the analyst’s determination (never “the attacker did not access the account”).',
      'The attestation is only offered once the case supports it: a successful phishing-kit sign-in anchors the case, no IP is Malicious or Suspect, every address with a successful sign-in has been classified, and the non-interactive sign-in feed has been retrieved and reviewed. The checkbox lists whatever is still outstanding.',
    ],
  },
  {
    version: '1.181.0',
    date: '2026-09-15',
    title: 'Reports work on phishing-kit (AiTM) cases with no malicious IP',
    changes: [
      'On an attacker-in-the-middle case the compromise is a phishing-kit sign-in that happens on the user’s OWN IP address — so there is often no separate attacker IP to mark Malicious, and every report button stayed disabled on a proven compromise. A successful phishing-kit sign-in now anchors the attacker window on its own, so the Client Report and Executive Brief can be generated.',
      'The Attacker Window panel and the reports say plainly when the window is anchored on a phishing-kit sign-in (“no IP classified malicious”) instead of showing “from 0 malicious IPs”, and never present the user’s own address as the attacker’s.',
    ],
  },
  {
    version: '1.180.4',
    date: '2026-09-15',
    title: 'Phishing-kit sign-ins stay in view and reach the reports',
    changes: [
      'The sign-in that an attacker-in-the-middle phishing kit completes appears on the user’s OWN IP address, so it was being hidden by the “Safe IPs” filter — even though it’s the single most important row in the case (it’s how the password and session token were captured). These sign-ins now always stay visible in All Events, like remediation actions, regardless of the Safe IPs pill.',
      'The Client and Technical reports now include a dedicated “How the Account Was Compromised — Phishing-Kit Sign-In” section that lists these sign-ins and states plainly that the address shown is the user’s own (relayed through the attacker’s proxy), not an attacker IP — and that this is the moment the credentials and session were captured.',
    ],
  },
  {
    version: '1.180.3',
    date: '2026-09-15',
    title: 'A portal outage no longer looks like “Access Denied”',
    changes: [
      'If the portal service is briefly unreachable (for example, just after a deployment), you now see a clear “Can’t reach the service — Retry” screen instead of an incorrect “Access Denied” that claimed you lacked permission. Your access hasn’t changed; the service just needs a moment.',
      'The “Access Denied” screen — shown only when your account genuinely isn’t in any MSP’s portal group — no longer suggests joining the full-administrator group. If you see it, the right next step is to be added to your MSP’s portal group.',
    ],
  },
  {
    version: '1.180.2',
    date: '2026-09-14',
    title: 'PSA close note reads correctly on real closes',
    changes: [
      'When you close an alert’s linked AutoTask ticket, the note added to the ticket is now titled “Alert resolved in Blue365Signal” instead of “Blue365Signal test update”, so a genuine close no longer reads as test activity on the client’s ticket.',
    ],
  },
  {
    version: '1.180.1',
    date: '2026-09-14',
    title: 'Closing an alert reliably closes its linked PSA ticket',
    changes: [
      'When you close an alert that has a linked PSA ticket, the “Close alert” button now waits for the ticket link to load before it can be clicked. Previously, closing very quickly could skip the ticket-close step silently — the alert closed, but the PSA ticket stayed open with no warning. The button briefly shows “Checking ticket…” while it looks up the link.',
      'AutoTask ticket closes are now verified. The portal reads the ticket back after setting it complete and reports an accurate result, instead of assuming success — so a close that doesn’t actually take (for example, a misconfigured completed status) is surfaced rather than hidden.',
    ],
  },
  {
    version: '1.180.0',
    date: '2026-09-12',
    title: 'Baseline Comparison colors + IP Intelligence user count',
    changes: [
      'Baseline Comparison now separates “matches your established location” from “matches only your recent week”. An Event Value is green when it matches your 30-day norm, amber when it matches only the last 7 days (recent travel — or a recent window already affected by the activity being investigated), and red only when it matches neither. The panel header now shows deviations and partial matches separately.',
      'The IP Intelligence “same-tenant users” count now agrees with the user list beneath it. The count previously included Microsoft’s “Not Available” placeholder sign-in legs, so it could read one higher than the list of real users.',
    ],
  },
  {
    version: '1.179.7',
    date: '2026-09-11',
    title: 'Case timeline: AiTM sign-ins show the phishing-kit URL instead of “Unknown”',
    changes: [
      'On an adversary-in-the-middle sign-in, the malicious application is a spoofed URL that Microsoft records with no friendly name, so the Application column (and the row description) read “Unknown”. Those rows now show the URL itself — e.g. portal.microsoftonline.com.orgid.com — so the phishing domain is visible at a glance.',
    ],
  },
  {
    version: '1.179.6',
    date: '2026-09-11',
    title: 'Case timeline: “Failed hidden” now also hides failed correlation sign-ins',
    changes: [
      'With “Failed events” hidden, a cluster of failed sign-ins pulled in by lookback correlation could still appear (and show as an “all failed” access period), because the lookback re-injection didn’t honour the Failed filter. It now does — consistent with how it already honoured the Safe IP, pre-compromise, and safe-session filters.',
    ],
  },
  {
    version: '1.179.5',
    date: '2026-09-11',
    title: 'Case timeline: AiTM phishing-kit sign-ins are surfaced, not buried',
    changes: [
      'When a compromise involves an adversary-in-the-middle phishing kit, the landing sign-in is often recorded on the victim’s own IP address, so it was being folded into the normal “access period” for that address and hidden. Those sign-ins now always appear as their own row in the case timeline.',
      'They also carry a red “phishing-kit app” flag when the sign-in application is a spoofed URL (rather than a normal app), and a recovered sign-in whose username Microsoft left blank keeps its amber “recovered” flag — so the row that defines the compromise stands out instead of sitting inside a low-risk group.',
    ],
  },
  {
    version: '1.179.4',
    date: '2026-09-11',
    title: 'Investigations: token-replay evidence no longer missed for Teams and app sign-ins',
    changes: [
      'The alert “Token Use” pivot and the case “Check real-user evidence” tool now match a user’s full identity, not just their email address. Non-interactive sign-ins from Teams and other apps are often recorded under an internal account ID rather than the email, and those were previously being left out — so both tools now surface the complete picture of where a user’s tokens were used.',
    ],
  },
  {
    version: '1.179.3',
    date: '2026-09-10',
    title: 'Reliability: clearer signalling if a log-collection call ever fails to authenticate',
    changes: [
      'Behind-the-scenes work so that, now the log-collection service enforces its new authentication, any failure to attach a credential is raised loudly for the team instead of failing quietly. No change to how the portal looks or works.',
    ],
  },
  {
    version: '1.179.2',
    date: '2026-09-10',
    title: 'Reliability: hardening behind tenant onboarding and log backfills',
    changes: [
      'Behind-the-scenes work to keep tenant activation, license-tier probes, and log backfills working smoothly as the log-collection service tightens its authentication. No change to how the portal looks or works.',
    ],
  },
  {
    version: '1.179.1',
    date: '2026-09-02',
    title: 'Executive Brief: page one fits on one printed sheet again',
    changes: [
      'The “what the attacker did” checklist was splitting across two sheets when the brief was exported to PDF. Page one now fits, with the whole checklist on it.',
      'The “Other accounts to review” note is much shorter, and it — along with the quarantine-release note — now runs the full width of the page instead of being squeezed into a narrow column.',
      'Both notes still say the same things: who released the message, that it was reported to Microsoft as a false positive, how many other accounts received mail from that sender, and that this is a lead rather than a finding of compromise.',
    ],
  },
  {
    version: '1.179.0',
    date: '2026-09-02',
    title: 'See which other users have signed in from an IP address, from the case IP table',
    changes: [
      'Expand any row on a case’s IP Addresses tab and click “Check other users” to see who else signed in from that address in the last 30 days — in this tenant, and in other tenants you have access to.',
      'Addresses in tenants you cannot access are still summarised as a count only, so you can tell whether an address is shared more widely without seeing names you are not entitled to.',
      'The lookup runs only when you click it, and the panel is the same one the Alerts page has always used.',
    ],
  },
  {
    version: '1.178.0',
    date: '2026-09-02',
    title: 'Reports now state that a phishing email was released from quarantine — and which other accounts the sender reached',
    changes: [
      'The Executive Brief and the Client Report now say when the account holder released the flagged phishing email from quarantine, and whether they also reported it to Microsoft as a false positive. Both were previously absent from every report.',
      'Both reports also list other accounts in the tenant that received mail from the same sender, with each message’s delivery status and whether it was released from quarantine — so you know where to look next.',
      'That list is framed as a lead, not a verdict: it says plainly that it does not establish those accounts were compromised, and it never claims they received the same email — only mail from the same sender.',
      'Where a message was not released, the report shows a dash rather than “No”. Another person’s mailbox actions are outside what this case collected, so the report does not claim either way.',
      'The list is scoped to within 5 days of the attacker activity in the case. If no attacker address has been classified yet, the report says it covers the whole reviewed period instead of claiming a 5-day window that does not exist.',
      'If more than 25 messages qualify, the report says “showing 25 of N” rather than quietly shortening the list.',
      'Clicking Generate now saves your phishing flags and URL classifications first and waits for the save. Previously, flagging an email and generating immediately could produce a report with that finding missing.',
      'The AI summary is now required to mention the quarantine release in words, and a summary that wrongly claims the message stayed in quarantine is rejected outright.',
    ],
  },
  {
    version: '1.177.1',
    date: '2026-09-02',
    title: 'Fixed: case pages could open completely blank',
    changes: [
      'A change in the previous release made every case page render as a blank screen. The page is restored; nothing was lost and no case data was affected.',
      'An automated check now catches this class of mistake before a build goes out.',
    ],
  },
  {
    version: '1.177.0',
    date: '2026-09-02',
    title: 'A phishing email released from quarantine now says so — and stays visible',
    changes: [
      'When someone releases a message you have flagged as the phishing email, the timeline used to read only “performed Release-QuarantineMessage”. It now reads who released it, who it was from, its subject, and whether they also reported it to Microsoft as a false positive.',
      'That row also used to vanish once the Safe IPs filter was on — quarantine releases carry a Microsoft address, not the user’s — so it disappeared from the report view. It now stays visible with every filter engaged.',
      'Only the release of the message you flagged is treated this way. Other quarantine releases in the same case, and previewing or submitting a quarantined message, are unchanged.',
      'Dismissing the row still hides it. The change works around the filters, never around your own judgement.',
    ],
  },
  {
    version: '1.176.1',
    date: '2026-09-02',
    title: 'Report generator no longer gets stuck on “Generating…” after you leave and come back',
    changes: [
      'If you started an Executive Report or Brief, closed the dialog, and reopened it after the report finished, the dialog kept showing “Generating…” forever — which also greyed out the Generate button, so you could not start a new one.',
      'Reopening the dialog now checks the real state: a finished report simply appears in Prior documents with a View button, and a report still in progress resumes its progress display. Either way the Generate button is available again as soon as nothing is actively running.',
    ],
  },
  {
    version: '1.176.0',
    date: '2026-08-26',
    title: 'Executive reports no longer lose the end of a long case',
    changes: [
      'On a long investigation the report\u2019s timeline was cut to the earliest 150 entries, and because containment is always last, the AI writing the summary never saw the remediation \u2014 on one real case it saw Jul 9 to Jul 29 of a window that closed Aug 3.',
      'The limit is now 1,500 entries, which is where the AI service\u2019s own capacity actually binds. Every case in the system now fits it whole.',
      'If a case ever does exceed it, the entries kept are chosen by significance \u2014 every containment step, the first and last activity of each day, and the first of each kind of action \u2014 never simply the earliest ones.',
      'And the report SAYS SO: the scope paragraph states how many entries there were, how many are listed, and that containment was kept. Truncation used to be silent.',
      'Reports also now carry a complete day-by-day summary of incident activity, so the AI always sees the full shape of the case even when individual entries are sampled.',
    ],
  },
  {
    version: '1.175.0',
    date: '2026-08-26',
    title: 'Report entry counts now match the case timeline exactly',
    changes: [
      'A report could say its records were shown as one more timeline entry than the case actually displays. Email trace records are stored per recipient, so one message sent to several people is several records and a single row — the case view has always merged them, and the report was not.',
      'Record counts are unchanged; only the entry count moves, and it now equals what the All Events tab shows.',
    ],
  },
  {
    version: '1.174.0',
    date: '2026-08-26',
    title: 'The case scope notice no longer appears when it does not apply',
    changes: [
      'Every case showed a notice saying non-interactive sign-ins (token refresh, SSO reuse, PRT replay) were not included in the results. That is no longer true for cases investigated with the current engine, which does collect them.',
      'The notice now appears only where the feed genuinely is missing — an older investigation run, or a run where that query failed. It tells you the timeline and IP list may be incomplete only when they actually may be.',
      'Re-run an older case to pick up the feed; the notice will disappear on its own.',
    ],
  },
  {
    version: '1.173.0',
    date: '2026-08-26',
    title: 'Reports say how much of an address’s activity was token reuse',
    changes: [
      'The attacker addresses addendum showed a single event total per address. That total mixes recorded events with authentications counted inside aggregated non-interactive records, so a short burst of token reuse and hours of hands-on access could look the same.',
      'It now shows an “Of which token reuse” column beside the total, with a note explaining that those are sign-ins made using an already-issued token rather than by entering credentials — and that they are recorded in aggregate, so a large number can be a brief burst.',
      'A report generated before this change shows a dash rather than a number, because those reports cannot tell you either way.',
    ],
  },
  {
    version: '1.172.0',
    date: '2026-08-26',
    title: 'Token activity now counts toward the attacker window and the report',
    changes: [
      'Non-interactive sign-ins (token refresh, SSO reuse, token replay) from an address you classified Malicious now count as attacker activity when the attacker window is calculated. They did not before, so the window could close at the last interactive sign-in \u2014 and the \u201cPost-window\u201d filter would then hide the token use that happened after it.',
      'Reports carry a new figure, \u201cToken authentications from malicious addresses\u201d, counted separately from interactive sign-ins. Authenticating and reusing a stolen token are different findings and are no longer added together.',
      'The two interactive figures now say \u201cinteractive\u201d in their labels, so neither reads as the total.',
      'Safe addresses and failed authentication buckets contribute to neither the window nor the figures.',
    ],
  },
  {
    version: '1.171.0',
    date: '2026-08-26',
    title: 'Report counts now match what you can see on the case',
    changes: [
      'A report could say \u201c167 events were incident related\u201d while the All Events table showed 43 rows. Both were right about different things: the count included records the timeline never displays \u2014 inbound mail, and link clicks you had ruled out by confirming a different one \u2014 which no filter pill can reach, so they were counted as incident-related without anyone deciding they were.',
      'The count is now taken from the same view you sign off on. Records the case cannot display are set aside instead, and a phishing-flagged message still counts \u2014 your flag is what makes it part of the incident.',
      'The report also states its own grouping: where it shows fewer timeline entries than records, it says so and says why (a run of sign-ins from one address is one access period; the several records Microsoft writes for one admin action are one entry).',
      'New \u201cBefore window\u201d filter pill, off by default: hides activity from before the attacker window opened, except from addresses classified Malicious. The existing \u201cPre-compromise\u201d pill only reaches events on a session the attacker touched, so file and mailbox activity from before they arrived had no pill at all.',
      'Read the pre-window activity before hiding it \u2014 that is where an earlier foothold would show. Engaging the pill is you saying you looked.',
      'Directory audit rows for routine Azure MFA maintenance no longer appear in All Events; they were already hidden on the Directory Audit tab and in reports.',
      'The Executive Report and Technical Report buttons are temporarily disabled pending review. The Client Report and Executive Brief are unaffected.',
    ],
  },
  {
    version: '1.170.0',
    date: '2026-08-26',
    title: 'Record that you reviewed the Unified Audit Log, and the report stops hedging',
    changes: [
      'A checklist line could read \u201cno SharePoint and OneDrive activity was recorded in the reviewed period\u201d \u2014 a claim about the whole period, on a log whose records do not cover it. It now says \u201cin the records reviewed\u201d, which is what the figure is actually counted over.',
      'New \u201cUAL reviewed\u201d checkbox in the case header (global administrators only): tick it when records for the case were retrieved directly from Microsoft\u2019s Unified Audit Log, outside the log feeds we monitor.',
      'When it is ticked, findings stop being hedged \u2014 a line that would have read \u201cNo correlating evidence found\u201d reads a plain \u201cNo\u201d. Coverage is measured from what reached our own data store, so work done outside it otherwise reads as a gap and the report understates what was examined.',
      'The report says so, in its scope section, and credits the reviewing analyst: an attestation that strengthens a finding is visible rather than silent.',
      'It is recorded against the case with who set it and when, and it can only be set by a global administrator.',
    ],
  },
  {
    version: '1.169.0',
    date: '2026-08-25',
    title: 'Applications that were only ever recorded together now share one line',
    changes: [
      'The applications list gave every application its own line. Some of them are only ever recorded as part of a group \u2014 the sign-in records they come from cover an hour at a time and name every application seen in that hour at once, so an application can appear simply because it shared that hour with another.',
      'Those are now combined into a single line that says so, with a second column stating for each entry whether it was recorded on its own or only alongside others. On the case that prompted this, six applications collapsed into one line \u2014 including two nobody could find anywhere else in the case, because there was nothing to find.',
      'The list also now says when a record named more applications than it listed, so a shortened list is never presented as the whole picture.',
      'Removed the key under the checklist and the paragraph listing the addenda. Each addendum has its own heading and states its own position, and the answers are written to be read without a key.',
      'Where a log does not cover the whole period, the report no longer prints a note about which records exist. The line reads \u201cNo correlating evidence found\u201d and the supporting text says what was reviewed and what came of it.',
    ],
  },
  {
    version: '1.168.0',
    date: '2026-08-25',
    title: 'Clearer wording where a log does not cover the whole period reviewed',
    changes: [
      'Removed the note under the attacker\u2019s active period that said the duration was a minimum because earlier days had not been examined. The period reviewed is already stated on the report, and repeating it as a caveat made a solid finding look uncertain.',
      'Where a log\u2019s available records do not cover the whole period, the checklist now reads \u201cNo correlating evidence found\u201d rather than \u201cNot determined\u201d \u2014 the records we hold were reviewed and nothing was tied to the attacker, which is a finding, not a gap.',
      'That line no longer says the log \u201cwas not being collected\u201d, which is not something the check can tell and is wrong wherever data has been backfilled. It now states the plain fact: the earliest record available for the tenant, and the date the reviewed period opened.',
      'A line with attacker activity on it is never reworded or withdrawn, and a log covering the whole period still reads a plain \u201cNo\u201d.',
    ],
  },
  {
    version: '1.167.0',
    date: '2026-08-25',
    title: 'The report now shows that mail the attacker sent was deleted moments later',
    changes: [
      'The report listed the messages the attacker sent and the messages it deleted as two separate counts. On the case that prompted this they were the same events — every deletion removed a message the attacker had just sent, within seconds.',
      'Each sent message now shows, on its own row, when it was deleted and whether it was permanently purged — for example “deleted from \\Sent Items 26 seconds after sending, and permanently purged after 60 seconds”.',
      'Where a message was deleted but NOT permanently purged, the report says so explicitly, because a copy may still be recoverable. That is often the most useful thing on the page.',
      'The link is established from the message identifier recorded on both the send and the deletion, so it is a direct match rather than an inference from timing.',
    ],
  },
  {
    version: '1.166.2',
    date: '2026-08-25',
    title: 'The report no longer claims the tenant’s full log volume was reviewed',
    changes: [
      'The scope paragraph said the investigation reviewed “the tenant’s full recorded log volume”. That was not true — for this kind of case some log sources begin collecting partway through the period being reviewed, and one may not be collected at all. It now says “the log volume available for this tenant in this period”.',
      'This matters because that sentence sat next to findings such as “no files were opened”. A reader told the full log volume was reviewed would take that as an all-clear.',
      'Where a specific log source did not cover the whole period, the report says so against the individual finding it affects, rather than as a general disclaimer — a general one would imply that everything it did not mention was complete.',
      'A related note that said “No SharePoint or OneDrive activity was recorded” has been reworded for the same reason: it described a source that may not have been collected.',
    ],
  },
  {
    version: '1.166.1',
    date: '2026-08-25',
    title: 'Fix: the report’s own totals now add up',
    changes: [
      'The provenance line said “Records in run 14,712” while the scope sentence on the same page said 1,872 incident-related plus 12,401 normal activity — which comes to 14,273. The count was including things that are not events: the per-address and per-application summaries, and the tenant’s secure-score readings.',
      'It now counts the records the report’s figures are actually drawn from, so the two totals agree.',
      'Flagged phishing messages are deliberately not counted here. They are real events, but they are not part of the timeline these figures come from and are listed separately on the case.',
    ],
  },
  {
    version: '1.166.0',
    date: '2026-08-25',
    title: 'The report now names every device the attacker added, not just most of them',
    changes: [
      'One attacker device was missing from reports: it was added to the tenant eleven days before that log source began collecting for the tenant, so there was no record of the act to find it by — and it was the device used to get in.',
      'Devices are now identified by their Microsoft device ID rather than by their name. A name can be changed, can appear under three different labels, and can be shared by two machines; the ID cannot.',
      'The record of the device being added is still used first, because it is the most direct evidence. The ID is used only where no such record exists, and the report then says so plainly rather than presenting a first sighting as the moment it was added.',
      'Where a device name cannot be matched to exactly one device ID, the device is left out rather than guessed at — a missing entry understates, a wrong one sends remediation to the wrong machine.',
      'Investigations already run need to be re-run to pick this up.',
    ],
  },
  {
    version: '1.165.1',
    date: '2026-08-25',
    title: 'Fix: the new log-coverage check could mark every answer “Not determined”',
    changes: [
      'The check added in the previous release read the wrong part of its own query result, so it found no log sources at all and would have reported every one as never collected — turning every “No” in the brief into “Not determined”.',
      'Caught before any report was generated with it. Fixed, and the check now reports itself as unavailable if it cannot make sense of the result, which leaves the brief exactly as it was rather than adding doubt that is not real.',
    ],
  },
  {
    version: '1.165.0',
    date: '2026-08-25',
    title: 'The brief says when the length of the attack is only a minimum',
    changes: [
      'The line “When they were active … ~25 days” read as a settled figure. Where the earliest activity found sits right at the start of the period reviewed, that number is a minimum — anything earlier was never examined — and the brief now says so.',
      'It appears only where it applies. If the activity began well inside the period reviewed, the days before it were examined and the figure is a finding, so no caveat is added.',
      'The wording says the earlier period was “not examined”, and deliberately never says no evidence was found there — nobody looked, and saying otherwise would close a question that is still open.',
    ],
  },
  {
    version: '1.164.0',
    date: '2026-08-25',
    title: 'The brief no longer reports “No” for a log that was not being collected',
    changes: [
      'A checklist line could read “No — the records were reviewed and nothing was attributed” about a log that had never been collected for that period. A log that is not being collected returns no results, and no results was being treated the same as “we looked and found nothing”.',
      'The brief now measures, per log source, whether that source actually held data across the period being reviewed. Where it did not, the line reads “Not determined” and says which log was unavailable and from when.',
      'Only negative answers are withdrawn. A count of events that were actually seen is never reduced — a late-starting log means the number is a minimum, not that it is wrong.',
      'If the measurement itself cannot be taken, nothing changes. A temporary problem reading the data will never turn a fully evidenced brief into one that determines nothing.',
      'This applies to every existing case with no need to re-run the investigation.',
    ],
  },
  {
    version: '1.163.1',
    date: '2026-08-24',
    title: 'The brief now explains how it works out when access ended',
    changes: [
      'The line “Access is treated as ending …” gave a time that matched no event in the case and appeared to disagree with the containment steps listed just below it. It now shows the working: “(password reset + 90min life)”.',
      'The time is the containment action plus the tenant’s token lifetime, because revoking sessions stops new sign-ins while a token already issued can still be used until it expires. That is deliberately cautious — it just was not explained.',
      'The action named is whichever one the calculation actually used, so a case contained by revoking tokens will say “tokens revoked”.',
    ],
  },
  {
    version: '1.163.0',
    date: '2026-08-24',
    title: 'The brief now lists the machines the attacker joined, and stops contradicting itself',
    changes: [
      'New Addendum E lists every device the attacker joined to the tenant, with the directory object id and when it happened. The brief previously said how many there were and named none of them — and a joined device keeps access after a password reset, so the object id is what an administrator needs to remove it.',
      'The summary could state that the attacker altered the account’s sign-in methods while the checklist on the same page said none was attributable to the attacker. The count behind that claim included the security team’s own password reset and MFA re-registration; it is now limited to activity actually attributed to the attacker, and those records are no longer shown to the AI assistant at all.',
      'The header date range disagreed with the provenance block by a day at each end, which also made the brief appear to list mitigations after its own end date. A case date range is now shown exactly as entered rather than being shifted by the display timezone.',
      'The note pointing at the addenda named a letter that did not exist and said an addendum appears only when it has something to list, which is no longer how they work.',
    ],
  },
  {
    version: '1.162.9',
    date: '2026-08-24',
    title: '“Not determined” is now reserved for what could not be checked',
    changes: [
      'A checklist row could read “Not determined — no SharePoint and OneDrive activity was recorded”, which states a finding and then declines to report it. Where a log was read and simply held nothing, the answer is now “No”.',
      '“Not determined” now means only one thing: the records could not be read, or they cannot tell the attacker apart either way. It is no longer used for “we looked and found nothing”.',
      'The wording under the checklist was updated to match, since “No” now also covers a category with no records at all.',
    ],
  },
  {
    version: '1.162.8',
    date: '2026-08-24',
    title: 'Tighter wording on empty addenda, and analyst notes stay internal',
    changes: [
      'When an addendum has nothing to list, it now says so once. It used to add “No individual files can be listed here, and that is not evidence that none was touched”, which repeated the heading above it, and the same section could print two sentences making the same point.',
      'A note following the bold label is now capitalised — it could read “Not determined. no SharePoint and OneDrive activity was recorded.”',
      'The attacker-addresses addendum no longer shows the “Analyst note” column. Those notes are written for the case file in internal shorthand and were never intended for a client to read; this applies to both the executive report and the brief.',
      'Those notes are also no longer sent to the AI writing assistant, so they cannot influence the wording of the summary either.',
    ],
  },
  {
    version: '1.162.7',
    date: '2026-08-24',
    title: 'Reports now say “None” where they wrongly said “Not determined”',
    changes: [
      'The “Opened or downloaded files” line said “Not determined” on cases where every file record carried an address and none of them was the attacker’s — which is a plain “None”. It now distinguishes the two by asking whether the records contained anything that could have identified the attacker, rather than only whether they did.',
      'Where part of a category genuinely cannot be judged either way, the line still says “None” but states how much could not be attributed, so the unknown portion is not hidden.',
      'Reports and briefs are now headed “Microsoft 365 Account Compromise” rather than just “Account Compromise”.',
      'The “about” paragraph no longer lists log sources. It named four, where an investigation reads fifteen log types plus sign-in, enrichment, device and identity data — so the list understated the work.',
      'The applications addendum no longer shows a “Status” column. It was reporting every application as unrecognised, including Microsoft’s own, so it has been removed rather than left saying something untrue.',
    ],
  },
  {
    version: '1.162.6',
    date: '2026-08-24',
    title: 'The executive brief no longer skips an addendum letter',
    changes: [
      'A brief could run from “Addendum A” straight to “Addendum C”, which reads as a page that went missing. Addenda A to D are now always present.',
      'Addendum B — Files — is the one that was most often dropped, and it is the one that matters most when empty: on a case where thousands of file events exist but none can be tied to the attacker, its absence removed the only place the brief showed that file activity had been examined at all. It now says which of those two situations applies, in the same words page 1 uses.',
      'The note explaining that only the summary was written by an AI assistant used to refer to a “bottom line” section that no longer exists, so a reader could not tell which part was meant.',
      'The provenance block no longer prints the internal source-run identifier.',
    ],
  },
  {
    version: '1.162.5',
    date: '2026-08-24',
    title: 'A printed executive brief keeps the space beside the summary',
    changes: [
      'On screen the brief’s summary sat a comfortable distance from the coloured rule beside it; printed or saved as PDF, the text ran right up against it. The spacing now survives printing.',
      'The executive report was never affected. A check was added so this cannot come back unnoticed — it measures the actual spacing a print rule would leave rather than looking for one particular way of writing it.',
    ],
  },
  {
    version: '1.162.4',
    date: '2026-08-24',
    title: 'The brief’s note about missing link-click data is now one short sentence',
    changes: [
      '“How the attacker got in” was saying the same thing three ways when no link-click records existed. It now reads: “Not established from the records reviewed. No lure message was identified, and no URL-click data is available, potentially because of a lack of licensing or a lack of configuration.”',
      'It also no longer claims the analyst found no malicious link click in cases where there was no click data to look at — those were two statements that could not both be true. Where link-click records were reviewed, the brief still says plainly that none were found.',
    ],
  },
  {
    version: '1.162.3',
    date: '2026-08-24',
    title: 'The Executive Brief now says whether link-click records existed to look in at all',
    changes: [
      '“How the attacker got in” used to say only that no entry point was established. A reader completes that sentence themselves, and the completion depends on something it did not say: whether we could see link clicks at all. The brief now states which it is.',
      'It distinguishes five situations rather than one: the records were reviewed; the tenant’s licence does not include them (and says which licence would); the licence does include them but none came back, so either none occurred or they are not being collected; nothing is recorded about the licence, so the cause cannot be attributed; or the log could not be read. This is worked out on the server from the investigation’s own stored data and the tenant record — not from what the browser sent.',
      'The heading “What the attacker did” no longer carries a subtitle, and there is more space between the summary and the coloured rule beside it.',
      'A printed brief or report is now titled by the case reference instead of the mailbox address, so a forwarded copy does not carry an address across the top of every page.',
    ],
  },
  {
    version: '1.162.2',
    date: '2026-08-24',
    title: 'The Executive Brief is now genuinely one page, and a garbled list is fixed in both documents',
    changes: [
      'The mitigation list in the brief was printing as run-together text — “1. 1Account password resetAug 3, 11:40 AM” — under a heading that appeared twice. Both are fixed.',
      'The same underlying problem was affecting the full Executive Report, where the inbox-rule, sign-in-method and persistence lists have been printing each item’s name run into its timestamp. That is fixed too.',
      'The brief no longer repeats itself at the top: the headline is gone from the page, and the summary no longer sits under a heading reading “Summary” — on a one-page summary, the page is the summary. The summary is now the page’s opening paragraph, still colour-coded by severity.',
      'The brief was running to a page and a half, because a short page of scope and mitigation notes sat between page one and the first addendum. Those notes now come after the addenda, so page two begins the first real table.',
      'Measured on four real cases, page one now has a quarter to a third of a page in reserve, so a longer summary no longer pushes it over.',
    ],
  },
  {
    version: '1.162.1',
    date: '2026-08-24',
    title: 'The Executive Brief’s first page now fits one printed page, and “Bottom line” is now “Summary”',
    changes: [
      'The summary block at the top of both the brief and the full report is now headed “Summary” instead of “Bottom line”.',
      'The brief’s first page was running onto a second page when printed. It now fits, measured on four real cases with 36–84 points of room to spare — including cases where the analyst identified a phishing email and a confirmed link click, which is what made the page longest.',
      'The note explaining that a lure email and a link click are two separate findings has moved to the addendum that shows them both in detail. It is still stated — the first page never claims the click came from that email — but it belongs next to the evidence rather than taking a quarter of a page whose length is the point.',
      'The checklist’s explanatory notes are shorter. They now give the count and say “none attributable either way”; the rest of what they used to say is what “Not determined” means, which the key under the table already spells out.',
      'A password reset performed by a service account with a very long name no longer overflows its column.',
    ],
  },
  {
    version: '1.162.0',
    date: '2026-08-22',
    title: 'Executive Brief — a one-page version of the executive report, with the detail attached',
    changes: [
      'There is a second button beside Executive Report: Executive Brief. It produces a single page that answers four questions — how the attacker got in, when they were active, when it was contained, and what they actually did — with the supporting tables attached behind it as addenda (messages, files, applications, attacker addresses, and the entry point in detail).',
      'On the brief, the only text written by the AI assistant is the bottom line. Everything else on the page — including the containment dates and the nine-line checklist — is rendered from the investigation’s own records.',
      'The addenda list only what the records tie to the attacker, so a reader who works down from page 1 is not shown activity that belongs to the real user. The full executive report is unchanged and still shows the whole reviewed view.',
      'Only one document can be generated at a time per case, and if one is already running the message now says which type it is, so you know whether to wait for the one you asked for.',
      'If a table had to stop short because the investigation recorded more than a report can carry, the table now says so instead of quietly ending.',
      'A brief needs its own approved template for your MSP. If one is missing the button explains that before you click it, rather than failing after a wait.',
    ],
  },
  {
    version: '1.161.0',
    date: '2026-08-21',
    title: 'Groundwork for the Executive Brief — a nine-line summary of what the attacker actually did',
    changes: [
      'The investigation now derives a short checklist of what the attacker did — read mail, sent mail, deleted mail, inbox rules, files, sign-in methods, devices joined, actions against other identities, applications used. Each line is either Yes with a count, No, or "not determined", and every count requires the event to be tied to the attacker by address, device or session; a count on its own never produces a Yes.',
      '"No" and "not determined" are deliberately different answers. On one real case 1,261 SharePoint records exist and not one can be tied to or ruled out as the attacker, so the file line reads "not determined" — saying "No" there would claim the attacker read no files, which the data does not support in either direction.',
      'A line also reports events it could not attribute. On the reference case a guest account was invited during the incident by a Microsoft service, so it cannot be tied to the attacker — the line reads No, and now adds that one such event was recorded and could not be attributed, instead of staying silent about it.',
      'If a log source failed to be collected, the affected line reads "not determined" rather than No. That is checked against the stored investigation data, not taken on trust from the browser.',
      'Nothing displays this yet — the Executive Brief that will show it is still being built.',
    ],
  },
  {
    version: '1.160.1',
    date: '2026-08-21',
    title: 'Removed internal field names from report text — they had been there all along',
    changes: [
      'Reports have been quoting internal data-field names in their own sentences — "the period reflected in attacker_window_duration", "Metric distinct_messages_read shows how many unique messages were opened". Checking the four most recent reports found 12, 5, 27 and 9 of them. Yesterday’s fix caught only the bracketed form of this, and the report checked that night was declared clean because only brackets were searched for.',
      'Reports now refuse to generate if any field name appears in a sentence, and the writing assistant is told plainly that these names are internal keys and never words. The names in the small print at the foot of the report (case, run, date range) are unaffected — those are meant to be there.',
    ],
  },
  {
    version: '1.160.0',
    date: '2026-08-21',
    title: 'Executive Report: a reviewer’s pass on what the client actually reads',
    changes: [
      'Mail folder names containing an apostrophe or an ampersand printed a code instead of the character — "\\2017 New Controller\\PO&#39;s and Purchasing". It affected every report generated so far, and only some rows in each table, which is why it went unnoticed.',
      'The old "Other sign-in method changes — not attributed to the attacker" list is now "Mitigation steps taken", and sits with the containment checklist instead of in the middle of the section about what the attacker did. The entries are written in plain words — "Account password reset", "New multi-factor authentication method registered" — rather than the raw audit phrasing they used to carry. A change made from an address you classified malicious still stays with the attacker’s activity, because that is persistence, not remediation.',
      'The "Bottom line" panel is now at most two short paragraphs. It had grown to three long ones, which is a summary of the summary.',
      'The scope statement used to say the figures came from incident-related events "not the raw log volume", which read as though the full logs were never examined. It now says what actually happens: the investigation reviews the tenant’s full recorded log volume for the period and reports the incident-related events drawn from it.',
      'The timezone the report’s times are shown in is now stated at the top of the document, next to the dates reviewed, instead of only in the small print at the bottom.',
      'The Scope section is no longer subtitled "honest limits" — it implied the rest of the document was something else. It now reads "what was reviewed, and what remains open".',
      'The footer’s Provenance block has been trimmed from seventeen fields to nine, keeping the case, run, record count, date range, timezone, requester and version. The internal details that were removed are still recorded against the report itself, so a support question can still be answered.',
    ],
  },
  {
    version: '1.159.2',
    date: '2026-08-21',
    title: 'Removed internal field names that had started appearing in report text',
    changes: [
      'Yesterday’s change to make the report mention deleted mail also caused the writing assistant to start appending internal data-field names to its sentences, in square brackets, throughout the document — 55 of them in one report. Those are gone, and the report now refuses to generate if any reappear.',
      'The deletion wording itself is unchanged and still reads correctly: one report now carries a dedicated "Evidence limited by deleted items" note explaining that permanently deleted messages cannot be recovered for review.',
    ],
  },
  {
    version: '1.159.1',
    date: '2026-08-21',
    title: 'The Executive Report now tells you when the attacker deleted mail',
    changes: [
      'A report generated on a live case showed the attacker moving sent mail to Deleted Items, soft-deleting messages, and permanently purging five items from Recoverable Items — all correctly listed in the item table, and none of it mentioned in the written summary, which described the activity as reading email. Deleting sent mail is how this kind of intrusion hides itself from the mailbox owner, and a permanent delete also destroys evidence, so the summary now states it whenever it happened.',
      'The writing assistant only ever sees a small sample of the item table, and deletions were ranked no higher than an ordinary message read — so on a case with 297 deletions among 1,203 reads, not one deletion reached it. Deletions now rank immediately after mail the attacker sent, and carry whether the delete was recoverable or permanent.',
    ],
  },
  {
    version: '1.159.0',
    date: '2026-08-21',
    title: 'The Executive Report no longer says the attacker read mail it deleted, or counts your own files as theirs',
    changes: [
      'Mail the attacker DELETED was reported as mail they read. Every Exchange record carrying a list of mail items was labelled "Read" without the operation being checked, so messages moved to Deleted Items, soft-deleted or purged all appeared in the report as messages that were opened. On one case 92 of 1,299 messages had never been read at all — they were only ever deleted; on another, 641 rows were affected. Deletions now read "Deleted", and each keeps its own wording, because a message moved to Deleted Items is recoverable by the user and a purged one is gone.',
      'A message that was read and then deleted now appears twice — once for each. The two used to merge into a single row that kept whichever happened first, so half of what was done to that message was silently dropped from the table.',
      '"SharePoint / OneDrive items opened" and "Files downloaded" counted every file the account touched, with no test of whether the attacker touched it. On a recent case that was 1,261 file records, 1,175 of them from the address you had classified safe on the user’s own machine, and not one of them attributable to the attacker — all printed under attacker-facing headings. Both figures now count only attacker-attributed activity, and their labels say so.',
      'Those two figures also disagreed with the item table beneath them about which operations count as opening a file. They now use the same list.',
      'The report refuses to generate on a count the investigation run cannot support. Two gaps in that check meant a figure could pass when the run held no matching records at all — including the file counts above.',
    ],
  },
  {
    version: '1.158.0',
    date: '2026-08-21',
    title: 'Device registrations now name the device — and say when it is the attacker’s',
    changes: [
      'A directory-audit row reading "Device Registration Service performed Add registered owner to device" named the account the device was given to, and never the device. The machine was in the record all along. Those rows now read "Registered device DESKTOP-2CED23 to controller@milmar.net".',
      'A new "attacker device" marker attributes activity through the machine that performed it. This is the only way a device registration can be attributed at all: Microsoft carries it out, so the row has no source address and no session of its own. A machine earns the marker only when it has been seen signing in from an address you classified malicious and from no other address — the second half is what keeps a shared machine name out of it. The marker reads differently from the existing attacker-IP and attacker-session ones, and its tooltip says it is inferred from sign-in history rather than taken from the event’s own record.',
      'Two devices registered seconds apart are no longer merged into one timeline row. They share the same target account, so the grouping treated four audit rows as a single action and could name only one of the two machines — which made a count of six devices render as five rows.',
      'The Executive Report now states the finding. Device registrations were previously absent from every report: they carry no source address, so they failed the report’s "is this significant" test entirely. There is now a "Devices the attacker joined to the tenant" section listing each machine, when it was joined and its device object id, with the reason it matters said outright — a joined device holds a sign-in token of its own and keeps working after the password is reset. It sits immediately after the note about sign-in methods, so "no sign-in method was registered" is no longer read on its own.',
      'A joined device now counts as persistence when the report grades severity. A case whose only remaining foothold was attacker-registered machines previously read as "contained" with nothing left to verify; it now reads "contained; verify persistence removed".',
      'Every machine name in the report is checked against the investigation run it was built from, and the report is refused if one does not appear there.',
    ],
  },
  {
    version: '1.157.0',
    date: '2026-08-20',
    title: 'Executive Report no longer files your remediation under "What the attacker changed"',
    changes: [
      'The "Sign-in methods registered or changed" list sat directly under the heading "What the attacker changed", so the password resets and multi-factor re-registration your team performed read as the attacker\\u2019s work. On a recent case all eight entries were response actions taken after the password reset, and none came from an address classified malicious.',
      'Entries are now split on their own recorded address: anything from an address you classified malicious appears as "Sign-in methods the attacker registered or changed", and everything else under "Other sign-in method changes \\u2014 not attributed to the attacker". Nothing is removed \\u2014 an executive should still see every identity change on a compromised account, just not credited to the wrong party. An entry with no recorded address is never credited to the attacker.',
      'When nothing is attacker-attributed the report now says so outright: "No sign-in method was registered or changed from an address classified as malicious." An attacker-registered sign-in method is persistence that survives a password reset, so the absence is worth stating rather than leaving to be inferred.',
    ],
  },
  {
    version: '1.156.4',
    date: '2026-08-20',
    title: 'Fixed: the Executive Report credited the attacker with your own remediation',
    changes: [
      'The report’s "Apps & access paths" section was being given a list of operations rather than applications — including "Reset user password", "Change user password" and "Admin deleted security info", which are the response actions your team and Microsoft performed. The generated narrative then stated that the attacker triggered password resets and security-information changes, contradicting the same report’s own Containment section. That list now contains applications only.',
      'It is also scoped to applications used from the addresses you classified malicious, so it matches the "Applications used in the attacker session" figure printed beside it. Previously the figure counted the attacker’s applications while the list showed every application seen on the account — mostly the real user’s own laptop and phone — and the narrative described them all as the attacker’s.',
    ],
  },
  {
    version: '1.156.3',
    date: '2026-08-20',
    title: 'Executive Report: three figures corrected after reviewing a generated report',
    changes: [
      'Addresses known only from non-interactive sign-ins no longer show "0 events" next to "Malicious". Three of the nine addresses on a recent case read that way — one of them with 224 authentications behind it, which is why it was classified in the first place. Their first- and last-seen times now come from that feed too.',
      'The application count now spans both sign-in feeds. It read "4 applications used" in a report whose own narrative named six.',
      'The mailbox statistic labelled "Distinct emails the attacker could see" now reads "Distinct emails recorded as opened". It counts messages with a recorded open, and it does not bound what an attacker holding mailbox access could have viewed — a zero there must never be read as "no mail was exposed".',
      'Counts that the report reconciles against the investigation run — sign-ins, files, message trace — are deliberately unchanged.',
    ],
  },
  {
    version: '1.156.2',
    date: '2026-08-20',
    title: 'Fixed: Executive Report refused addresses found only in non-interactive sign-ins',
    changes: [
      'The Executive Report checks that every address you marked malicious really appears in the investigation run it is built from. That check only looked at the run’s IP summary, which the investigation builds from interactive activity — so an address you graded from the Non-interactive sign-ins panel was reported as "not present in the investigation results" and the report was refused. On a recent case that was three of nine malicious addresses.',
      'The check now looks across the whole investigation run rather than just its IP summary. It is still verified against the same pinned run and nothing else, and an address the run never saw is still refused.',
    ],
  },
  {
    version: '1.156.1',
    date: '2026-08-20',
    title: 'Fixed: the Executive Report could not be generated',
    changes: [
      'The Executive Report is generated from a fixed set of filters, and one of them — "Risk detections hidden", added in 1.154.0 — was never being read from the case. The report view could therefore never be satisfied: pressing "Set the report view" applied the filters correctly, but the report still reported that one as unset and refused to generate. Any case was affected.',
      'The list of filters the report needs now states each one’s current setting beside it — for example "Risk detections hidden · currently off". Previously it named only the setting the report wanted, which read as though the report wanted risk detections shown.',
    ],
  },
  {
    version: '1.156.0',
    date: '2026-08-20',
    title: 'The reports now show the same timeline you signed off on',
    changes: [
      'The Client and Technical reports build their timeline from exactly the view you approved on screen: access periods, grouped directory actions, and the non-interactive (token refresh) activity. Previously the reports rebuilt the timeline their own way — on a recent case that meant 620 individual sign-in rows where the screen showed 6 periods, and 45 directory rows where the screen showed 13 actions.',
      'Non-interactive sign-ins now appear in the reports at all. That feed is where token re-use shows up, and it was missing from every report while being visible on screen — on a recent case it accounts for three of the nine malicious addresses. It is labelled "Token Use" in the timeline, and a new appendix section lists every underlying record.',
      'The timeline now states how its rows reconcile to the recorded events — for example "45 rows, covering 812 recorded events", naming how many rows are groups. A short report and a filtered report look alike otherwise, and you should not have to take either on trust.',
      'The Application column no longer contradicts the row beside it. A grouped row used to show the first application in the group while its own description named the busiest one; it now names the busiest and counts the rest.',
      'Counts in the reports are unchanged and still count individual events. "The attacker signed in 38 times" means 38 sign-ins, even though the timeline shows them as one period.',
      'The Client report shows only the token-refresh activity attributable to the attacker — on a recent case 11 rows rather than 123, because the rest is the account’s own laptop and phone re-authenticating. The count of what was left out is stated on the report, and the Technical report still lists every one.',
      'The Executive Report timeline now draws on the same grouped view, including token re-use, so its highlights match the case timeline. Its statistics are unchanged.',
    ],
  },
  {
    version: '1.155.0',
    date: '2026-08-20',
    title: 'The timeline now reads as what happened: access periods, directory actions, and attacker-attributed events marked',
    changes: [
      'Repeated sign-ins are grouped into access periods — one row per continuous stretch of activity from an address, instead of one row per sign-in. On a recent case that is 341 rows reduced to 6, and those six tell the story: sustained access from one address, three brief web logins, a run of failures 41 minutes after the refresh tokens were revoked, and a single failed attempt the following day. Expanding a row lists every sign-in behind it.',
      'Non-interactive (token refresh) activity uses the same grouping, so one address active across two days is one row rather than five. Failure codes are carried into the row, so a revoked session still shows why it failed.',
      'Directory changes are grouped into actions and named after the operation that describes what happened. A guest invitation was three separate rows — "Invite external user", "Add user" and "Update PasswordProfile" — and is now one row named for the invitation, with the other two listed inside it. Across a recent case, 41 directory rows became 13 actions.',
      'That grouping also corrects an undercount: Microsoft splits some actions across more than one internal identifier, so the previous "1 of 2" marker was understating them. Eight actions on that case were affected — rows that belonged together were shown as unrelated activity.',
      'Events attributable to the attacker are now marked in the timeline. A red mark means the event’s own address is one you classified malicious. An amber mark means the event happened inside a sign-in session that a malicious address also used — deliberately worded as corroboration, not proof, because a stolen token keeps the real user’s session identifier.',
      'On a recent case this surfaces immediately what previously had to be found: six fraudulent replies sent to an external accounting address, and the two inbox-rule operations that hid the responses in a folder named RSS Feeds.',
      'Nothing is removed by any of this. Every grouped row lists its constituents when expanded, per-event tabs are unchanged, and dismissing a grouped row moves the whole group together.',
    ],
  },
  {
    version: '1.154.0',
    date: '2026-08-20',
    title: 'Risk Detection rows can be hidden from All Events once you have read them',
    changes: [
      'Microsoft writes an Identity Protection risk record about a sign-in, so All Events showed the same moment twice — a sign-in row and, at the identical second, a risk row reading "performed generic". On a recent case 222 of 227 risk rows paired 1:1 with a sign-in, and all 227 had their risk level and detail withheld by Microsoft, so the row stated no risk of its own while the sign-in beside it already carried the risk state.',
      'A "Risk detections" filter now hides these rows from All Events. It is off by default, so the rows are in front of you during first review and can be switched off once read; the count is on the button. The Risk Detections tab is unaffected and always shows every row, including the few that are not generic and do say something a sign-in does not.',
      'Executive Reports now require this filter to be on, so report event counts are not inflated by the same activity being counted twice. The one-click "Set the report view" applies it along with the rest.',
    ],
  },
  {
    version: '1.153.0',
    date: '2026-08-20',
    title: 'Token-refresh activity is condensed instead of crowding the timeline — and the addresses behind it now reach the reports',
    changes: [
      'Non-interactive sign-in rows (token refresh, single sign-on reuse, device re-authentication) are recorded by Microsoft once per hour per session, so a single idle laptop produced around twenty-five near-identical rows a day — on a month-long case, 2,836 rows in all. They are now condensed to one row per day for each combination of address, device, session and outcome: 442 rows on that case, with every authentication, address, device, session and error code still accounted for. Expanding a condensed row lists each hourly period as recorded.',
      'The Application column on those rows showed the same word, "TokenRefresh", on every one of them. It now names the applications the tokens were issued for, which is the part that varies.',
      'A day of failed token refreshes now reads as one finding. Successful and failed activity are never combined into the same row, so a revoked session shows as, for example, "753 failed" against one device on one day rather than being spread across eleven hourly rows.',
      'Reports were missing the addresses that appear only in token-refresh activity. On a recent case that was 37 of 59 addresses, three of which the analyst had marked malicious — so a report could name an attacker’s network while leaving out two of its addresses. Those addresses are now included in the IP Address List and in the Attacker Origin table, marked "token only" and left without a location or risk score, because no reputation data exists for them.',
      'Addresses seen only in failed attempts are deliberately left out of reports, and the report states how many were withheld so a shortened list is never mistaken for a complete one.',
      'Re-run an investigation to pick up the above on an existing case.',
    ],
  },
  {
    version: '1.152.0',
    date: '2026-08-20',
    title: 'Directory changes now name what they changed, who Microsoft carried them out through, and which rows are one action',
    changes: [
      'A directory change often reads as "<user> performed Update PasswordProfile" and names nothing that was changed, because Microsoft leaves the target display name empty on a whole family of user-lifecycle events. The target is now named from its address instead, and an invited guest’s address is shown as the real mailbox (for example sowmyatewari@gmail.com) rather than the encoded directory form.',
      'Account creation and external-guest invitations are no longer filtered out of the timeline. Creating an identity or inviting an outside address is a common way an attacker keeps access, and only the second half of such an action was being kept — the half that named the new account was dropped as routine noise.',
      'Microsoft records one thing a person did as several separate audit rows. Rows that belong to one action are now marked (for example "1 of 2") with the other operations named on hover, and the expanded row lists them in order, so three unrelated-looking rows read as the single change they were. Only small, tightly-timed groups are marked — a bulk sync job sharing an identifier is not one action and is left unmarked.',
      'Far more directory rows now correctly show their IP as Microsoft’s own infrastructure rather than as an address worth investigating. Previously only sign-in-service events were recognised; a further eight Microsoft services — the Microsoft 365 admin portal, guest invitation, Teams, Groups, licensing and others — recorded an IP that looked like a normal attributable address, and on some of those it also carried a risk score, which pointed the investigation at Microsoft’s plumbing. Those rows now say which Microsoft service performed the action, and the address is greyed.',
      'Each such row is checked against the address it recorded before being marked, so a genuine user or attacker address is never greyed out on the strength of a familiar-looking name.',
      'Outbound email rows now carry the sign-in session that sent them. Message trace records the mail server that relayed a message, which for anything Microsoft sends on a user’s behalf — SharePoint and OneDrive sharing notifications, Teams, service mail — is Microsoft’s own address on both sides of the record, leaving the send attributable to nobody. Each outbound row is now matched to its Exchange audit record and inherits that record’s session and resolved address.',
      'One email to several people is now one row instead of one row per recipient. Every recipient is still listed, in the timeline description, in the expanded row and in the reports, each with its own delivery result — a message delivered to two people and rejected by a third says exactly that rather than reading as "delivered". Where the message went to addresses outside this mailbox, the row says how many recipients it is showing of the total Microsoft recorded.',
      'Reports counted recipients rather than messages, so one email to three people read as three emails sent. Corrected.',
      'Re-run an investigation to pick up all of the above on an existing case.',
    ],
  },
  {
    version: '1.151.1',
    date: '2026-08-20',
    title: 'Mailbox change events now say what changed on nearly all of them, not just some',
    changes: [
      'v1.151.0 described what a mailbox "Update" changed, but on almost every such event the changed-property list was not being saved in a readable form, so the event still read only as "performed Update" while the expanded row showed the detail. On the reference case this affected 63 of 64 of them — any event that changed exactly one property. Fixed, and existing investigation results are read correctly without re-running.',
      'An Exchange event’s calling application no longer reads as "(unresolved)" when Microsoft does not publish a name for it. The application ID is shown instead, copyable, with a note that no display name is available in the tenant’s app data — it can be given a name under Settings → Known Applications, which resolves it everywhere.',
    ],
  },
  {
    version: '1.151.0',
    date: '2026-08-20',
    title: 'Mailbox change events say what actually changed, and the reveal control now covers file names',
    changes: [
      'Exchange "Update" and "Create" events used to read only as "performed Update" or "<app> performed Create", so a row carrying real evidence looked like noise and invited being dismissed. They now say what changed: which property was modified, the subject of the item, the folder it was in, and how many attachments were on it — for example "changed AttachmentCollection on ‘FW: payment request’ in \\Sent Items (2 attachments present)", or "saved a draft in \\Drafts".',
      'Only the top-level folder appears in the one-line description, since nested folder names can identify a client or matter; the full path, the changed-property list, the item size, the message ID, the calling application and the device ID are in the expanded row under "Item / Change Detail".',
      'A routine calendar save can modify over a hundred properties at once, which read exactly like mail tampering. Those now collapse to "saved a calendar item".',
      'The attachment COUNT is shown, never the names, and never the word "attached" — Microsoft records all attachments currently on a message rather than the ones just added, so a description must not claim the person attached anything.',
      'The application shown on an Exchange event was frequently the Microsoft API the request passed through rather than the app that made it. The real calling application is now shown as well, and the recorded one is labelled "Recorded application (API pathway)" so the two are not confused.',
      'The reveal control that unmasks email subjects now also covers mail attachment names and SharePoint/OneDrive file names. File names previously had no reveal control at all — they simply showed, including in reports, on a case whose subjects were hidden. The audit entry is renamed to "sensitive_detail.reveal" and counts everything the control discloses.',
      'Re-run an investigation to pick up the new detail on an existing case.',
    ],
  },
  {
    version: '1.150.1',
    date: '2026-08-19',
    title: 'The hover text on a newly-attributed address now says which of the two things happened',
    changes: [
      'v1.150.0 attributes background-service events either by replacing a Microsoft address with the real one or by confirming the address already on the record. The label distinguishing the two was not being saved with the investigation results, so every one of them read as a replacement \u2014 wrong for the 54 that were confirmations. Re-run an investigation to pick this up.',
    ],
  },
  {
    version: '1.150.0',
    date: '2026-08-19',
    title: 'Background-service file and mail events can now be attributed to a real address',
    changes: [
      'Some Exchange and SharePoint events are recorded by a background service rather than by the person, so the address on them is the service\u2019s. Those events used to sit in the Service IP Events tab with no way to tell whether the real user or an attacker was behind them. The investigation now checks the session\u2019s own sign-in activity within the case and, where that points to a single address, attributes the event to it.',
      'On the reference case this attributed 169 previously unattributable events \u2014 168 file and page views, edits and previews in SharePoint, plus one address-book change. 115 had a Microsoft address replaced with the real one; the other 54 had the address already on the record confirmed.',
      'The two outcomes are labelled differently, because they claim different things: hover the address to see whether it was substituted or confirmed. Events on a session used from more than ten addresses are still left unattributed rather than guessed at.',
    ],
  },
  {
    version: '1.149.2',
    date: '2026-08-19',
    title: '"New to this case" was listing nothing, although the addresses were all there',
    changes: [
      'The filter that picks out addresses the investigation never surfaced on its own returned an empty list. The addresses were present and classifiable the whole time; only the marker that identifies them was missing, so the filter had nothing to match.',
      'It also no longer depends on having run the Non-interactive sign-ins panel first. Those addresses are now part of the investigation results, so the marker is applied as soon as the case loads.',
    ],
  },
  {
    version: '1.149.1',
    date: '2026-08-19',
    title: 'The IP Addresses tab no longer changes which addresses it lists when you toggle a filter pill',
    changes: [
      'Switching a pill such as Safe IPs could add or remove rows from the IP Addresses tab. The pills exist to tidy the timeline, and the IP tab is where addresses get classified, so a pill removing a row there took away the ability to act on that address instead of just hiding it. The tab now always lists every address seen anywhere in the case.',
      'No address disappears from the tab that you could reach before: the list now matches what it showed with every pill switched off.',
    ],
  },
  {
    version: '1.149.0',
    date: '2026-08-19',
    title: 'Non-interactive sign-ins now appear in the case timeline',
    changes: [
      'Token refresh, single sign-on reuse and PRT replay activity is now part of a case. It comes from a separate Microsoft feed that investigations never read, so until now the All Events timeline, the IP list and the device list could all be missing real attacker activity with nothing to indicate it. On the reference case that was 19,192 authentications, 817 of them successful from addresses the analyst had marked malicious.',
      'The activity is grouped so it can be compared rather than scrolled: one row per session, address and hour, showing how many authentications happened, how many succeeded or failed, the failure codes, the apps involved and the device. Every authentication is still counted, so nothing is dropped to make the list shorter.',
      'Known-good activity is included on purpose, so you can compare it against unknown activity in the same hour. Every existing filter pill applies to these rows, including Safe IPs, Dismissed and Hide failed, so you can set them aside the same way as anything else.',
      'Addresses seen only in this feed now appear in the IP Addresses tab automatically, ready to classify, without having to run the Non-interactive sign-ins panel first. They arrive without location or ISP data, so use Enrich unenriched to fill it in.',
    ],
  },
  {
    version: '1.148.12',
    date: '2026-08-19',
    title: 'Non-interactive sign-in results now show the full IPv6 address',
    changes: [
      'About one in six IPv6 addresses was shown with its last group missing, because the query stripped it while trying to remove a port number. A shortened address is not a real address, so it could never be enriched with location and ISP data, "Enrich unenriched" always failed on it, it did not line up with the same address elsewhere on the case, and it was displayed as an indicator of compromise. Addresses now come through in full. Re-run "Non-interactive sign-ins" on a case you have already looked at to replace the shortened values.',
      'When an action fails on a list of items, the message now names the items instead of showing "[object Object]", and wraps instead of stretching across the toolbar.',
    ],
  },
  {
    version: '1.148.11',
    date: '2026-08-19',
    title: 'Saved non-interactive results now keep the failure codes, which are the containment evidence',
    changes: [
      'When a result is too large to save in full, it was keeping session IDs ahead of failure codes — so after a reload you could see that an IP had hit a revoked token grant, but not the codes and counts behind it. Those codes are what separate a cut-off attacker from the real user re-authenticating, so they now come first: every IP that has them keeps them.',
    ],
  },
  {
    version: '1.148.10',
    date: '2026-08-19',
    title: 'Fixed the failed-sign-in coverage check reading a single day and reporting it as the whole window',
    changes: [
      'The check introduced moments earlier in 1.148.9 was reading only the first day of a case window instead of every day, then reporting that one day as if it had measured the lot — so a window could be described as fully covered on the strength of a single day. It now reads the days it is supposed to, and if the selection ever comes back malformed it says coverage could not be measured rather than showing a figure.',
    ],
  },
  {
    version: '1.148.9',
    date: '2026-08-19',
    title: 'Non-interactive results keep more detail when saved, and the failure check now works on busy tenants',
    changes: [
      'When a non-interactive result is too large to save in full, it now keeps as much per-IP detail as will fit instead of dropping all of it — session IDs, whether the IP hit a revoked token grant, and its last success survive on most rows. Previously a quarter of the available space went unused, and the session links that tie these IPs back to the case timeline were lost on reload.',
      'A case with a very large number of non-interactive IPs used to save nothing at all and show "too large to save". It now keeps the address list and time bounds for every IP, which is the part you cannot re-derive by running the query again.',
      'The check for whether failed sign-ins were collected for a tenant was timing out on the busiest tenants, so those cases showed "coverage could not be measured". It now samples whole days across the window and completes in around ten seconds; the panel says how many days it sampled rather than implying it read every one.',
    ],
  },
  {
    version: '1.148.8',
    date: '2026-08-19',
    title: 'Reloading a case no longer quietly drops half the session and resource detail',
    changes: [
      'Saved non-interactive results were being trimmed even when there was no need to trim them, so reopening a case showed fewer sessions and resources than the original run — with nothing on screen to say so. What is saved now matches what the query returned, and any genuine trimming still announces itself.',
    ],
  },
  {
    version: '1.148.7',
    date: '2026-08-19',
    title: 'Fixed a line that reported "0 events" without ever reading them',
    changes: [
      'The note saying how much case activity fell inside an IP’s token window was reading the wrong field name, so it counted nothing and said "0 events — the case reads as normal" on every IP of every case. It now reads the events correctly, and if it cannot read them at all it shows nothing rather than a zero.',
      'The phrase "none from an IP classified malicious" now only appears once you have actually classified an IP. Before, it was true simply because nothing had been classified yet, which read as reassurance nobody had earned.',
      'The panel footer was still showing the old fixed-date message about failed sign-ins alongside the newer measured one. Both now come from the same measurement.',
    ],
  },
  {
    version: '1.148.6',
    date: '2026-08-19',
    title: 'A window with no failure data now says so clearly, and whether it can be recovered',
    changes: [
      'When a case window contains no failed non-interactive sign-ins at all, the notice now says that plainly instead of reusing the partial-coverage wording, which referred to a range that did not exist. Success counts are complete; failure counts are unknown; and "still succeeding" or "stopped succeeding" must not be read as a containment result for that window.',
      'The notice now also tells you whether the gap can still be fixed: while the window is inside Microsoft’s ~30-day sign-in retention, a backfill can recover the missing failure rows, and the notice says so. Beyond that the data is permanently gone and it stays quiet, so nobody requests a backfill that cannot work.',
    ],
  },
  {
    version: '1.148.5',
    date: '2026-08-19',
    title: 'Guidance on how to evaluate non-interactive sign-ins',
    changes: [
      'A new "When and how to evaluate non-interactive sign-ins" explainer sits above the IP table once you have run the query. It is collapsed until you open it, so it never competes with the coverage and save notices beside it.',
      'It covers when to run the query, how to weigh a row in order — device, then session, then neighbouring IPs, then timing, then the outcome after a password reset, and network reputation last — and what to do once you have decided.',
      'It is equally explicit about what the data cannot tell you: this log records authentication only, so it will never add rows to the case timeline, and a shared session, a scripted client or an unscored IP are each insufficient on their own.',
    ],
  },
  {
    version: '1.148.4',
    date: '2026-08-19',
    title: 'Devices not in the case list are now ranked by how suspicious they are',
    changes: [
      'Devices registered inside the case window now sort to the top with a "registered during this case" badge. On the case that prompted this, that separates the two attacker devices from the user’s own laptop and tablet, which have been registered since 2024 and January.',
      'A red "shared build" badge marks devices registered in the window that share an identical OS build with each other. The build is fixed when a device enrols, so the same value on machines enrolled weeks apart is not something a real fleet produces. Only devices registered inside the window are compared, so a company running one standard image is not flagged.',
      'Legitimate devices are still listed, just unbadged and below — the section ranks, it does not hide.',
      'Fixed a device showing no IP addresses at all when the per-IP lists did not name it.',
    ],
  },
  {
    version: '1.148.3',
    date: '2026-08-19',
    title: 'Fixed the new Devices section listing every device instead of the missing ones',
    changes: [
      'The "seen only in non-interactive sign-ins" section was comparing against the wrong field, so it matched nothing and listed every device in the feed — each labelled as never having signed in interactively, which was untrue for nearly all of them, including devices shown in the table directly below.',
      'It now lists only devices genuinely absent from the case’s device list, and is retitled "Not in this case’s device list" — because that is what it can actually show. The note explains both reasons a device lands there: it only ever presented an already-issued token, or its interactive sign-ins fall outside the case window.',
    ],
  },
  {
    version: '1.148.2',
    date: '2026-08-19',
    title: 'Devices tab now shows devices seen only in non-interactive sign-ins',
    changes: [
      'Fixed: the new "Seen only in non-interactive sign-ins" section on the Devices tab was never receiving its data, so it rendered nothing even after running the query. On the case that prompted this it now shows DESKTOP-D61DA9, which appears in no other view.',
    ],
  },
  {
    version: '1.148.1',
    date: '2026-08-19',
    title: 'Non-interactive sign-in results now survive a page reload',
    changes: [
      'Fixed: running "Non-interactive sign-ins" appeared to work, but the result was silently lost on the next reload. The saved size limit was set roughly 50% higher than the real per-case storage limit, so a larger result was rejected outright — and the failure was only visible in the browser console.',
      'Large results are now trimmed rather than discarded. Every IP and every device is always kept — they are the point — and less critical detail (client strings, resource breakdown, failure codes) is shed only as far as needed to fit.',
      'If anything was trimmed, or could not be saved at all, the page now says so above the IP table instead of failing quietly.',
      'The session, containment and resource detail added in 1.148.0 is now included in what gets saved, so it is still there when you come back to the case.',
    ],
  },
  {
    version: '1.148.0',
    date: '2026-08-19',
    title: 'Non-interactive sign-ins now show what they tie to, not just which IPs',
    changes: [
      'An IP recovered from the non-interactive feed now shows the session its tokens belong to, cross-referenced against your case: which other IPs share that session, how many of them you have classified malicious, and which recorded actions ran on it. On the case that prompted this, all three recovered IPs turn out to be one session — the same one carrying the compromise IP, four confirmed attacker IPs, the inbox rules created on Jul 31, and the user’s own office IP. That is a stolen token, and neither feed shows it alone.',
      'A shared session is corroboration, not attribution, and the panel says so: a stolen token keeps the victim’s own session id, so the same session legitimately carries their real activity. Judge each event on the IP recorded against it.',
      'Each IP now shows what the case itself was showing during that IP’s token window. On the reference case one address refreshed a token 224 times over six days while the case showed only ordinary work and no attacker activity at all — those days read as clean and were not.',
      'Failure outcomes are surfaced: error codes with counts, and the last time that IP succeeded. After a credential reset a 50173 means the token grant was revoked — the real user re-authenticates and carries on, a stolen token cannot. On the reference case the attacker’s last success is Aug 3 and the user’s office IP is still succeeding today.',
      'Corrected a disclosure that was working against you: it claimed failed sign-ins were not collected before a fixed date, which was false for any tenant that has been backfilled — and it discouraged trusting the very rows that prove containment. Coverage is now measured per tenant per window and reported as what was actually found.',
      'Each IP now lists the resources its tokens were obtained for, with counts, and states plainly that this feed records authentication and not actions — whatever was done appears in the case timeline under the IP recorded on that event.',
      'Devices seen only in non-interactive sign-ins now get their own section on the Devices tab, instead of being absent entirely.',
    ],
  },
  {
    version: '1.147.3',
    date: '2026-08-19',
    title: 'Corrected a wrong claim in the case scope notice',
    changes: [
      'The scope notice on every case said the non-interactive blind spot had "moved the apparent start date 10 days later than the truth". That is wrong, and on the case it cites you can see it is wrong: the case shows first attacker activity on Jul 9, while the IP recovered from that feed does not appear until Jul 19 — ten days after the start, not before it. The earlier date came from other logs, not this one.',
      'What the ten days actually measures is narrower: that IP was active ten days before the First Known Indicator of Compromise the case was opened from. That is a lead pointing at activity predating what you knew at the outset — not a statement that anyone dated the compromise wrongly. The notice and the panel now both say it that way.',
      'Two smaller figures in the same sentence were also corrected — the case has 11 confirmed attacker IPs and this gap accounts for 3 of them (an older note said 5 of 9), and the recovered IP does not carry "the entire first phase" of the intrusion.',
    ],
  },
  {
    version: '1.147.2',
    date: '2026-08-19',
    title: 'Corrections to the new non-interactive sign-in panel',
    changes: [
      'The panel could understate how far back an IP’s activity ran — it reported "9 days before this case’s first known indicator" where the correct answer was 10. Day counts are now measured date to date.',
      'An IP with no threat intelligence at all showed a green "Low" risk badge, which reads as "checked and clean" for an address nobody ever scored. It now shows a grey "Unknown", matching the "(unenriched)" note in its detail panel.',
      'The notice about failed sign-ins not being collected now gives the exact time the collection started (2026-08-18 22:34Z) rather than just the date, since most of that day is uncovered too.',
    ],
  },
  {
    version: '1.147.1',
    date: '2026-08-19',
    title: 'IP enrichment now survives a page refresh, and the IP table no longer hides rows',
    changes: [
      'Enriching IPs on a case and then reloading the page could lose the enrichment. The data was being saved correctly — it was not being re-applied to every row on the way back in. Specifically, IPs that the case adds because they appear in an event but were not in the original results (which is exactly what "Enrich unenriched" targets) came back blank. They now keep their enrichment across reloads.',
      'The "Email IPs" filter no longer removes rows from the IP Addresses table. That table is where IPs get classified, so hiding a row there took away the ability to mark it Safe, Suspect or Malicious at all. IPs that only appear in message-trace events now carry an "email-only" badge instead of disappearing.',
    ],
  },
  {
    version: '1.147.0',
    date: '2026-08-19',
    title: 'See — and classify — the sign-in activity that case results leave out',
    changes: [
      'The IP Addresses tab has a new "Non-interactive sign-ins" action. Microsoft records token refresh, SSO reuse and PRT replay in a separate log that investigations do not read, which is what the scope line added in the last release warns about. This queries that log for the case’s user and window and adds anything it finds straight into the IP table, so those addresses can be classified Safe / Suspect / Malicious like any other and colour the rest of the case.',
      'On the case that prompted this, it recovers three attacker IPs the investigation could not show at any date range — including one with 224 sessions over five days that was already active 10 days before the indicator the case was opened from — and gives a device that appeared nowhere in the case its activity for the first time.',
      'Every added row is badged "non-interactive only", because the case timeline still will not show those events. Expanding a row explains how to judge it: which devices presented the token, how many records carried no device at all, the network and country, and whether the activity starts before the First Known Indicator of Compromise the case was opened from.',
      'Any IP in the table now shows when its neighbours are already incriminated — "2 malicious in 216.203.13.0/24". Attacker infrastructure clusters, and this works even for addresses we have no threat intelligence on at all.',
      'Two honesty guards come with it. If the log has no data for the earlier part of a case’s window, the panel says so and labels every first-seen as "earliest observed" rather than the start of activity. And because failed non-interactive sign-ins were only collected from 18 August, a low failure count for earlier activity is flagged as missing data rather than left to read as "the attacker never failed".',
    ],
  },
  {
    version: '1.146.1',
    date: '2026-08-18',
    title: 'A completed investigation no longer reports itself as failed',
    changes: [
      'An investigation that finished normally could show a red "Investigation failed — Failed to get runbook status. (The operation was canceled.)" next to the green success message. The investigation was fine every time: one status check had hung and its error arrived after the run had already completed, overwriting the result. Status checks no longer overwrite a finished run, and a single slow check no longer stacks up more behind it.',
      'A status check that fails is also no longer treated as a failed investigation. The portal now retries, and if it really cannot read the status it says so in amber — "Couldn’t confirm the investigation’s status" — and tells you the run is most likely still going, rather than implying you need to start over. The same wording now applies when the connection drops just after you start a run.',
      'Fixed the underlying cause: the portal was re-setting its Azure subscription context on every single status check — about 85 times per investigation — and that call is what intermittently hung. It now only runs when the context is actually wrong.',
    ],
  },
  {
    version: '1.146.0',
    date: '2026-08-18',
    title: 'Case results now state that non-interactive sign-ins are not included',
    changes: [
      'Every investigation now carries a scope line above its tabs: non-interactive sign-ins — token refresh, SSO reuse and PRT replay — are not part of case results, so the timeline, IP list and device list can be incomplete. Microsoft records these in a separate log that the investigation does not read, which means an IP or device an attacker used only to replay a stolen token leaves no trace on the case.',
      'This matters most for two judgements: the earliest event on a case is not necessarily the start of the activity, and the IP list is not necessarily the full set of indicators. On a real case this gap hid 3 of the 11 confirmed attacker IPs, and the busiest of them had been active for 10 days before the earliest indicator that case was opened from.',
      'Expanding the line explains where you can see this activity today — the Token Use panel on an alert for the same user reads that log directly. Surfacing it on the case itself is the next step.',
    ],
  },
  {
    version: '1.145.2',
    date: '2026-08-18',
    title: 'A running backfill is now visible to anyone, not just the person who started it',
    changes: [
      'Starting a backfill for a newly onboarded tenant could report "Failed to enqueue backfill" while the backfill was in fact starting normally and went on to run to completion. The portal was giving up after 60 seconds; a routine 30-day, all-log-type backfill takes about two and a half minutes to queue. It now waits long enough, and if it ever does time out it says so plainly instead of reporting a failure that did not happen.',
      'Opening the Backfill drawer now shows a backfill that is already running for that tenant, whoever started it. Previously progress was only visible in the browser tab that launched it, so a colleague — or you after a page refresh — saw an empty form while a backfill was underway.',
      'If the portal ever does lose track of a backfill, a "Reconnect to a running backfill" button picks it back up rather than leaving "start another one" as the only option.',
      'Backfills that lost their portal record also never triggered the tenant baseline build that normally follows sign-in collection. That link is now reliable, so a newly onboarded tenant gets its baseline.',
    ],
  },
  {
    version: '1.145.1',
    date: '2026-08-18',
    title: 'Fixes to the new Investigations Avoided dashboard',
    changes: [
      'Ticking "Include TEST-mode rules" on Investigations Avoided left the By Suppression Method, By Suppressed Alert Type and By Severity tables empty while the headline numbers still showed. All three now fill in correctly.',
      'Choosing a customer with no suppressions in the selected date range showed an error instead of an empty result. It now reports zero, and the "tenants with suppressions" count no longer reads 1 when there are none.',
      'Two suppression methods that were being grouped under "Other" are now labelled correctly: guest/B2B sign-ins from a new country, and sign-ins from an IP already in the user’s baseline.',
    ],
  },
  {
    version: '1.145.0',
    date: '2026-08-18',
    title: 'Investigations Avoided loads instantly, and Pipeline Health can now prove the event mirror is complete',
    changes: [
      'The Investigations Avoided dashboard now loads from a pre-computed daily summary instead of querying the data warehouse every time you open it, so it appears immediately rather than after a warehouse start-up.',
      'It also now separates two numbers that used to be blended into one: "investigations avoided" (the alerts an analyst would actually have worked) and "suppressed matches" (every individual occurrence). The per-day chart plots both, so the gap between them shows how much repeat noise was cleared.',
      'A new "Credit" selector controls which suppressions count toward the headline. It defaults to those where our own context was decisive — device inventory, per-user baselines, tenant allowlists — which is the number that holds up in a client conversation. You can switch to include suppressions a competing tool would also have caught, or all of them.',
      'Weekends are now shaded on the per-day chart. Suppression volume genuinely drops at the weekend, and without the shading a normal Saturday looked like missing data.',
      'Pipeline Health has a new "Delta → Mirror" tab. It reports how completely the recent-event mirror — what the Recent Events page reads, and what the sign-in outcome re-check and directory name lookup on an alert depend on — matches the underlying data each day, per feed, with the specific portal feature each feed affects.',
      'That tab deliberately says "not measured" rather than "0% loss" when a day’s check did not run, marks a result older than 26 hours as stale, and flags when a percentage is computed over too few rows to be meaningful.',
    ],
  },
  {
    version: '1.144.3',
    date: '2026-08-17',
    title: 'Trace Session now works for every analyst',
    changes: [
      'The Trace Session button on an alert — which shows you every other event from the same sign-in session — previously only worked for global administrators. It was visible to everyone but returned an error for anyone else. It now works for any analyst who can see the alert.',
    ],
  },
  {
    version: '1.144.2',
    date: '2026-08-17',
    title: 'Internal: hardened how the portal authenticates to the log collection service',
    changes: [
      'Behind the scenes, tenant backfill, license-tier probing and tenant activation now authenticate to the log collection service with the portal\'s own managed identity in addition to the existing shared key, ahead of that service moving to identity-based access. No change to how any of those features behave.',
    ],
  },
  {
    version: '1.144.1',
    date: '2026-08-17',
    title: 'Security group names updated for the Blue365Signal rebrand',
    changes: [
      'The nine Entra security groups that control portal access are now named SG365_Blue365Signal_* instead of SG365_M365Signal_*. Your access is unchanged — the groups themselves, and everyone in them, are exactly the same; only the display name changed. You do not need to do anything.',
      'The access-denied screen now names the current group, so anyone who does need access is pointed at the right one.',
    ],
  },
  {
    version: '1.144.0',
    date: '2026-08-17',
    title: 'The portal is now Blue365Signal',
    changes: [
      'The product is now named Blue365Signal throughout the portal — the sidebar, the login and access-denied screens, the browser tab, the user guide, the tenant onboarding wizard and the consent pages. Nothing about how the portal works has changed.',
      'The portal is reachable at blue365signal.bluealliance.com. The previous address, m365signal.bluealliance.com, continues to work — both point at the same site, so existing bookmarks and links are fine.',
      'Admin-consent links generated from the onboarding and re-consent screens now use the new address, and the Microsoft consent prompt a client Global Admin sees names the application "Blue365Signal - Security Monitoring". Consent links already sent out remain valid.',
      'Ticket text written into AutoTask and ConnectWise carries the new name, including the resolution note added when an alert is closed and the note on a logged time entry.',
      'The access-denied screen previously asked users to request membership of a security group that no longer exists. It now points to the right group for your MSP.',
    ],
  },
  {
    version: '1.143.0',
    date: '2026-08-14',
    title: 'Executive reports state how the intrusion started',
    changes: [
      'When an analyst marks the phishing entry point on a case - the lure email in Message Trace, the confirmed malicious click in URL Clicks, or both - the executive report now states it as a finding under "How the attacker got in", with the sender, delivery status, time and clicking user. Previously both marks were discarded: the click survived only as a number, and the email as a hedged note buried in the mailbox table.',
      'Wrapped links are shown as their real destination. A phishing link arriving through Safe Links, Cisco Secure-Web or EdgePilot can be hundreds of characters of redirector; the report now shows the site the user actually reached and names the link-protection services it passed through.',
      'Either mark alone produces the section, and with neither mark it is left out entirely - so its absence means the entry point was not identified, rather than that the report forgot. Reports generated without revealing email subjects keep the finding and withhold only the subject line.',
    ],
  },
  {
    version: '1.142.3',
    date: '2026-08-12',
    title: 'Executive reports without email subjects now summarise mailbox access instead of listing it',
    changes: [
      'When an executive report is generated without revealing email subjects, the table of items the attacker touched is grouped by location and action — one line per folder or site, with the number of items, the number of accesses and the time span. Previously it listed every item individually, and with the subjects withheld those lines were indistinguishable from one another: hundreds of identical-looking rows that buried the ones worth reading.',
      'Lines that are not interchangeable stay on their own: an item the analyst marked as the phishing entry point, an item with a large attachment, and any message the attacker sent. Reports that do reveal subjects are unchanged and still list every item.',
    ],
  },
  {
    version: '1.142.2',
    date: '2026-08-12',
    title: 'Executive reports have ten times the AI capacity headroom',
    changes: [
      'The AI service capacity behind executive reports was raised tenfold. The previous limit was sized such that a fully detailed case — one with inbox rules, sign-in method changes and confirmed phishing clicks, all of which the test case lacked — could exceed it, and two people generating reports in the same minute could collide even when each report fit on its own.',
    ],
  },
  {
    version: '1.142.1',
    date: '2026-08-12',
    title: 'Executive reports that include email subjects generate again',
    changes: [
      'Fixed a failure introduced in 1.142.0 where a report that includes email subjects stopped generating. The new table of accessed items was being sent to the AI model as well as printed in the report, which pushed the request over the size limit of the AI service. The model now receives a summary of the table instead — it never needed the rows, since it is told to describe the table rather than repeat it — and the report still prints every row.',
    ],
  },
  {
    version: '1.142.0',
    date: '2026-08-12',
    title: 'Executive reports list what the attacker touched, with times, in one table',
    changes: [
      'The "Files and emails the attacker accessed" section is now a single table sorted by time, with a column for where each item was and whether it was read, opened, downloaded or sent. It used to be a separate table per mail folder — thirteen of them on a recent case — with no access times and no files in it at all.',
      'Files opened in SharePoint and OneDrive now appear alongside the emails. Previously the report gave a count of files but never said which ones.',
      'An item read more than once appears once, with the time of the first access and how many times it was opened. A message read from several folders is one row listing them, rather than one row per folder.',
      'A "Sent" row appears only when the message came from an address confirmed as the attacker\'s. Mail the account holder sent themselves during the period is left out, so the report cannot imply the attacker sent something they did not — and when nothing was sent from an attacker address, the report can say so as a finding rather than leaving it unclear.',
      'Reports that withhold email subjects now still list what was touched and when, showing every row without the subject line. Previously the whole table disappeared, so a report with subjects withheld said nothing about which items were accessed.',
      'An email flagged by the analyst as the phishing entry point is now described as exactly that, instead of the weaker "likely lure".',
    ],
  },
  {
    version: '1.141.1',
    date: '2026-08-11',
    title: 'Executive Report: the scope note leads with what was reviewed',
    changes: [
      'The report\'s scope note now reads "229 events were incident related; 2,217 were normal activity, set aside by the reviewing analyst" rather than stating only how many events were excluded. On a typical mailbox case most recorded activity is the account holder\'s own, so an exclusion count on its own understated the work rather than describing it.',
    ],
  },
  {
    version: '1.141.0',
    date: '2026-08-11',
    title: 'Case filters are always visible, and executive reports state what they counted',
    changes: [
      'Every filter on a case now stays visible, including the ones with nothing to hide — those show a count of (0) and appear dimmed. Filters used to appear and disappear as an investigation progressed, so you could not tell a filter that was switched off from one that was not there at all, and a filter you wanted was sometimes unavailable until the case data happened to warrant it.',
      'Microsoft background mailbox reads are now hidden by default. These are automated, sessionless reads with no person behind them, and they were the largest source of noise on a mailbox case. The "MS substrate" filter is always visible if you want to see them.',
      'Executive reports are now built from one fixed set of filters, so the same investigation always produces the same figures. If the case view is not set for reporting, the Executive Report dialog names the filters that differ and sets them for you in one click.',
      'Report figures that count a reviewed subset now say so — "Messages reviewed" rather than "Messages in the traced period" — and a report states plainly how many recorded events the reviewing analyst excluded.',
      'Executive reports now summarize mailbox activity by top-level folder (Inbox, Sent Items, Deleted Items) instead of listing every subfolder, with messages counted once per folder even when they appear in several subfolders.',
    ],
  },
  {
    version: '1.140.9',
    date: '2026-08-11',
    title: 'Executive Report: raw code-like text can no longer appear in the narrative',
    changes: [
      'In one report the "Bottom Line" section contained three lines of raw code-like text in place of narrative. The writing assistant had started writing structure into a paragraph, and nothing was checking that a paragraph reads as a sentence. Every piece of text the assistant writes is now checked before the report is built; if any of it is not prose, the assistant is asked again, and the report is never published with it.',
    ],
  },
  {
    version: '1.140.8',
    date: '2026-08-11',
    title: 'Executive Report: two corrected figures and a more faithful narrative',
    changes: [
      'The report\'s provenance block reported "Records in run: 0". It now states how many records the investigation run it was built from actually holds.',
      'The "Emails sent from the account" figure only counted mail sent from the mailbox\'s primary address, so a mailbox that also sends under a second address — common where the sign-in address and the email address differ — was undercounted. It now counts every address the investigation searched for that person, and the report says plainly when a mailbox uses more than one.',
      'Where an email subject line happened to match a word the report uses structurally — a folder name, or the name of a figure — the writing assistant received a garbled version of that structure and could not describe mailbox activity folder by folder. Subject lines are now substituted only where a subject belongs, so the narrative is written from intact facts. Subject lines and file names are still never sent to the writing assistant.',
    ],
  },
  {
    version: '1.140.7',
    date: '2026-08-11',
    title: 'Executive Report: reports with subject lines revealed now generate',
    changes: [
      'Generating a report with email subjects revealed failed immediately with a message about subjects not being removed from the model input. The safety check was flagging the report\'s own "(no subject)" placeholder — used where a mail item has no subject line — as if it were a real subject. Those reports now generate, and the check still blocks a report if any real subject line would reach the writing assistant.',
    ],
  },
  {
    version: '1.140.6',
    date: '2026-08-09',
    title: 'Executive Report: a finished report no longer shows as still generating',
    changes: [
      'Reports were being written correctly but never marked finished, so the dialog kept saying "generating" indefinitely and the report never appeared in the list. The completed report is now recorded properly and opens as soon as it is ready.',
    ],
  },
  {
    version: '1.140.5',
    date: '2026-08-08',
    title: 'Executive Report: reports now actually generate',
    changes: [
      'A started report sat at "generating" forever and never finished. The background worker could not read its own job message, so generation never began.',
      'A report stuck that way also blocked every later report on the same case. Stuck jobs are now cleaned up automatically — reopening the Executive Report dialog releases them and lets you try again.',
    ],
  },
  {
    version: '1.140.4',
    date: '2026-08-08',
    title: 'Executive Report: generation is no longer rejected on most cases',
    changes: [
      'Generating a report failed with "the report data no longer matches this case\'s investigation results" on any case containing URL clicks, directory audit, message trace, risky user, risk detection, Azure activity or security alert events — which is nearly all of them. The report data was naming those log sources differently from the check that validates it.',
      'When that check does reject a report, the dialog now lists the specific reasons instead of only suggesting a reload.',
    ],
  },
  {
    version: '1.140.3',
    date: '2026-08-08',
    title: 'Executive Report: fixed an error when generating',
    changes: [
      'Clicking Generate failed with a script error on every case. The report builder was reading the case\'s phishing-flag list in the wrong format, which also made the dialog report zero email subjects even when subjects were revealed. Both fixed.',
    ],
  },
  {
    version: '1.140.2',
    date: '2026-08-08',
    title: 'Executive Report: the Generate button now works',
    changes: [
      'The Executive Report dialog said an approved brand template was needed even when one had been added, and the Generate button stayed greyed out. The check that looks up approved templates was comparing against the wrong shape of list, so it never matched any MSP. Fixed — a case whose MSP has a template can now generate a report.',
    ],
  },
  {
    version: '1.140.1',
    date: '2026-08-07',
    title: 'Executive Report (AI): an executive-level write-up of a case',
    changes: [
      'A third report button on a case, beside Client Report and Technical Report. It turns the same investigation data into a short, plain-language incident report written for business leadership, branded for the MSP that owns the case.',
      'Writing takes one to four minutes, so it runs in the background. Start it, and it appears in the case\'s report list when it is ready — you can close the dialog or leave the page while it works.',
      'Email subject lines and file names are never sent to the AI. They are replaced with placeholders before the request and the real text is put back when the report is built, so the finished report still shows them in full while the AI itself never reads one.',
      'Every number, date, IP address and subject line in the report comes from your investigation data. The AI chooses what to highlight and writes the explanation around it; it never supplies a figure itself, and the severity rating is calculated, not guessed.',
      'Each report records exactly which investigation run and which active filters produced it, so two reports of the same case can always be told apart.',
      'If you generate a report while email subjects are revealed, the subjects are written into it and stay readable to anyone who can see the case. Those reports are labelled "contains email subjects" in the list before you open one, and every viewing is logged.',
      'Available per MSP as each brand template is approved, starting with PathForward IT. On a case whose MSP has no approved template the button explains that rather than producing an unbranded report.',
    ],
  },
  {
    version: '1.139.1',
    date: '2026-08-07',
    title: 'AAD Session IDs: columns can now be resized',
    changes: [
      'Drag any column edge in the AAD Session IDs table to resize it, the same as the Alerts and Coverage tables. Useful there in particular, since session IDs are long and provider names vary a lot in length.',
      'Text that no longer fits is shortened with a tooltip showing the full value, so widening a column always reveals more rather than reflowing the row.',
    ],
  },
  {
    version: '1.139.0',
    date: '2026-08-06',
    title: 'One consistent way to classify: the coloured circle, first column',
    changes: [
      'Classifying a row is now the same gesture everywhere. IP Addresses, URL Clicks and AAD Session IDs all use the same coloured circle in the first column, as does the session list in the Attacker Window panel. Previously the same action appeared as a circle in one table, a labelled pill in another and a text chip in a third.',
      'The colours mean the same thing wherever you are: green is cleared, amber is suspect, red is condemned, grey is unclassified. Hovering always names the current state, so you never have to rely on colour alone.',
      'On URL Clicks the "Class" column moved to the front and became "Status", matching every other table.',
      'The four Default / Safe / Suspect / Malicious buttons inside an expanded event row are unchanged — the circle cycles, and there you need to see and reach every option at once.',
      'Fixed cramped layout in the expanded event row, where a long IP address could break across lines mid-address and the classification buttons wrapped awkwardly.',
    ],
  },
  {
    version: '1.138.0',
    date: '2026-08-06',
    title: 'Timeline: AAD Session ID column replaces VPN/Proxy',
    changes: [
      'The All Events timeline now has an AAD Session ID column in place of VPN/Proxy. It is sortable, has its own filter dropdown, and is matched by the "Filter events" search box — so you can paste a session ID from the AAD Session IDs tab and collapse the timeline to just that session.',
      'The session is colour-matched to its state: red if it was touched by a malicious IP, green if you have marked it safe. Events that carry no session ID show a dash, because they cannot be attributed to one.',
      'VPN, proxy and TOR detail is unchanged on the Sign-In Logs view, in each event\'s expanded detail, and in IP Intelligence. Those flags also still force events into view when a trust filter would otherwise hide them.',
    ],
  },
  {
    version: '1.137.1',
    date: '2026-08-06',
    title: 'AAD Session IDs: mark sessions safe with the same chip used on the Timeline',
    changes: [
      'The Status column has moved to the far left, matching the IP Addresses table, and now uses the same green "safe" chip as the Attacker Window panel\'s "Other Sessions Seen" list. It drives the same setting — mark a session safe in either place and it is safe in both. Previously it sat on the far right and looked like a label rather than a button.',
      'Sessions that touched a malicious IP show a red "compromised" chip that cannot be cleared here, because a compromised session overrides a safe mark when events are filtered. To change it, un-classify the malicious IP.',
      'Unlike the Timeline list, this table shows every session — including compromised ones, and without its 50-session limit.',
    ],
  },
  {
    version: '1.137.0',
    date: '2026-08-06',
    title: 'AAD Session IDs: a "User?" column showing which sessions the real user was on',
    changes: [
      'Each session now shows whether it contains an IP the user actually signed in from before the incident, taken from their 7-day sign-in baseline. Sessions the user has no history on are marked "no baseline IP" and can be sorted together.',
      'This is often the only thing that separates attacker activity from legitimate activity. Microsoft frequently fetches SharePoint files on your behalf from its own servers, so the recorded IP is Microsoft\'s and the real client IP is never captured — an attacker\'s file access and a genuine one look identical on every other field. Which session it happened under is the difference.',
      'The answer comes from the user\'s own sign-in history, so unlike IP classifications it cannot be skewed by a judgement call or by anything the attacker did during the incident.',
      '"No baseline IP" is not by itself proof of compromise — a new office, a phone or a VPN all look the same — but a session that was only ever the attacker\'s can never look like anything else.',
    ],
  },
  {
    version: '1.136.4',
    date: '2026-08-06',
    title: 'AAD Session IDs: network type is no longer colour-coded',
    changes: [
      'Provider and network type now render in a single muted tone. Colour in the IP column is reserved for your own classifications — safe, suspect, malicious — so tinting the network type competed with that and suggested a verdict the portal had not made.',
    ],
  },
  {
    version: '1.136.3',
    date: '2026-08-06',
    title: 'AAD Session IDs: the ISP is now shown beside every IP',
    changes: [
      'Each IP in the session matrix now shows its provider and network type underneath it — for example "Tier.Net Technologies LLC · Hosting" — with city, state and country on hover.',
      'Provider and network type are searchable, so searching "hosting" finds every session that touched a datacenter.',
    ],
  },
  {
    version: '1.136.2',
    date: '2026-08-06',
    title: 'AAD Session IDs: see at a glance which IPs belong to only one session',
    changes: [
      'Every IP in the session matrix is now marked with how widely it appears. An asterisk means the address was seen only in that session and nowhere else in the case; "×N" means it is shared across N sessions. Hovering an IP gives the full breakdown.',
      'Events that carry no session ID are included in that judgement — around a third of events in a typical case have none, so ignoring them would have labelled addresses "only seen here" while they were active elsewhere in the case.',
    ],
  },
  {
    version: '1.136.1',
    date: '2026-08-06',
    title: 'Fixed: AAD Session IDs showed some IPs as safe when the IP Addresses tab did not',
    changes: [
      'An IP could appear green (safe) in the new AAD Session IDs tab while showing as unclassified in the IP Addresses tab. The session tab was treating any address used by a trusted application as safe, rather than using only your own classifications and the tenant safe-IP list.',
      'That mattered: when an attacker replays a stolen session they use the same application as the real user, so an attacker address could have been shown as safe — and if every address in a session looked safe, the "mixed classification" warning would not have appeared on precisely the sessions it is meant to catch. Both tabs now use the same rule.',
    ],
  },
  {
    version: '1.136.0',
    date: '2026-08-06',
    title: 'New case tab: AAD Session IDs',
    changes: [
      'A new tab alongside Timeline, IP Addresses, Devices and Baseline shows every session in the investigation as a single row: the session ID, all IP addresses seen under it, first and last seen, the operating systems, and the browsers or mail clients used. Sortable, searchable and paginated.',
      'IP addresses are colored with the classifications you have set in the case, and any session whose IPs disagree — a known-good address and an attacker address under one session — is flagged as "mixed classification". That pattern is the signature of a stolen session token, and it is very hard to spot in a time-ordered timeline.',
      'Sessions that touched an IP you marked malicious are marked Compromised automatically. Any other session can be marked Safe from this tab, and the mark is shared with the rest of the case.',
      'This tab deliberately ignores the filter bar. Hiding safe or service IPs would remove exactly the rows that reveal a mixed session.',
    ],
  },
  {
    version: '1.135.4',
    date: '2026-08-06',
    title: 'Fixed: adding a service IP did nothing unless you were a global administrator',
    changes: [
      'Adding or removing a service IP — from the Service IP Events tab inside a case, or from the Service IPs settings page — silently failed for everyone except global administrators. The form was on screen for all users, the button responded, and nothing happened: the rejection was being discarded instead of shown.',
      'Any signed-in user can now add and remove service IPs. Marking an address as Microsoft service infrastructure moves its events into the Service IP Events tab, where they stay visible with per-IP counts and can be switched back on from the case filter bar — nothing is hidden permanently, and every change is recorded in the Activity Log.',
      'The Service IPs page now shows who added each entry instead of listing every row as "API".',
      'If an add or remove is ever refused, the reason now appears next to the button rather than being swallowed.',
    ],
  },
  {
    version: '1.135.3',
    date: '2026-08-06',
    title: 'Fixed: most IP addresses in a case showed no country, provider or network type',
    changes: [
      'The IP Addresses table could list an IP with every intelligence column blank — no country, provider, network type, or Microsoft-infrastructure label. In one investigation 90 of 136 rows were affected. Microsoft\'s own service addresses were the most common victims, and with the label missing they read as unclassified third-party IPs rather than known Microsoft infrastructure.',
      'Every IP shown in the table now has its intelligence looked up, including addresses that only appear on events whose real source IP could not be determined. Risk scoring is unchanged — Microsoft service addresses still do not contribute risk, because they are not the user\'s.',
    ],
  },
  {
    version: '1.135.2',
    date: '2026-08-06',
    title: 'Fixed: case timeline crashed on open',
    changes: [
      'Opening a case crashed the page with a blank screen. A helper added in 1.135.0 to mark events logged under a user\'s alternate address was used in the timeline without being imported, so the timeline threw as soon as it rendered. Investigations were unusable on that build.',
    ],
  },
  {
    version: '1.135.1',
    date: '2026-08-06',
    title: 'Fixed: leaving Demo Mode could fail silently and strand the portal',
    changes: [
      'Exiting Demo Mode could finish its countdown and then not reload, leaving the portal switched on in Demo Mode across reloads and sessions. Because the portal is deliberately read-only while Demo Mode is on, this looked like a broken site rather than a stuck setting — every save and every investigation run was refused. Recovering needed the browser\'s developer console.',
      'Demo Mode is now switched off the moment the countdown reaches zero, before the reload is attempted, so it can never survive a reload that does not happen. If the page does not refresh on its own, the dialog says so and offers a "Reload now" button — and because the setting is already off, a refresh from anywhere brings real data back.',
      'The countdown now runs off the clock rather than counting itself down, so it still completes if you switch tabs or windows while it is running — which the dialog asks you to do, to stop screen sharing.',
    ],
  },
  {
    version: '1.135.0',
    date: '2026-08-06',
    title: 'Investigations now find activity logged under a user\'s other email addresses',
    changes: [
      'A person is not always one address. Sign-ins are recorded under the account\'s Microsoft sign-in name, but mailbox activity is often recorded under their email address instead — and for roughly one user in six across our tenants, those two are different. Until now an investigation searched only the address you typed, so on a split account the mailbox evidence was missing from the case entirely and the Exchange tab simply looked empty. In a real incident this hid 354 messages an attacker had opened.',
      'Every investigation now looks the person up in Microsoft Entra when it runs, then searches every address that one account answers to — across sign-ins, mailbox activity, email trace, SharePoint, directory changes, alerts and the behaviour baseline. You can open the case under either address; the case header shows what else was searched, and events that came in under an alternate address are marked "alias identity" in the timeline.',
      'When the lookup cannot safely resolve alternate addresses, the case now says so at the top rather than quietly searching just one. That covers a user who is not in the directory (shared, room and resource mailboxes are not), a directory lookup that failed, and — deliberately — the case where an address is shared by more than one account: rather than guess and risk pulling a second person\'s activity into your case, only the address as typed is searched and you are told why.',
      'Also fixed: an account whose sign-in name is stored with different capitalisation than the case no longer loses its behaviour baseline, which previously zeroed the "unusual country / IP / device" risk scoring for that user.',
      'This applies to new investigation runs. To pick up the wider search on an existing case, use Retrieve Logs to re-run it.',
    ],
  },
  {
    version: '1.134.2',
    date: '2026-08-06',
    title: 'Demo Mode: every fake company is labelled "Demo"',
    changes: [
      'In Demo Mode, fictional company and MSP names now end in "Demo" — "Fourth Coffee Demo", "Northgate Technology Demo". On their own the fictional names read as though they could be real clients, so a tenant list can no longer be mistaken for an actual client roster.',
      'Fixed: an MSP\'s fake code and fake name could come from two different fictional MSPs, so the same partner appeared under mismatched names. Fixed: a fictional company could also change after a page reload — a tenant\'s name and its Microsoft domain could end up pointing at two unrelated companies.',
    ],
  },
  {
    version: '1.134.1',
    date: '2026-08-06',
    title: 'Demo Mode: fake email domains are now obviously fake',
    changes: [
      'In Demo Mode, every fake domain now ends in .demo — reid.chandler@fourthcoffee.demo rather than …@fourthcoffee.com. The fictional company names on their own read as though they could be real clients, and a demo should never leave a prospect wondering whether they are looking at someone else\'s data. A tenant\'s Microsoft domain keeps its familiar shape for the same reason it is recognisable: contoso.onmicrosoft.demo.',
      'Every domain a tenant owns now maps to the same fictional company, so its primary domain and its .onmicrosoft domain no longer show up as two unrelated companies.',
    ],
  },
  {
    version: '1.134.0',
    date: '2026-08-06',
    title: 'Demo Mode — show the live portal without showing customer data',
    changes: [
      'New "Demo Mode" button at the bottom of the sidebar (global administrators only). While it is on, every piece of customer-identifying data the portal displays is replaced on the fly: company and MSP names become fictional companies, people become fake names and email addresses, email subjects become generic ones, device and group names are replaced, tenant IDs show as 752b8092-****-****-****-************, and IP addresses show as **.**.45.67 (IPv4) or ****:****:****:****:… (IPv6). The same real value always maps to the same fake value, so grouping, filters and baseline comparisons still line up.',
      'Because it transforms the live portal, a demo always shows the current product — there is no separate demo site to keep up to date.',
      'The portal is read-only while Demo Mode is on: any save, status change, rule edit or job trigger is refused with "Portal is read-only in Demo Mode." Read-only investigation pivots keep working normally — IP prevalence, List users, Session Trace, Token Use and a case\'s real-user evidence all run against live data and display it anonymized. A few controls that spend quota or write back are shown disabled instead: IP enrichment refresh, the manual "Resolve Name" lookup, and the cross-tenant IP activity search.',
      'Turning Demo Mode on is instant. Turning it off asks for confirmation and then counts down 30 seconds before real data reappears, so there is time to stop screen sharing — the countdown can be cancelled at any point. An amber "DEMO MODE" banner stays visible at the top of every page while it is active.',
      'Note: the admin consent URL on a tenant is shown with the masked tenant ID so the flow can be walked through, which means it is not a usable link — copying it is disabled in Demo Mode.',
    ],
  },
  {
    version: '1.133.1',
    date: '2026-07-31',
    title: 'User Guide corrections',
    changes: [
      'The Alerts section of the User Guide now describes the "Re-fetch IP enrichment" button correctly: it is available to every portal user, and it asks you to confirm if the IP was already enriched in the last 24 hours.',
      'The same guide page also said Token Use was an administrator action. It is not — anyone who can see an alert can run the Token Use pivot on it. Session Trace is the one action on an alert that does require an administrator.',
    ],
  },
  {
    version: '1.133.0',
    date: '2026-07-31',
    title: 'IP enrichment refresh is now available to everyone',
    changes: [
      'The IP enrichment buttons are no longer restricted to administrators. On a case, the IP Addresses tab now shows "Enrich unenriched" and the small refresh icon beside each IP for every user — use them to fill in the location, ISP and risk data for IPs the nightly enrichment has not covered yet.',
      'The same refresh icon is now available to everyone on the Alerts and Test Run Results pages, in the IP Intelligence panel and next to "Event value" in Baseline Comparison. On a real alert the refreshed values are saved to the alert, so they are still there after a reload.',
      'If an IP was already enriched in the last 24 hours you will be asked to confirm before it is re-fetched. Two actions stay administrator-only: "Check real-user evidence", and adding a case’s malicious IPs to the shared blocklist (that one affects detection for every tenant).',
    ],
  },
  {
    version: '1.132.0',
    date: '2026-07-30',
    title: 'Fixed: long investigations could show an empty Directory Audit tab',
    changes: [
      'Bug fix: on an investigation covering a long date range, the Directory Audit tab could come back empty even when directory events existed. The same case over a shorter range showed them correctly. The events were never missing from the log data — the query that fetched them was hitting a size limit and failing, and the failure was being reported as "no events". Directory Audit is where account disables, password resets, session revocations and MFA changes live, so this could hide exactly the remediation and tampering activity a case is built around. Long-range investigations now return complete Directory Audit results.',
      'New warning banner when part of a case fails to load. If any data source fails during an investigation, the case now shows an amber banner above the tabs naming the affected workloads and stating plainly that a zero count for them does not mean no events occurred. Previously a failed query looked identical to a genuinely quiet workload — an empty tab. Re-running the investigation, or narrowing the date range, resolves it. The banner cannot be dismissed while the results are incomplete.',
      'If you have an open case with a long date range, re-run it to pick up any Directory Audit events that were missed.',
    ],
  },
  {
    version: '1.131.1',
    date: '2026-07-29',
    title: 'User Guide, now inside the portal',
    changes: [
      'New User Guide in the sidebar (just above Release Notes), starting with a full "Alerts" section — how to read the alerts list, what every panel in an expanded alert is telling you, how to run a Session Trace or Token Use pivot, and how each of the seven quick-suppress actions works.',
      'It is a working part of the portal, not a document: references to other pages are links you can click, the table of contents on the right tracks where you are, and a small "?" next to the Detection Alerts heading opens the guide at the matching section.',
      'The guide was checked line by line against what the portal actually does before it went in, so a few long-standing misconceptions are corrected — most usefully, the "Reconcile sentinel" button on the allowlist pages has nothing to do with Microsoft Sentinel; it just nudges the detection pipeline to re-read your allowlist, and you rarely need it.',
      'A "Running a Case Investigation" section is planned and shows as coming soon on the guide landing page.',
    ],
  },
  {
    version: '1.130.0',
    date: '2026-07-28',
    title: 'Record a tenant’s Microsoft cloud (Commercial / GCC / GCC High / DoD)',
    changes: [
      'Managed tenants now carry a Cloud Environment setting — Commercial (the default for almost every tenant), GCC, GCC High, or DoD. Set it in the onboarding wizard or on an existing tenant from the Edit drawer. Government-cloud tenants show a coloured "Cloud" badge on the Managed Tenants page; commercial tenants stay unmarked.',
      'Why it matters: Microsoft does not support the message-trace API in government clouds, so log collection previously retried that feed forever and raised a daily collection alert that nobody could act on. With the cloud recorded, collection skips just that one feed for those tenants — everything else is collected as before — and keeps checking daily in case Microsoft adds support later.',
      'Bug fix: saving a tenant that had no License Tier set failed with a "licenseTier must be one of…" error, even when you were only changing an unrelated field. Tier is optional (it is normally filled in later by the Probe action), and saving now works with it left blank.',
    ],
  },
  {
    version: '1.129.0',
    date: '2026-07-21',
    title: 'Detection rules: edit any field in PROD, plus a per-event alerting option',
    changes: [
      'Detection rules are now fully editable in both TEST and PROD. Previously a PROD rule was locked to enable/disable only, so changing its severity, scope, suppression toggles, or detection settings meant demoting it to TEST first (which stops live alerting). You can now adjust a live PROD rule in place — every save still versions the rule and takes effect on the next pipeline refresh.',
      'New rule setting "Alert on every matching event" (next to Cooldown). When off (default), repeated matches for the same user collapse to one alert per cooldown window; turn it on for rules that should surface every distinct source event — useful for Microsoft-security pass-through rules that emit several alerts for one user in a burst. With it on, the cooldown no longer collapses per-user duplicates.',
    ],
  },
  {
    version: '1.128.1',
    date: '2026-07-20',
    title: 'IP addresses in IP Intelligence now clearly link out',
    changes: [
      'In an alert\'s IP Intelligence panel, the IP address links to an external lookup (iplocation.net) but didn\'t look clickable. It now shows a small external-link arrow and an "opens a new tab" tooltip so it\'s clear the IP takes you to another site.',
    ],
  },
  {
    version: '1.128.0',
    date: '2026-07-20',
    title: 'Resolve names for brand-new groups/users on directory alerts',
    changes: [
      'Directory audit alerts (like Conditional Access policy changes) now show a "Resolve Name" button next to any group, user, or role that still appears as a raw ID. This happens when the object was created the same day it was referenced — our once-a-day directory sync hasn\'t picked it up yet. Clicking the button looks the name up from recent activity data and saves it to the alert, so the friendly name then shows for everyone, permanently. If it can\'t be found yet, you\'ll see "try again after 30 min".',
    ],
  },
  {
    version: '1.127.0',
    date: '2026-07-20',
    title: 'Conditional Access changes now name the added/removed group or user',
    changes: [
      'When a directory audit alert shows a Conditional Access policy change (or other directory changes), the "Target Resource" details now display the friendly name of any group, user, or directory role that was added or removed — instead of only the raw object ID. For example, adding a group to a policy\'s included groups now reads as the group\'s name with the ID shown alongside it. This works on both the Detection Alerts page and the Test Run Results page.',
    ],
  },
  {
    version: '1.126.1',
    date: '2026-07-20',
    title: 'Copy button on the Alerts "User" column now copies what you see',
    changes: [
      'On the Detection Alerts page, the copy button next to the User column now copies the exact identity shown in that column. Previously, for a few alert types (such as "Anomalous access to other users\' mailboxes," where the column shows the mailbox owner), the button copied a different underlying account instead — so pasting it into a search gave you the wrong user. The copied value now always matches the displayed one.',
    ],
  },
  {
    version: '1.126.0',
    date: '2026-07-20',
    title: 'MSP data separation enforcement made permanent',
    changes: [
      'MSP-based data separation — which limits each MSP user to their own MSP\'s tenants, cases, alerts, and events — is now permanently enforced. This has been active since June, so you should see no change; the update simply removes the internal switch that could have turned it off, hardening tenant isolation.',
    ],
  },
  {
    version: '1.125.2',
    date: '2026-07-20',
    title: 'Testing a rule now clears its previous test results first',
    changes: [
      'When you start a test run for a detection rule, the portal now deletes that rule\'s earlier test results before the run begins. Previously, results from an earlier test (often over a different date range) lingered and were shown under the new run\'s header — making it look like the rule matched events outside the dates you selected. Each test run now shows only its own matches.',
    ],
  },
  {
    version: '1.125.1',
    date: '2026-07-17',
    title: 'See exactly which licenses a tenant has, right on the Cost dashboard',
    changes: [
      'Cost dashboard: the "Tenant profile" panel (drill into a tenant) now shows a full license breakdown — each Microsoft product and how many users are assigned it (for example "30 × Microsoft 365 E5, 10 × Business Premium"), plus how many users are licensed vs. unlicensed. It sits right under the Global Administrators list, so the cost figures have real licensing context.',
      'Product names come from our shared Microsoft licensing reference, so they stay consistent with our other tooling. A few specialized SKUs Microsoft doesn\'t publish a name for (e.g. partner/sandbox or brand-new products) may show their raw ID until a name is added — they\'re never hidden.',
    ],
  },
  {
    version: '1.124.0',
    date: '2026-07-17',
    title: 'Silence noisy Microsoft "service account" false-positive alerts in one click',
    changes: [
      'Microsoft-security pass-through alerts can now be suppressed by their actor. Some high-severity Defender alerts (e.g. "Mailbox Permission Change") are really just Microsoft\'s own background service doing routine maintenance — for example provisioning the built-in eDiscovery mailbox — and there\'s nothing to investigate. Admins can now click "Suppress this alert actor" on such an alert to stop that specific Microsoft service account from raising it, without hiding the same alert type when a real user is involved. A genuinely-malicious IP still alerts.',
      'Admins can also manage the excluded actor list directly on the rule (Alert Rules → edit a Defender pass-through rule → "Excluded Alert Actors"), alongside the existing excluded titles and descriptions.',
    ],
  },
  {
    version: '1.123.1',
    date: '2026-07-17',
    title: 'Fixed: "Link source audit event" error on alerts triggered by a service account',
    changes: [
      'On a Microsoft-security pass-through alert whose actor is a service account (for example an Exchange background service), pressing "Link source audit event" no longer fails with an error — the portal now handles alerts that have no user object ID and proceeds to search for the matching audit event.',
    ],
  },
  {
    version: '1.123.0',
    date: '2026-07-16',
    title: 'See a tenant’s size and admins right on the Cost dashboard',
    changes: [
      'Cost dashboard: when you drill into a tenant, a new "Tenant profile" panel shows how big it is at a glance — active user count, number of groups, license tier, and exactly who its Global Administrators are — so the cost figures have context. Click to expand (it loads on demand).',
    ],
  },
  {
    version: '1.122.0',
    date: '2026-07-16',
    title: 'A Top Users view on the Activity Log, plus clearer admin menu labels',
    changes: [
      'Activity Log: global admins can now click "Top Users" to see who has been most active in the selected date range — each person\'s total actions, the action they perform most, and their top category — all without leaving the page.',
      'UI improvements: clearer admin menu labels ("PSA Setup", "PSA Ticket Routing", "Alert Webhooks"); an MSP with alert email turned off now reads "(currently disabled)" instead of a stark "Disabled"; and a small wording fix on the Cost dashboard.',
    ],
  },
  {
    version: '1.121.2',
    date: '2026-07-16',
    title: 'Closing an alert no longer loses your note on a stray click',
    changes: [
      'When you set an alert to Closed and start typing a resolution note, clicking outside the note box no longer discards it and closes the dialog. The close dialog now stays open until you confirm, cancel, press Escape, or click the X.',
    ],
  },
  {
    version: '1.121.1',
    date: '2026-07-15',
    title: 'PSA-closed alerts show the ticket number you recognize',
    changes: [
      'When closing a PSA ticket auto-closes its alert, the "via PSA ticket" badge now shows AutoTask’s display ticket number (e.g. T20260713.0416) instead of the internal id. Alerts closed earlier today are being corrected to match.',
    ],
  },
  {
    version: '1.121.0',
    date: '2026-07-15',
    title: 'Closing a PSA ticket can now close its alert',
    changes: [
      'When a ticket that was opened from an alert is closed in your PSA (AutoTask or ConnectWise), the matching alert now closes automatically and carries the ticket’s closing note as its resolution note. A PSA-closed alert shows a "via PSA ticket" badge on its Resolution Note. If an analyst already closed the alert, their reason is kept. (Activates once enabled for your environment.)',
      'PSA Configuration (AutoTask): a new optional "Ticket-Closed Status IDs" field lets you choose which ticket statuses count as "closed" for this automation — leave it empty to use AutoTask’s Complete status. ConnectWise needs no setting.',
    ],
  },
  {
    version: '1.120.1',
    date: '2026-07-15',
    title: 'Linked source events: shared mailboxes now show their name',
    changes: [
      'When a linked mailbox-permission change targets a shared mailbox, the "Link source audit event" panel now shows the mailbox name (e.g. "LCT-Sales Leads") instead of a raw object ID. Re-check the source events on an alert linked before this update to refresh the name.',
    ],
  },
  {
    version: '1.120.0',
    date: '2026-07-15',
    title: 'Link source audit event: shows real names and captures every change at once',
    changes: [
      'On Microsoft-security pass-through alerts (e.g. "Mailbox Permission Change"), the "Link source audit event" candidate list now shows the mailbox and the person granted access by name (UPN) instead of raw object IDs.',
      'When one alert covers several changes (for example, one permission-change alert where an admin granted four people access to a mailbox), a single click now links and saves all of them together — the alert shows the full list of changes, and it survives a page refresh. You can still link just one change if you prefer.',
    ],
  },
  {
    version: '1.119.5',
    date: '2026-07-13',
    title: 'Investigations Avoided defaults to the last 7 days',
    changes: [
      'The Investigations Avoided page now opens on the last 7 days by default (previously 30), matching the most common view. The 30d and 90d buttons still switch to a longer window.',
    ],
  },
  {
    version: '1.119.4',
    date: '2026-07-13',
    title: 'Investigations Avoided: the "new country" suppression no longer skews the numbers',
    changes: [
      'On the Investigations Avoided dashboard, the "Sign-in from New Country" country-baseline suppression was counted on every repeat sign-in, which inflated it to roughly 4–5× the next-largest method and made it dominate the daily chart and the breakdown tables. It is now counted once per user, country, and day, so the dashboard reflects real suppression value. Every other method is unchanged, and the totals still line up across the chart, tables, and the headline count.',
    ],
  },
  {
    version: '1.119.3',
    date: '2026-07-13',
    title: 'Cost dashboard: weekends are now shaded',
    changes: [
      'Weekend columns (Saturday/Sunday) in the Cost dashboard tables now have a subtle shading so you can pick out weekends at a glance across the 7, 30, and 90-day views.',
    ],
  },
  {
    version: '1.119.2',
    date: '2026-07-12',
    title: 'Cost dashboard: today no longer shows a blank column in the evening',
    changes: [
      'Fixed a case where the Cost dashboard could show today as an empty column (and skew the 30-day projection) during evening hours. The dashboard now uses Eastern Time to decide the last day to show, so today\'s always-blank row stays out of the tables and the projection.',
    ],
  },
  {
    version: '1.119.1',
    date: '2026-07-12',
    title: 'Cost dashboard: hide disabled clients, plus the 30-day projection on a single tenant',
    changes: [
      'Disabled (inactive) clients are now hidden from the Cost dashboard by default so the totals reflect only active clients; a "Show inactive (N)" toggle brings them back when you need them.',
      'The "Projected 30-Day" column now also appears when you drill into a single tenant, so you can see the projected monthly cost per line item for that client.',
    ],
  },
  {
    version: '1.119.0',
    date: '2026-07-12',
    title: 'Cost dashboard: 7-day default, today excluded, and a 30-day cost projection',
    changes: [
      'The Cost dashboard now defaults to the last 7 days and no longer shows today (the cost feed always lags a day, so today was always blank). The per-tenant cost matrix gains a "Projected 30-Day" column that scales each tenant\'s total to a 30-day estimate at the current window\'s daily rate — a quick read on monthly run-rate. Still an internal, alpha cost-to-serve estimate, not client billing.',
    ],
  },
  {
    version: '1.118.0',
    date: '2026-07-12',
    title: 'See when each tenant was added to monitoring',
    changes: [
      'The Tenants page now shows a "Monitored Since" date for every tenant, and the tenant Edit panel breaks it out into three dates: when the tenant was onboarded, when log collection was first activated, and the earliest log event we have on record. New tenants get exact onboarded/activated timestamps automatically. For tenants added before we started tracking this, the date shown is approximate (marked with a ≈), based on the earliest data we have.',
    ],
  },
  {
    version: '1.117.0',
    date: '2026-07-11',
    title: 'Link a Microsoft-security alert to the audit event that shows what actually changed',
    changes: [
      'Microsoft-security pass-through alerts like "Mailbox Permission Change" tell you who acted but not what changed. A new "Link source audit event" button on the alert detail finds the matching Exchange admin audit event and shows the mailbox that was changed (flagged if its account is disabled), who was granted access, the exact permission, and the source operation, IP, and time. If the source log hasn\'t been ingested yet (Microsoft delivery can lag up to an hour) you\'ll get a "try again later" note; if more than one event matches, you pick the right one. Use "Re-check source event" to re-run it any time.',
    ],
  },
  {
    version: '1.116.0',
    date: '2026-07-11',
    title: 'Promote a case’s confirmed malicious IPs to the blocklist in one click',
    changes: [
      'When an investigation is complete, you can now push every IP you marked Malicious in a case straight into the cross-tenant Known Malicious IPs blocklist from the case’s IP Addresses tab. Each IP is added as type "attacker" with a note referencing the case (e.g. "per case IR-20260710-Lauram-001"); IPs already on the list have their note and date refreshed. A confirmation lists exactly which IPs will be added (with a warning when there are more than 10) before anything is written. Available to global admins.',
    ],
  },
  {
    version: '1.115.3',
    date: '2026-07-10',
    title: 'All Events: Security Alerts, Directory Audit, and Risk Detections stay visible under the Safe IPs filter',
    changes: [
      'In the All Events timeline, Security Alert and Risk Detection events are no longer hidden when "Safe IPs hidden" is active — they now stay visible regardless of IP, matching how Directory Audit events already behaved. These carry critical detection context you shouldn\'t lose just because the source IP is marked safe.',
    ],
  },
  {
    version: '1.115.2',
    date: '2026-07-10',
    title: 'Case timeline: trace an email to its URL clicks (and back), plus a clearer IP classification control',
    changes: [
      'Message Trace ↔ URL Clicks are now linked both ways. Expanding a message-trace record shows its Network Message ID (the value that ties an email to the URL-click events it generated) with a copy button and a "Find URL clicks" button that jumps to the URL Clicks tab filtered to that message. Expanding a URL-click record has the matching "Find message" button that jumps back to the Message Trace tab. (Previously the message-trace side didn\'t expose that ID at all.)',
      'The inline IP classification control in the All Events expanded row is now an explicit Default / Safe / Suspect / Malicious selector — click the state you want and it\'s set directly (the current one is checked). This replaces the single "click to cycle" button, which made it easy to land on a classification you didn\'t intend — and, with a filter active, immediately hide the row.',
    ],
  },
  {
    version: '1.115.1',
    date: '2026-07-10',
    title: 'Case timeline: the filter toggles now only affect the All Events table',
    changes: [
      'Fixed: the toolbar toggles on a case (Safe IPs, Safe apps, MS substrate, Outlook noise, Post-window, Pre-compromise, Failed, DLP, and the rest) were also hiding records from the individual sub-tabs — Message Trace, Sign-In, SharePoint, URL Clicks, DLP, General, Azure Activity, Risk, and Security Alerts/Incidents. They now scope to the All Events table only; each dedicated sub-tab shows its full record set.',
      'Generated reports, the excluded-record counts on the Service IP / Safe IP tabs, and the Attacker Window panel are unchanged — they still reflect the toggles.',
    ],
  },
  {
    version: '1.115.0',
    date: '2026-07-10',
    title: 'Case timeline: hide Outlook background noise, jump from an email to its URL clicks, and reclassify IPs inline',
    changes: [
      'New "Outlook noise" toggle on a case (next to "MS substrate hidden") hides the noisy Outlook background events (OutlookItemPrioritized — mailbox priority-classification telemetry with no user or attacker signal). The button only appears when a case actually has such events, and defaults to showing them until you click.',
      'In Message Trace, expanding an email now shows its Network Message ID (the value that links the email to its URL-click events) with a copy button and a one-click "Find URL clicks" button that jumps to the URL Clicks tab already filtered to that message — so you can trace a phishing email straight to the clicks it generated.',
      'In the All Events timeline, you can now cycle an IP address through its classification (Default → Safe → Suspect → Malicious) directly from the expanded row, without switching to the IP Addresses tab. The change saves to the case and re-colors that IP everywhere.',
    ],
  },
  {
    version: '1.114.0',
    date: '2026-07-10',
    title: 'Alerts: add a false-positive IP to the benign-IP allowlist in one click',
    changes: [
      'On the Detection Alerts page, alerts from rules that use the benign-IP allowlist now have a one-click "add this IP to the benign-IP allowlist" button in the Actions column. It opens a small dialog pre-filled with the alert’s source IP — just add a reason and confirm, instead of retyping the IP over on the settings page.',
      'Adding an IP here trusts it globally for Microsoft-security pass-through suppression (across every rule with the setting on, for all tenants). The dialog spells this out, and a genuinely-malicious IP still alerts.',
    ],
  },
  {
    version: '1.113.4',
    date: '2026-07-08',
    title: 'Cost dashboard: day-by-day cost matrices that add up to the full total',
    changes: [
      'The Cost page now presents cost as a day-by-day matrix. In the all-tenants (or per-MSP) view, rows are tenants and columns are each day in the selected range, with a total per tenant down the right side and a total per day along the bottom. Click a tenant to drill in.',
      'A tenant’s drill-down shows a line-item × day matrix that reconciles to the tenant’s full cost: an “M365 Log data” category breaks the log-driven cost out by log feed (sign-in, Exchange, directory audit, …), and the remaining categories (Storage, Enrichment, Portal, Shared infrastructure) show their makeup. Row and column totals are shown, and the grand total matches the headline total.',
      'Cost figures in the matrix are shown to the cent (sub-cent amounts show as $0.00 rather than long decimals).',
      'Cost components are the full, up-to-date set: Databricks compute is broken out into ingest, detection (streaming), detection (cron) and enrichment; “Overhead” is renamed “Shared infrastructure” (idle/always-on capacity); and Collection, Silver processing and Portal show live costs.',
    ],
  },
  {
    version: '1.112.1',
    date: '2026-07-07',
    title: 'Smarter “can’t reach the server” recovery + a new Azure cost-coverage view',
    changes: [
      'When a page can’t reach the server, the portal now checks whether you’re actually still signed in before telling you what to do. If your session is still valid — usually the case, since it was just a brief dropped connection — it suggests trying again or reloading instead of wrongly implying you were signed out, and only prompts “Sign in again” when your sign-in has genuinely expired.',
      'On a case, if a dropped connection interrupts starting or checking an investigation, the message now explains the investigation may still be running and asks you to reload to check — so you don’t accidentally start the same investigation more than once.',
      'New on the admin Cost page: an “Azure bill coverage” reconciliation panel comparing the authoritative Azure spend tagged to M365Signal against what the cost model captures and splits per tenant — a coverage percentage plus a daily breakdown of allocated vs. in-scope-but-unallocated vs. out-of-scope cost, so you can spot any cost center the model isn’t capturing. Internal estimate, not a client invoice.',
    ],
  },
  {
    version: '1.111.4',
    date: '2026-07-07',
    title: 'Clearer recovery when a page can’t reach the server',
    changes: [
      'If a page fails to load data because of a dropped connection or an expired session (previously shown as a cryptic “Failed to fetch”), the portal now tries to recover automatically and, if it can’t, shows a clear banner with a one-click “Sign in again” button (and a Reload option) — no more closing the tab and signing back in by hand.',
    ],
  },
  {
    version: '1.111.3',
    date: '2026-07-07',
    title: 'Cost dashboard: MSP filter now scopes the whole view',
    changes: [
      'Fixed: on the Cost page, choosing an MSP (e.g. “All tenants in LCT”) previously only narrowed the tenant picker — the totals and breakdowns still included every tenant. Selecting an MSP now scopes the entire overview (totals, by-tenant, by-component, trend, and coverage) to that MSP’s tenants.',
    ],
  },
  {
    version: '1.111.2',
    date: '2026-07-07',
    title: 'Cost dashboard: see which costs are still pending',
    changes: [
      'The daily cost trend no longer treats a not-yet-priced day as $0 — days still awaiting a cost figure are drawn with a hatched marker and the line breaks across them, so a genuine low-cost day and a “cost pending” day are no longer confused.',
      'New “Data Coverage” view on the Cost page shows, day by day, which cost components (or tenants) are priced, still cost-pending, or have no data yet — at a glance you can see exactly what’s outstanding.',
      'Every cost component in the model is now listed even before its dollars arrive — components still being onboarded show as “not onboarded” rather than being invisible, so the picture reflects the whole cost-to-serve model.',
    ],
  },
  {
    version: '1.111.1',
    date: '2026-07-06',
    title: 'New: per-tenant Cost dashboard (Alpha)',
    changes: [
      'A new admin “Cost” page shows an internal, per-tenant, per-day cost-to-serve estimate — tenants ranked by cost, a breakdown by cost component, a daily trend, and a per-tenant drill-down. Global admins see all tenants; MSP admins see their own.',
      'Clearly marked ALPHA: the figures are an incomplete internal estimate (more cost components are still being added, so totals understate actual cost) and do NOT represent client charges, invoices, or pricing.',
      'Before pricing data lands, unpriced tenants and totals clearly read “pending” rather than $0.00.',
    ],
  },
  {
    version: '1.110.23',
    date: '2026-07-05',
    title: 'Fixed: some settings pages could error after the passwordless-storage switch',
    changes: [
      'A follow-up to v1.110.22: right after the switch to passwordless (Managed Identity) storage, pages that read certain lists (e.g. Known Malicious IPs) could fail to load. Fixed; all pages load normally again.',
    ],
  },
  {
    version: '1.110.22',
    date: '2026-07-05',
    title: 'Under the hood: passwordless (Managed Identity) storage authentication',
    changes: [
      'Completing the storage modernization begun in v1.110.20, the portal now authenticates to its Azure storage using a passwordless Managed Identity instead of an account key. No pages, features, or data change — everything should look and behave exactly as before. If anything seems off (a list not loading, a save not sticking), please report it.',
    ],
  },
  {
    version: '1.110.21',
    date: '2026-07-05',
    title: 'Fixed: saving a detection rule failed after the storage modernization',
    changes: [
      'Disabling or saving a detection rule could fail with a “Failed to update detection rule” error introduced by the storage-layer modernization (v1.110.20). Rule saves now work again; no other pages were affected.',
    ],
  },
  {
    version: '1.110.20',
    date: '2026-07-05',
    title: 'Under the hood: storage-access modernization',
    changes: [
      'The portal’s internal storage access layer was modernized onto Microsoft’s current Azure Tables SDK, the first step toward passwordless (Managed Identity) storage authentication. No pages, features, or data change — everything should look and behave exactly as before. If anything seems off (a list not loading, a save not sticking), please report it.',
    ],
  },
  {
    version: '1.110.19',
    date: '2026-07-03',
    title: 'Security hardening: authorization on suppression, allowlist, and integration settings',
    changes: [
      'Closed a set of authorization gaps so settings and evidence data are strictly limited to what your role and MSP are permitted to see. The suppression/allowlist pages (Known Apps, Service IPs, Mailbox-Pair, Account, User/App and Group/App pairs, AiTM Trusted Pairs) and the Spray-Origin IP Watchlist now require sign-in and scope results to your tenants; adding or removing entries requires the appropriate admin role. Global administrators are unaffected; if you manage a subset of tenants, you now see only those.',
      'The three global cross-tenant lists (Teams-Phone Models, Defender File-Hash Allowlist, Defender Pass-through IP Allowlist) are now global-admin-only, their sidebar links are hidden for non-administrators, and for administrators they carry a small lock icon as a reminder that they are admin-scoped.',
      'Source-event and raw-event evidence can no longer be retrieved without proving access to the owning alert/event and tenant.',
      'PSA (AutoTask/ConnectWise) and webhook URLs are now validated to be public https endpoints before the portal will call them, and investigation tenant IDs are validated as GUIDs. No change to normal use.',
    ],
  },
  {
    version: '1.110.16',
    date: '2026-07-03',
    title: 'Security hardening: Device Inventory scoping',
    changes: [
      'The Coverage → Device Inventory pages now strictly scope results to the tenants and MSPs you are authorized to see — including the BlackPoint (BPC) view and the "All tenants" option. Global administrators are unaffected; if you manage a subset of tenants, you will now see only those.',
    ],
  },
  {
    version: '1.110.15',
    date: '2026-07-03',
    title: 'Security hardening: cross-tenant data queries',
    changes: [
      'Hardened how several investigation views query the data lake (IP prevalence and shared-IP user lists, the suppressed-alerts drill-down, and the token-use and case IP-evidence pivots) so lookups are strictly scoped and inputs are validated. No change to how these features work or what you see.',
    ],
  },
  {
    version: '1.110.14',
    date: '2026-07-02',
    title: 'Fixed: “Fetch from PSA” said “Loaded 1 companies”',
    changes: [
      'Fetching companies on the PSA Configuration and Ticket Routing pages briefly reported “Loaded 1 companies” and left the Client Company picker nearly empty — a regression in the fix that lifted the 1,000-company cap. The full company list now loads and counts correctly.',
    ],
  },
  {
    version: '1.110.13',
    date: '2026-07-02',
    title: 'Baseline Comparison: click the IP count to see the user’s full known-IP list',
    changes: [
      'On an alert’s Baseline Comparison panel, click the “N known IPs” count on the “IP in baseline” row to open a side panel listing all of the user’s baseline sign-in IPs, split into clearly labeled 7-day and 30-day sections. It’s scrollable and readable (the previous hover tooltip was a hard-to-use blob), and each IP is highlighted against the alert’s IP — green for an exact match, amber for an IP on the same network (same /24 or /64) — so you can see at a glance whether the alert IP belongs to a network the user normally uses.',
    ],
  },
  {
    version: '1.110.12',
    date: '2026-07-02',
    title: 'Baseline Comparison: spot when an alert IP is on a network the user normally uses',
    changes: [
      'On an alert’s Baseline Comparison panel, the “IP in baseline” row now flags when the sign-in IP isn’t an exact baseline match but is on the same network (same /24 for IPv4, /64 for IPv6) as an IP the user regularly signs in from — it turns amber and names the specific baseline IP, e.g. “18 known IPs · 102.129.232.113 same subnet”. That’s a strong hint the IP belongs to a known network (e.g. a corporate or VPN range) rather than being brand new. Exact matches stay green; genuinely new IPs stay red. Hover the “N known IPs” count to see the full list without cluttering the table.',
    ],
  },
  {
    version: '1.110.11',
    date: '2026-07-02',
    title: 'ConnectWise Manage Permission Guide on the PSA Configuration page',
    changes: [
      'The PSA Configuration page now has a “ConnectWise Manage Permission Guide” link that opens a reference panel showing exactly which ConnectWise security-role permissions the M365Signal API member needs (role “API-M365Signal”) — a quick summary of the actual grants plus the full section-by-section role table, so you can set up or audit the API member without hunting through ConnectWise documentation.',
    ],
  },
  {
    version: '1.110.10',
    date: '2026-07-02',
    title: '“Ticketing” is now “PSA Configuration”, and company lists load past 1,000',
    changes: [
      'The admin sidebar item “Ticketing” is renamed “PSA Configuration” to better describe what the page holds (PSA credentials, MSP defaults, and test tickets). In-app hints that pointed to “the Ticketing page” now use the new name.',
      '“Fetch from PSA” now retrieves the complete ConnectWise company list instead of silently stopping at the first 1,000 — so the Ticket Routing page’s Client Company picker offers every company in your PSA.',
    ],
  },
  {
    version: '1.110.9',
    date: '2026-07-02',
    title: 'Ticketing pages: “Fetch from PSA” results now appear next to the button',
    changes: [
      'On the MSP Ticketing Integration and Ticket Routing pages, error messages from “Fetch from PSA” (e.g. an authentication failure) now appear directly beside the button you clicked — the same spot success messages already use. Previously the error rendered at the bottom of the panel, next to the test-ticket buttons, making it look like a different action had failed.',
    ],
  },
  {
    version: '1.110.8',
    date: '2026-07-02',
    title: 'Baseline Comparison: OS reads as a match when it’s one of the user’s normal OSes',
    changes: [
      'On an alert’s Baseline Comparison panel, the OS row now shows green (a match) whenever the sign-in’s OS is one the user regularly uses — even if it isn’t their single most-common OS in that window. Previously it could show red for, say, a MacOS sign-in from a user who’s mostly on Windows but uses a Mac about a third of the time. The OS is now compared against the user’s full OS mix (e.g. “Windows 67%, MacOS 33%”), matching what the baseline columns already display.',
      'The OS value now displays in a consistent form (e.g. “MacOS”, “Windows”) so the event and baseline columns read the same.',
    ],
  },
  {
    version: '1.110.7',
    date: '2026-07-02',
    title: 'Baseline Comparison: OS no longer flagged red when it’s a secondary-but-normal OS',
    changes: [
      'Fixed the OS row on an alert’s Baseline Comparison panel showing a red deviation for an OS the user regularly uses but that isn’t their single most-common one (e.g. a MacOS sign-in from a mostly-Windows user). The event OS is now checked against the user’s full OS mix for each window. Described together with the display polish in 1.110.8.',
    ],
  },
  {
    version: '1.110.6',
    date: '2026-07-02',
    title: 'Baseline Comparison now shows device details for classic sign-in alerts',
    changes: [
      'On an alert’s Baseline Comparison panel, the OS, Hour of day, and Intune Compliant rows now populate for classic sign-in alerts (log type “signin”) — previously they showed “—” even though the information was in the event. This restores the at-a-glance “does this match what’s normal for the user?” signal for those alerts.',
      'The Intune Managed row fills in when the device’s managed state can be determined from the sign-in, and stays “—” only when it genuinely can’t — so it never shows a misleading “No”. Alerts from Microsoft Graph sign-ins are unchanged.',
    ],
  },
  {
    version: '1.110.5',
    date: '2026-07-01',
    title: 'The portal now picks up new versions automatically',
    changes: [
      'The app now loads the latest deployed version on your next navigation, instead of occasionally running an older copy cached by your browser until a manual hard-refresh. Static assets are still cached for speed, so this doesn’t slow anything down.',
    ],
  },
  {
    version: '1.110.4',
    date: '2026-07-01',
    title: 'See who else used an IP across the tenants you manage',
    changes: [
      'On an alert’s IP Intelligence panel, “List users” now also shows the users who signed in from that IP in other tenants you’re permitted to see — with each user labeled by client and MSP. Previously every cross-tenant user was hidden behind a “UPNs withheld” count, even for tenants you already have access to. Users in tenants outside your access are still withheld as a count, so nothing crosses an MSP boundary.',
      'Fixed a crash that could blank the Detection Alerts page after loading a fresh build.',
    ],
  },
  {
    version: '1.110.2',
    date: '2026-07-01',
    title: 'Scope detection rules to an MSP, and suppress Defender alerts by their description',
    changes: [
      'Detection rules can now be scoped to an entire MSP — pick one or more MSPs in the rule editor’s “Rule Scope” section and the rule automatically covers every current and future tenant of those MSPs, so you no longer have to add each new tenant by hand.',
      'You can now suppress noisy Microsoft Defender pass-through alerts by their description (not just their title) — useful when an approved tool keeps triggering a false positive whose identifying text is in the alert description (e.g. an approved remote-support client). Add descriptions in the rule editor, or use the new one-click “Suppress this alert description” button on the alert itself, with an optional detector-ID field to pin the suppression to one alert type.',
    ],
  },
  {
    version: '1.109.9',
    date: '2026-07-01',
    title: 'Re-check sign-in outcome now sticks when you re-open the alert',
    changes: [
      'On a Microsoft-security pass-through alert (Defender / Entra ID Protection), clicking “Re-check sign-in outcome” now keeps the resolved result — e.g. “PASSWORD COMPROMISED” or “Wrong password (spray noise)” — when you collapse the alert and expand it again. Previously the banner reverted to “Sign-in outcome pending” until a full page refresh, even though the result had already been saved.',
    ],
  },
  {
    version: '1.109.8',
    date: '2026-07-01',
    title: '“Identity Inventory” is now a top-level sidebar item',
    changes: [
      'The Identity Inventory page is now its own item in the left sidebar, at the same level as Coverage Analysis and Release Notes. Previously it was tucked under Coverage Analysis and only appeared after you clicked into that section, so it was easy to miss — it’s now always visible.',
    ],
  },
  {
    version: '1.109.7',
    date: '2026-07-01',
    title: 'Fixed a crash when viewing alerts with the timezone set to “Local”',
    changes: [
      'Expanding certain alerts (mailbox-access and other aggregation alerts) while the timezone picker was set to “Local” could crash the page. That’s fixed, and the “Local” setting now correctly shows times in your browser’s own timezone across every panel of an alert — the alert timeline, session trace, token-use, and sign-in outcome panels are now all consistent.',
    ],
  },
  {
    version: '1.109.6',
    date: '2026-07-01',
    title: 'Inbox rule details now show on “Inbox Forwarding Rule Creation” alerts',
    changes: [
      'Alerts that fire on an inbox-rule change made from Outlook or Outlook on the web (the UpdateInboxRules operation) now show the rule’s details in the expanded row — including the forward-to address, the rule condition, and whether the rule was added, modified, or removed. Previously the “Rule / Operation Parameters” section was blank for these alerts and you had to dig through the raw event JSON to find the forwarding target.',
    ],
  },
  {
    version: '1.109.5',
    date: '2026-06-30',
    title: 'Tenant ID shown (and copyable) in the case header',
    changes: [
      'A case’s header now shows the tenant ID alongside the tenant name, with a one-click copy button (hover the tenant ID to reveal it) — handy when you need the GUID for cross-referencing or support.',
    ],
  },
  {
    version: '1.109.3',
    date: '2026-06-30',
    title: 'UI: wider Client IP column on the case timeline',
    changes: [
      'On a case’s All Events timeline, the Client IP column is now wider by default so full IP addresses (especially IPv6) are readable without resizing. You can still drag it to any width.',
    ],
  },
  {
    version: '1.109.1',
    date: '2026-06-30',
    title: 'New “Defender Pass-through IP Allowlist” to silence Microsoft false-positive alerts',
    changes: [
      'Added a Defender Pass-through IP Allowlist (under the alerting settings) for the Microsoft-security pass-through alerts (Defender / Entra ID Protection). When Microsoft mis-flags a trusted IP as malicious — for example a SaaS app’s shared egress that keeps re-firing the same alert — an admin can add that IP here to stop the false positives across every tenant at once. A genuinely-malicious IP still alerts.',
      'Each entry records the IP, a reason, and the alerting Microsoft service. Entries are validated as you type (showing exactly how the IP will be stored) and are soft-disabled rather than deleted, so the history is preserved. The list is read-only for non-admins.',
      'Detection rules gained a matching toggle, “Suppress Microsoft-security alerts from allowlisted (benign) IPs”, in the rule editor’s suppression section.',
    ],
  },
  {
    version: '1.108.1',
    date: '2026-06-30',
    title: 'Alert Rules search now matches the rule ID',
    changes: [
      'On the Alert Rules page, the search box now also matches a rule’s ID (partial match) and its legacy ID, in addition to name and description — so pasting a rule_id (e.g. from an alert or a handoff) surfaces that rule directly.',
    ],
  },
  {
    version: '1.108.0',
    date: '2026-06-30',
    title: 'New suppression option: provider already trusted in the user’s own history',
    changes: [
      'Detection rules gained a new noise-suppression option, “Skip if Provider in User Baseline”. When enabled on a rule, an alert is stood down if the sign-in IP’s network provider/ASN is already established in that specific user’s own sign-in history — even if the exact IP is brand new. This handles rotating commercial VPN/egress providers, where the IP changes constantly but the provider stays the same. It is a per-user decision: the same provider that is trusted for an established user still alerts for a user who has never used it.',
      'The new option appears as a toggle in the rule editor’s suppression section, alongside an optional “minimum distinct baseline IPs per provider” setting (default 2). Suppressions from this tier now show on the Investigations Avoided page as “Provider is established in user’s own baseline”.',
    ],
  },
  {
    version: '1.107.4',
    date: '2026-06-30',
    title: 'Alert/Event times now honor your timezone in every browser',
    changes: [
      'Fixed: on the Alerts page (and Test Run Results / Recent Events), some Alert Time and Event Time values did not reflect the timezone you selected — they showed a raw UTC value instead. This happened in Firefox and Safari for events whose timestamps carried microsecond precision; those timestamps couldn’t be parsed and silently fell back to the unconverted value. Times now convert correctly to your chosen timezone in all browsers. (Chrome and Edge were already correct.)',
    ],
  },
  {
    version: '1.107.3',
    date: '2026-06-30',
    title: 'Find alerts and tenants faster',
    changes: [
      'The Alerts page search now also matches the tenant name — type a customer’s name to filter the list to their alerts (alongside the existing user, IP, rule, country, operation, and provider matching).',
      'The Managed Tenants page now shows each tenant’s short name in its own column on the left, so you can scan and sort by short name at a glance.',
      'On the Investigations Avoided page, the Tenant dropdown is now sorted alphabetically, making a specific customer easier to find.',
    ],
  },
  {
    version: '1.107.2',
    date: '2026-06-29',
    title: 'Compromised-credential verdict now distinguishes token replay',
    changes: [
      'The compromised-credential verdict no longer mislabels a replayed/stolen token as a known password. When a sign-in is blocked by Conditional Access but the evidence shows a token was presented (not a fresh password entry), the alert now shows an amber “Blocked by CA — password not confirmed (possible token replay); review” note instead of the red “PASSWORD COMPROMISED” banner. A genuine password block still shows the red banner, with wording reflecting whether the password step was explicitly logged or inferred from a browser sign-in reaching the MFA stage.',
    ],
  },
  {
    version: '1.107.1',
    date: '2026-06-29',
    title: 'Simpler blocked-by list + enrich an alert IP from IP Intelligence',
    changes: [
      'On the compromised-credential alert, the Conditional Access policies that blocked the sign-in are now shown as a simple labelled list under “Blocked by CA Policies:”. The colour-coded risk tiers and the colour legend were removed — the policy names are what matters.',
      'The IP Intelligence panel now always offers the enrich button (the refresh icon next to the IP). Click it to fetch threat intelligence — risk score, country, provider, detections — for the alert’s IP; the result is saved onto the alert, so it stays put after a page refresh. Previously this button only appeared on alerts without a baseline comparison.',
    ],
  },
  {
    version: '1.107.0',
    date: '2026-06-29',
    title: 'See whether the attacker had the valid password',
    changes: [
      'Defender / Entra ID Protection pass-through alerts now show a clear verdict on whether the sign-in behind the alert actually used the correct password. When the credential is confirmed compromised — the password was valid (or the sign-in outright succeeded) and only Conditional Access / MFA stopped it — a bold red “PASSWORD COMPROMISED” banner appears at the top of the expanded alert, and the alert is flagged with a red “PWD COMPROMISED” marker right in the list so you can triage it first without opening it.',
      'When a valid password was blocked, the alert lists exactly which Conditional Access policies stopped the attacker, colour-coded by how much risk remains: green for MFA/strong-auth (the attacker still needs a second factor) and red for location/IP rules (the attacker can simply switch IP or region and the same valid password may then work — reset the password and revoke sessions).',
      'If the sign-in hadn’t finished processing when the alert fired, a “Re-check sign-in outcome” button fills the verdict in on demand (available ~30 minutes after the sign-in, once the data has caught up). Spray noise — wrong password, lockout, generic failures — is shown muted so it doesn’t distract from the real compromises.',
    ],
  },
  {
    version: '1.106.1',
    date: '2026-06-29',
    title: 'Clearer backfill status + force re-collect',
    changes: [
      'A backfill that finds nothing new to do no longer looks stuck. When the days you asked for were already collected, the progress view now says so plainly — “All N requested days were already collected — nothing new was queued” — and per-log-type rows read “no new data” instead of a misleading “queued”.',
      'New “Force re-collect already-collected days” option when starting a backfill — turn it on to re-pull days that already have data (for example, to repopulate days that were collected with bad or stale data). It’s off by default and warns you before running, since it adds load on the client tenant.',
      'The backfill panel now explains plainly that work is queued and paced server-side (safe to start anytime, and safe to close the drawer). The old “run after 5 PM Eastern” warning and the unused “schedule for later” option were removed — the collection backend now paces the load itself, so neither applies anymore.',
      'When a backfill finishes, the panel now notes that log collection is done but the data isn’t searchable in investigations until full pipeline processing completes (typically 15–30 minutes later) — so it may not show up the instant the backfill completes.',
    ],
  },
  {
    version: '1.105.2',
    date: '2026-06-29',
    title: 'IP comment column moved next to the IP address',
    changes: [
      'On the IP Addresses tab, the new Comment column now sits directly beside the IP address (instead of at the far right) so the label reads right next to the IP it describes.',
    ],
  },
  {
    version: '1.105.1',
    date: '2026-06-29',
    title: 'Copy the Microsoft Defender link from an alert',
    changes: [
      'Defender pass-through alerts now show a copy icon next to the “Open in Microsoft Defender” link in the expanded row — click it to copy the alert URL to your clipboard and paste it into whichever browser or profile you’re signed into Defender with.',
    ],
  },
  {
    version: '1.105.0',
    date: '2026-06-29',
    title: 'Label an IP address with a comment for the whole case',
    changes: [
      'You can now add a freeform comment to any IP address in a case — like “primary home IP”, “Atlanta Office IP”, or “Known SaaS IP”. Add or edit it in the new Comment column on the IP Addresses tab; it saves automatically and stays with the case for anyone who opens it.',
      'The comment appears as a subtle note next to that IP throughout the case: in the event Timeline, on the Devices tab (its own column in the per-device event list), and in both the Client and Technical reports.',
    ],
  },
  {
    version: '1.104.3',
    date: '2026-06-27',
    title: 'Reveal an email subject directly from a Message Trace row in All Events',
    changes: [
      'When you expand a Message Trace event in the All Events timeline, it now shows the sender, recipient, subject, status and a “Reveal subjects” button — matching the dedicated Message Trace tab. Previously you could only un-mask message-trace subjects by toggling reveal on an Exchange event.',
    ],
  },
  {
    version: '1.104.2',
    date: '2026-06-27',
    title: 'Investigations now show every recipient of an outbound email blast',
    changes: [
      'Fixed a bug where the Message Trace / sent-mail view in an investigation collapsed a mass send down to a single recipient per message — badly under-reporting the scope of a phishing blast and hiding which recipients actually received it. The view now lists every recipient with its own delivered/failed status. Re-run an existing investigation to pick up the full recipient list.',
    ],
  },
  {
    version: '1.104.1',
    date: '2026-06-27',
    title: 'Behavioral baselines rebuild automatically again after a sign-in backfill',
    changes: [
      'When you backfill a tenant’s sign-in history, its 7-day and 30-day behavioral baselines now rebuild automatically once the data lands — restoring the behavior from before the recent backfill engine change. No action needed; it happens in the background after the backfill completes.',
    ],
  },
  {
    version: '1.104.0',
    date: '2026-06-27',
    title: 'Tenant backfill is now fast and reliable — even on high-volume tenants',
    changes: [
      'Historical backfill now runs as a server-side queued job: starting one returns immediately and the work streams in the background, with live per-log-type progress shown by day. Closing the drawer (or the tab) is safe — reopen it to re-attach.',
      'The timeouts and out-of-memory failures that used to block backfilling high-volume tenants — especially busy Exchange-audit days — are gone. Those days now complete reliably.',
      'Re-running the same range is safe: days already collected are skipped automatically, so a re-run just fills the gaps.',
      'Only one backfill runs per tenant at a time; starting a second while one is in progress is blocked with a clear message.',
    ],
  },
  {
    version: '1.103.6',
    date: '2026-06-26',
    title: 'Only one tenant backfill runs at a time',
    changes: [
      'Starting a tenant backfill while another is already running is now blocked with a clear message ("a backfill is already running for <tenant>… please wait"). The log-collection backend is shared, so two backfills at once overload it and cause failures — this guard prevents independent admins from accidentally launching overlapping jobs.',
    ],
  },
  {
    version: '1.103.5',
    date: '2026-06-26',
    title: 'Historical backfills now survive slow, high-volume tenants',
    changes: [
      'Backfilling a newly onboarded (or catching-up) tenant no longer stalls on a "504 Gateway Time-out" for high-volume log types like Exchange audit logs and message trace. The backfill now automatically breaks a slow window into smaller pieces and retries transient hiccups, and keeps going — without you having to pick a shorter date range.',
      'If a backfill runs long or is interrupted, it now stops cleanly and can simply be re-run: days already collected are skipped and it resumes where it left off.',
    ],
  },
  {
    version: '1.103.4',
    date: '2026-06-26',
    title: 'Fix: Activity Log page was blank when the time zone was set to "Local"',
    changes: [
      'The Administration → Activity Log page failed to load (showed a blank page) for anyone whose time-zone picker was set to "Local". The page now renders correctly with any time-zone selection, including "Local".',
    ],
  },
  {
    version: '1.103.3',
    date: '2026-06-26',
    title: 'Fix: onboarding a tenant now keeps your selected MSP',
    changes: [
      'When onboarding a new tenant, the MSP field no longer silently reverts to the default ("PFIT") if the page was still loading your account details when the form opened. Previously an MSP admin could select their MSP, see it selected in the dropdown, and still get a "You can only onboard tenants into MSPs you administer" error on submit because the selection had not actually registered. The MSP now correctly reflects your selection (and defaults to the MSP you administer).',
    ],
  },
  {
    version: '1.103.2',
    date: '2026-06-26',
    title: 'Reports now list every email subject, and MFA changes show who did them',
    changes: [
      'Client and Technical reports now include a "Mailbox Items Detail" table that lists each individual email touched during the incident — read, synced, deleted, or sent — with its subject, folder or recipient, size, and the IP it was accessed from. Previously a bulk record showed only a count (e.g. "read 11 mail items") and the actual subjects were missing from the report and its PDF/HTML export.',
      'Email subjects can contain sensitive information, so they stay masked in the report unless you turn on "Reveal subjects" for the case first (that reveal is audit-logged, as it is everywhere else).',
      'MFA and passkey security-info changes now appear on the case timeline — attacker-era registrations and responder removals, including FIDO passkey deletions and Microsoft Authenticator removals — with the device name (e.g. "iPhone 14") and a clear label like "Passkey removed" or "Security info removed".',
      'These entries now name WHO performed the change (e.g. "Passkey removed by zEmergencyAdmin@…"). Microsoft sometimes records the admin who did it by internal ID only; the portal now resolves that to the actual account so admin/attacker actions on a victim\'s security info are properly attributed instead of showing "Unknown user". Re-run an investigation to pick these up on existing cases.',
    ],
  },
  {
    version: '1.102.0',
    date: '2026-06-26',
    title: 'New: review and revoke spray-origin IP prefixes',
    changes: [
      'A new Spray-Origin IP Watchlist page (Settings → Spray-Origin IP Watchlist) shows the attacker IP prefixes the detection engine automatically captures from password-spray alerts. When a successful sign-in later comes from one of these prefixes, the engine raises a HIGH "the spray succeeded" alert across all tenants.',
      'Because the list is shared across every tenant, a benign shared address (a corporate VPN exit, a mobile-carrier range, or an over-broad IPv6 block) can occasionally get added by mistake and start flagging legitimate sign-ins. Admins can now revoke such a prefix in one click — with a required reason for the audit trail — and the engine stops alerting on it within about 30 seconds. Revoked entries are kept (not deleted) and can be re-enabled.',
      'Each prefix expands to show full provenance — which tenants it sprayed (by friendly name), sample IPs and target users, the source feed, and when it will auto-expire — plus a cross-link to any real alerts that already fired on it, so you can judge a false positive with evidence rather than guesswork.',
    ],
  },
  {
    version: '1.101.3',
    date: '2026-06-26',
    title: 'Fix: refreshing an IP’s details in a case now always shows the data',
    changes: [
      'When you refresh an IP’s threat-intel details on a case’s IP Addresses tab, the row now fills in correctly even if that IP was already looked up in the last 24 hours — previously it could stay blank unless you chose to re-run the lookup. The portal now reuses the data it already has, so the details appear instantly without spending a lookup.',
      'If the IP-intelligence provider’s daily lookup limit has been reached, the portal now says so clearly ("provider quota is exhausted — try again later") instead of showing a cryptic error.',
    ],
  },
  {
    version: '1.101.2',
    date: '2026-06-26',
    title: 'See a user’s usual operating systems at a glance on alerts',
    changes: [
      'On a detection alert’s Baseline Comparison panel, the OS row’s 7-day and 30-day baseline columns now show the full mix of operating systems the user signs in from — for example “Windows (95%), MacOS (4%), iOS (1%)” — instead of just one. This makes it easy to tell whether an alert’s device type is genuinely unusual for that user or just their occasional second device, without digging into the raw event. The same context appears on the Test Run Results page.',
    ],
  },
  {
    version: '1.101.0',
    date: '2026-06-26',
    title: 'Suppress noisy Defender alert titles in one click',
    changes: [
      'Microsoft Defender / Entra ID Protection “pass-through” alerts can now be quieted by their title: on a Defender alert, a new “Suppress this alert title” action stops that exact title (or, optionally, any title containing your text) from raising future alerts on that rule. The confirmation shows exactly which tenants the rule covers so the impact is clear before you apply it.',
      'You can review and remove suppressed titles anytime from the rule’s editor (Alert Rules → Edit), and a badge on the Alert Rules list shows how many titles a rule excludes. Suppressed alerts are counted in the Investigations Avoided dashboard.',
      'Detection-rule changes (including title suppressions) now apply automatically: the detection engine restarts on its own about 10 minutes after your last edit, so you no longer have to wait for or request a manual restart. Several edits in a row are batched into a single restart.',
    ],
  },
  {
    version: '1.100.6',
    date: '2026-06-26',
    title: 'Defender pass-through alerts now show IP Intelligence',
    changes: [
      'For Microsoft Defender / Entra ID Protection “pass-through” alerts (e.g. password-spray detections), the IP address and its country were buried in the alert evidence, so the IP Intelligence panel showed blank values and a misleading green “0” risk score. The panel now surfaces the alert’s IP (with a one-click external lookup), its country, and the cross-tenant prevalence of that IP — and admins can enrich it on demand to pull risk score and provider.',
    ],
  },
  {
    version: '1.100.5',
    date: '2026-06-26',
    title: 'Fix: “look up this IP” links now work for IPv6 addresses',
    changes: [
      'Clicking an IP address to look it up on iplocation.net (in the Alerts, Recent Events, and Test Run Results detail panels) now opens the correct lookup page. The link format had drifted out of date, which broke lookups for IPv6 addresses in particular.',
    ],
  },
  {
    version: '1.100.4',
    date: '2026-06-26',
    title: 'Fix: “Source Events” panel on mailbox-access alerts showed a blob-path error',
    changes: [
      'Expanding an aggregation alert (e.g. “Anomalous access to other users’ mailboxes”) could show “Blob path does not match the alert row.” in the Source Events section instead of the event detail. The underlying source-events evidence was being blocked by a path-comparison mismatch; the evidence now loads correctly.',
    ],
  },
  {
    version: '1.100.3',
    date: '2026-06-25',
    title: 'Case reports: show subjects of deleted emails in the timeline',
    changes: [
      'In the Investigation Timeline (both the case reports and the in-app case view), “soft deleted” and “hard deleted” mailbox events now show the subject lines of the affected emails — previously they only noted that the deletion happened without naming the items.',
      'When several emails were deleted in a single action, the timeline lists the first few subjects and notes how many more were affected.',
    ],
  },
  {
    version: '1.100.2',
    date: '2026-06-25',
    title: 'Pipeline Health dashboard: cleaner per-workload breakdown',
    changes: [
      'On the admin Pipeline Health page, the non-interactive sign-in workload was being shown as two separate half-rows; it now appears as a single row with its full end-to-end timing.',
      'Workloads that only exist at one internal pipeline stage (e.g. exchange_bulk) now show “n/a” in the stages that don’t apply to them, instead of a blank dash — making it clear those stages aren’t missing data.',
    ],
  },
  {
    version: '1.100.1',
    date: '2026-06-25',
    title: 'Fix: renaming a tenant could fail for partially-onboarded tenants',
    changes: [
      'Admins can edit a tenant’s Friendly Name (and domain/short name/MSP) from Tenant management. Saving could fail for a tenant whose onboarding wasn’t fully completed (present in the basics list but with no services record yet); those edits now save correctly.',
    ],
  },
  {
    version: '1.100.0',
    date: '2026-06-25',
    title: 'Cases: add an editable Comment (shown in the Cases list)',
    changes: [
      'Every investigation case now has a Comment field for your closing remarks — fill it in when you wrap up the case. Edit it on the case page (it saves when you click away), or edit it inline straight from the Cases list.',
      'The Cases list shows a Comment column, so you can scan each case’s conclusion at a glance without opening it.',
    ],
  },
  {
    version: '1.99.4',
    date: '2026-06-25',
    title: 'Case reports: new “Export to HTML” + fixed “Export to PDF”',
    changes: [
      'The Client and Technical case reports now have an “Export to HTML” button that downloads the full report as a single, self-contained .html file (named after the case) — easy to attach to a ticket or archive, and it opens in any browser with all formatting intact.',
      'Fixed the “Export to PDF” button, which did nothing — the report window’s buttons were being blocked by the portal’s security policy. Both export buttons now work reliably (PDF opens the print dialog; HTML downloads the file).',
    ],
  },
  {
    version: '1.99.2',
    date: '2026-06-25',
    title: 'Fix: “Local” timezone now actually renders in local time',
    changes: [
      'Selecting the “Local” timezone on the Detection Alerts and Test Run Results pages was silently falling back to raw UTC (the Alert Time / Event Time columns showed the unformatted UTC timestamp). “Local” now correctly renders in your browser’s local timezone, matching the other zone options. Same fix applied to the Source Event timestamps shared across the Alerts, Test Run Results, and Recent Events pages.',
    ],
  },
  {
    version: '1.99.1',
    date: '2026-06-24',
    title: 'New “Baseline” tab on cases — see the user’s normal 7-day & 30-day profile',
    changes: [
      'Case investigations now have a “Baseline” tab (between Devices and Summary) showing the investigated user’s normal sign-in behavior side-by-side for their 7-day and 30-day windows: typical countries, known IPs, states, cities, and apps, plus a per-OS breakdown of typical sign-in hours (UTC), sign-in volume, and whether the device is usually compliant/managed. This gives you an at-a-glance read on how far the investigated activity strays from “normal” for this user.',
      'Each OS in the per-OS breakdown now shows a “First seen” date — the earliest baseline snapshot the user signed in from that OS — with a “new in window” flag when an OS first appears during the period you’re looking at. A brand-new OS (e.g. a macOS that shows up right around the incident) is a useful attacker-device tell.',
      'It defaults to the baseline snapshot for the case’s First Known Indicator of Compromise date (the pre-compromise picture), and a dropdown lets you step back to any other available daily snapshot to see how the user’s baseline looked on a different day. The tab also loads noticeably faster.',
    ],
  },
  {
    version: '1.98.5',
    date: '2026-06-24',
    title: 'See what security info a user actually registered (Passkey, Authenticator, etc.)',
    changes: [
      'In a case timeline, “User registered security info” directory-audit events now show exactly what was registered — e.g. “Security info registered on ‘Stephanie Zamudio’ — User registered Passkey” instead of a generic line, with the specific detail also shown in the expanded row. This makes a string of MFA-registration events (Authenticator, Passkey, phone, etc.) easy to tell apart, where before several of them looked like the same event.',
    ],
  },
  {
    version: '1.98.4',
    date: '2026-06-23',
    title: 'Prove an IP is the real user — “real-user evidence” on case IPs',
    changes: [
      'The case IP Addresses tab has a new “Check real-user evidence” button (admins) that grades every IP on whether the user’s own device and token history proves it belongs to the real user — not a potential attacker. IPs get a tier: “Real user · managed device” (green) when a compliant or Entra-joined/hybrid device is involved, or “Real user · registered device” (blue) when it’s the user’s registered device with established refresh-token history. Expand the IP to see the device name, network (ASN), token-refresh count, app count, days seen, the device’s registration date, and a one-click “Mark Safe”.',
      'Filter the table by tier (managed / registered / any real-user / caution) and use the bulk “Mark Safe” button to clear all the shown real-user IPs at once — turning IP triage from a judgment call into an evidence-backed, few-click decision.',
      'Replay-aware: if a known device shows up from a country it doesn’t normally sign in from (a possible stolen-token replay) or was registered right around the incident, the IP is flagged “⚠ verify” in amber and is excluded from the bulk Mark-Safe — you can still clear it manually after reviewing. Never auto-classifies; you always confirm. (It confirms benign IPs and can’t flag an attacker on its own; sign-in history is still building up, so coverage improves over the coming weeks.)',
      'Both the real-user evidence grades and the on-demand IP enrichment refreshes now persist on the case, so they survive a page reload — you don’t have to re-run them each time you open the case.',
    ],
  },
  {
    version: '1.97.2',
    date: '2026-06-23',
    title: 'See where a user’s token was used — non-interactive sign-in pivot on alerts',
    changes: [
      'Alerts now have a “Token Use” panel (expand an alert to find it) that pulls the user’s non-interactive sign-ins — refresh-token and SSO token reuse — from 2 hours before to 24 hours after the alert, a view the normal interactive sign-in log doesn’t give you. A successful token use from an IP, network (ASN), or country the user doesn’t normally sign in from is flagged in amber: it can mean a stolen refresh token is being replayed against Microsoft 365. (Proxy-style AiTM phishing pages bypass these logs and are caught by separate sign-in detections.)',
      'The panel also stitches in the originating interactive sign-in (matched by correlation ID) and enriches every IP with provider, network type, country, risk, and VPN/proxy/Tor flags — so you can see “logged in here, then the token was used there” at a glance. The token timeline is paginated (10 per page by default).',
    ],
  },
  {
    version: '1.96.0',
    date: '2026-06-23',
    title: 'Enrich all unenriched IPs on a case in one click',
    changes: [
      'The case IP Addresses tab now has an “Enrich unenriched” button (for admins), to the left of the search box, that fetches location/ISP/risk data for every IP in the table that has none yet — in batches, in a single click — and fills the rows in place. It shows how many were fetched fresh from ProxyCheck.io versus already had data on file.',
      'The per-IP refresh button (and the new batch button) no longer get blocked by a “refreshed in the last 24 hours” prompt on IPs that were only ever seen but never actually enriched — those now fetch immediately instead of asking you to override a freshness check that didn’t apply.',
    ],
  },
  {
    version: '1.95.0',
    date: '2026-06-23',
    title: 'Refresh IP enrichment from a case’s IP Addresses table',
    changes: [
      'The IP Addresses tab on a case now has a refresh button next to each IP (for admins). If an IP shows no location, ISP, or risk data — or the data looks stale — click it to re-fetch live enrichment from ProxyCheck.io. The row updates in place with the new country, ISP, risk level, and VPN/proxy/Tor/compromised flags, the same way the refresh control on the alert pages works. If the IP was already refreshed in the last 24 hours, you’ll be asked to confirm before re-running.',
    ],
  },
  {
    version: '1.94.2',
    date: '2026-06-23',
    title: 'Noise filter now hides unresolved attachment-access events too',
    changes: [
      'The case timeline\'s "Unresolved mail reads" filter — which hides high-volume mailbox-read events whose source IP never resolved past the Microsoft service proxy — now also hides attachment-access events of the same kind. One toggle clears both, and it\'s relabeled "Unresolved mail/attachment reads" with the combined count.',
    ],
  },
  {
    version: '1.94.1',
    date: '2026-06-22',
    title: 'Recovered sign-ins are now flagged in the timeline',
    changes: [
      'Sign-in events that Microsoft logged without a username — recovered in v1.94.0 by matching the user\'s directory ID — now show an amber "recovered · no UPN" marker in the case timeline, with a short explanation when you expand the event. These are frequently the key record in an adversary-in-the-middle (AiTM) compromise (a successful login from an attacker\'s proxy against a spoofed sign-in page), so the marker tells you why the event lacked a username and prompts you to check the application and source IP. The marker appears after you re-run an investigation.',
    ],
  },
  {
    version: '1.94.0',
    date: '2026-06-22',
    title: 'Investigations now capture sign-ins Microsoft logs without a username',
    changes: [
      'Some sign-in records — most importantly the successful token issuance in an adversary-in-the-middle (AiTM) attack — are written by Microsoft without the user\'s name attached, so they previously fell outside an investigation scoped to that user. The investigation now recovers these records by matching the user\'s directory ID, so the event that proves a compromise (a successful login from an attacker\'s proxy against a spoofed sign-in page) shows up in the case instead of being silently missed. Re-run an investigation to pick up any such events.',
    ],
  },
  {
    version: '1.93.3',
    date: '2026-06-22',
    title: 'Reveal email subjects from the expanded row, not the toolbar',
    changes: [
      'Moved the "Reveal subjects" control out of the case filter toolbar and into the places subjects actually appear — the expanded mail-items detail, the Message Trace expanded row, and the Related Recipients view. Clicking it still reveals every subject in the case at once and records a single activity-log entry, but the button now sits right next to the masked subjects where you would expect it.',
    ],
  },
  {
    version: '1.93.2',
    date: '2026-06-22',
    title: 'Clearer "Reveal email subjects" button on cases',
    changes: [
      'The per-case email-subject control now reads "Reveal email subjects" with an eye icon and an amber highlight, so it\'s easy to find on the case toolbar when subjects are masked (it reads "Hide email subjects" once revealed).',
    ],
  },
  {
    version: '1.93.1',
    date: '2026-06-22',
    title: 'Fix: case page failed to load after the subject-masking change',
    changes: [
      'Fixed a crash that prevented investigation case pages from loading after the v1.93.0 email-subject masking change.',
    ],
  },
  {
    version: '1.93.0',
    date: '2026-06-22',
    title: 'Investigation cases: email subjects hidden until a per-case reveal',
    changes: [
      'Email subject lines (potential PHI/PII) are now masked by default throughout an investigation case — the timeline, the Message Trace and Related Recipients tabs, the Exchange item lists, and the raw event JSON viewer. A single "Email subjects hidden / shown" button on the case toolbar reveals every subject in the case at once, and that reveal is recorded in the Activity Log as one entry (who, which case, how many subjects, when). Generated Client and Technical reports follow the same gate — subjects are masked in the report unless you have revealed them. Searching still works while masked, and the masking resets when you reload the case.',
    ],
  },
  {
    version: '1.92.3',
    date: '2026-06-22',
    title: 'Activity Log: clearer label for email-subject reveals',
    changes: [
      'Renamed the email-subject reveal action so it describes the action without over-claiming — it now logs as "email_subject.reveal" under a "sensitive" category (amber) rather than asserting confirmed PHI, since an email subject is only potentially sensitive.',
    ],
  },
  {
    version: '1.92.2',
    date: '2026-06-22',
    title: 'Activity Log ties subject reveals to the exact messages',
    changes: [
      'When an analyst reveals masked email subjects, the Activity Log entry now also records the specific message identifiers (message ID, folder, size) and a pointer to the evidence — so a reveal can be tied back to exactly which messages were viewed. As before, the subject text itself is never logged.',
    ],
  },
  {
    version: '1.92.1',
    date: '2026-06-22',
    title: 'Fix: Activity Log now shows entries',
    changes: [
      'Fixed an issue where the Activity Log page could come up empty even though actions were being recorded — entries (including PHI subject reveals) now display correctly.',
    ],
  },
  {
    version: '1.92.0',
    date: '2026-06-22',
    title: 'Email subjects (PHI) now hidden until you click to reveal — and the reveal is logged',
    changes: [
      'On alerts that list other people\'s mailbox activity (e.g. "Anomalous access to other users\' mailboxes"), the email Subject lines — which can contain PHI/PII — are now masked by default. Each mailbox card has a "Reveal subjects" button; clicking it unmasks that mailbox\'s subjects (and the subjects in the raw event JSON below). Every reveal is recorded in the Activity Log with who viewed it, which alert and mailbox, how many subjects, and when — the subject text itself is never written to the log. This applies on both the Detection Alerts and Test Run Results pages.',
    ],
  },
  {
    version: '1.91.0',
    date: '2026-06-22',
    title: 'Activity Log: see who changed what in the portal',
    changes: [
      'A new admin-only Activity Log page records who did what across the portal — tenant onboarding and edits, detection-rule changes, allowlist edits, ticketing and MSP configuration, alert status changes, and job triggers (investigations, test runs, backfills). Each entry shows the person, the action, the target, the time, and expandable details. Filter by date range, category, actor, or free-text search, and read every timestamp in your chosen time zone.',
    ],
  },
  {
    version: '1.90.1',
    date: '2026-06-15',
    title: 'UI: "Unresolved mail reads" toggle color',
    changes: [
      'The new "Unresolved mail reads" timeline filter toggle now uses the same purple highlight as the "Post-window" toggle when active, keeping the filter bar consistent.',
    ],
  },
  {
    version: '1.90.0',
    date: '2026-06-15',
    title: 'Hide noisy "unresolved" background mail-read events',
    changes: [
      'A new "Unresolved mail reads" toggle on the case timeline filter bar lets you temporarily hide MailItemsAccessed events whose IP could never be resolved past Microsoft\'s service proxy (shown as Unresolved-ServiceIP). These are high-volume background reads that bury the events you actually want to diagnose. The toggle is off by default and shows a count so you can see what it would hide before turning it on — and it only affects this one specific operation/IP-resolution combination, so it never touches your IP classifications or other activity.',
    ],
  },
  {
    version: '1.89.1',
    date: '2026-06-15',
    title: 'UI: tidier case-timeline filter bar',
    changes: [
      'The case timeline\'s filter toggle buttons (Safe IPs, Safe apps, MS substrate, Post-window, Safe sessions, Extend safe to sessions, Failed, Lookback, Email IPs) are now a uniform height and wrap neatly onto a second row instead of squashing and wrapping their labels at inconsistent points.',
    ],
  },
  {
    version: '1.89.0',
    date: '2026-06-15',
    title: 'Extend "Safe" from IPs to whole sessions',
    changes: [
      'A new "Extend safe to sessions" toggle on the case timeline (next to "Safe sessions") lets you collapse the leftover background events whose own IP is an unresolvable Microsoft service IP but that clearly belong to a session the user owned. It works by taking the sessions seen on the IPs you marked Safe and hiding the other non-sign-in events from those same sessions — so a user roaming across office, home and mobile networks stops cluttering the timeline without you classifying every IP by hand. It needs no re-run and your reports honor it automatically.',
      'It is built to never hide attacker activity: any session that also touched an IP you flagged Malicious is left fully visible (and reappears automatically as you classify more attacker IPs), sign-in events are always kept (they show where a session was used), and any event whose own IP you marked Suspect or Malicious stays visible. The toggle is off by default and shows a count so you can see what it would hide before turning it on.',
    ],
  },
  {
    version: '1.88.1',
    date: '2026-06-15',
    title: 'Background Microsoft "Update" mailbox noise is no longer collected',
    changes: [
      'Microsoft back-end services constantly perform sessionless background "Update" operations on mailboxes (shown as a RESTSystem client on a Microsoft service IP with no resolvable user). These are not user or attacker activity — just timeline clutter. Investigations now drop these at collection time, so they never appear in the All-Events timeline or in generated reports, and the case is a bit lighter to load. Re-run a case to apply it; existing results are unchanged.',
      'Safety: only the low-signal background "Update" operation is dropped, and only when it comes over Microsoft\'s internal RESTSystem channel. Security-relevant mailbox actions — inbox rules, permission grants, forwarding, transport rules, delegation, sends and deletes — are never affected and are always collected.',
    ],
  },
  {
    version: '1.88.0',
    date: '2026-06-15',
    title: 'Hide Microsoft background mailbox reads from case timelines',
    changes: [
      'Microsoft back-end ("substrate") services generate large volumes of automatic background mailbox reads that are neither user nor attacker activity — they run with no sign-in session, on a Microsoft service IP, and just add noise to a case timeline. The "Safe apps" filter can\'t hide them because these Microsoft service identities aren\'t listed apps. A new "MS substrate shown / hidden" toggle on the case page (next to "Safe apps") lets you collapse them, and any event identified as Microsoft substrate now carries an "MS substrate" badge so it\'s always easy to spot. Generated Client and Technical reports honor the toggle too.',
      'The toggle is OFF by default — nothing is hidden until you choose to hide it — and an event is never hidden if it carries any threat signal: an IP you classified as Suspect or Malicious, an IP flagged as VPN/proxy/Tor/compromised/anonymous (checked across the event\'s direct, resolved, and inferred IPs), or a session you flagged as compromised. A re-run of a case is required to populate the new classification; older results simply hide nothing.',
    ],
  },
  {
    version: '1.87.6',
    date: '2026-06-15',
    title: 'Guidance note on "Anomalous mailbox access" alerts',
    changes: [
      'Alerts for "Anomalous access to other users\' mailboxes" now show a short guidance note when expanded: "Evaluate event for the access being authorized or not. Rule was created to detect access being abused (like an admin accessing sensitive data without direct authorization)." It\'s a reminder that this rule also fires on legitimate delegate or admin mailbox access, so the activity should be judged on whether it was authorized rather than assumed malicious.',
    ],
  },
  {
    version: '1.87.5',
    date: '2026-06-13',
    title: 'Large investigations are more reliable (out-of-memory fix)',
    changes: [
      'Fixed very large investigations intermittently failing with an out-of-memory error before results were produced. The investigation engine now releases each batch of raw log data as soon as it has been processed and streams the final results to storage instead of building the entire result in memory at once — cutting peak memory use by roughly a quarter. Cases that previously failed (e.g. a user with thousands of sign-ins plus thousands of mailbox and message-trace events) now complete reliably, with the exact same results.',
    ],
  },
  {
    version: '1.87.4',
    date: '2026-06-12',
    title: 'Investigations no longer blame background mailbox activity on failed attacker logins',
    changes: [
      'Fixed a case-timeline issue where a background mailbox or SharePoint read could be attributed to an attacker IP that only ever appeared on failed sign-in attempts. Background access runs under a valid login token, which a failed sign-in never produces — so the investigation engine now only correlates this activity to sign-ins that actually succeeded. When no successful sign-in matches, the event is shown as "unresolved" instead of pointing at a wrong IP, removing false attacker attributions from the timeline.',
    ],
  },
  {
    version: '1.87.3',
    date: '2026-06-11',
    title: 'Refreshed IP enrichment now also keeps State and City',
    changes: [
      'Completes the previous fix: when you re-fetch IP enrichment from an alert, the retrieved State and City now persist along with country, provider, risk and detections — so the full location no longer reverts to blank after a page reload.',
    ],
  },
  {
    version: '1.87.2',
    date: '2026-06-11',
    title: 'Refreshed IP enrichment now sticks to the alert',
    changes: [
      'When you re-fetch IP enrichment from an alert (the refresh icon next to “Event value” in Baseline Comparison), the freshly-retrieved country, provider, risk and detection data is now saved onto the alert itself — so it’s still there when you reload the page or come back to the alert later, instead of disappearing after you navigate away.',
    ],
  },
  {
    version: '1.87.1',
    date: '2026-06-11',
    title: 'Baseline Comparison: VPN/corporate providers no longer falsely flagged as deviations',
    changes: [
      'Fixed the Provider row in an alert’s Baseline Comparison panel incorrectly showing a provider like “ZSCALER, INC.” in red (a deviation) even when that VPN/provider was clearly part of the user’s normal 7-day and 30-day history. Provider names that contain a comma were being split apart and never matched the baseline — now the full provider name is matched, so a familiar VPN or carrier shows green as expected.',
    ],
  },
  {
    version: '1.87.0',
    date: '2026-06-10',
    title: 'Tenant backfill now builds behavioral baselines automatically',
    changes: [
      'After a tenant backfill finishes collecting sign-in history, the portal now automatically builds that tenant’s 7-day and 30-day behavioral baselines — so baseline-based detection rules and backtesting work for the new tenant right away, instead of waiting for the next overnight run.',
      'The backfill drawer shows a new "Baseline build" step: it waits for the collected sign-in data to finish processing (checking every 15 minutes), then kicks off the baseline build, which finishes in the background (~60 minutes).',
    ],
  },
  {
    version: '1.86.3',
    date: '2026-06-10',
    title: 'Alerts: clearer explanation when an alert has no PSA ticket to log time against',
    changes: [
      'When you close an alert that has no linked PSA (AutoTask/ConnectWise) ticket, the close window now explains why there’s no "Log time" option — either ticket creation for the tenant is currently disabled, or the alert fired before the tenant’s PSA routing was set up (new alerts will open tickets going forward).',
    ],
  },
  {
    version: '1.86.1',
    date: '2026-06-10',
    title: 'Rule editor: fix squished suppression toggles + tighter wording',
    changes: [
      'Fixed the suppression on/off sliders in the rule editor being squished by long descriptions — every toggle now keeps its full size.',
      'Tightened the suppression option descriptions so they read more cleanly, with a quick link to manage each allowlist.',
    ],
  },
  {
    version: '1.86.0',
    date: '2026-06-10',
    title: 'User/App Allowlist: suppress all guests from a partner domain',
    changes: [
      'The User/Application allowlist now accepts a domain entry — "Any guest from a domain" (e.g. westerncomputer.com) — to suppress any external B2B guest signing in from that partner domain.',
      'Domain entries only affect external (B2B) guests; internal tenant members with a matching domain are never suppressed.',
      'Domain entries are tagged with a "Domain" badge in the list, and the option is available both on the allowlist page and from the Suppress action on an alert.',
    ],
  },
  {
    version: '1.85.1',
    date: '2026-06-10',
    title: 'Group/App Allowlist: quick-suppress from an alert, searchable pickers, real app list',
    changes: [
      'You can now allowlist a (group, application) pair straight from a Detection Alert — a new Suppress action opens a modal where you pick the Entra group (the app is pre-filled from the alert).',
      'The group and application pickers are now type-to-search, so finding the right one in a large tenant is fast.',
      'The application picker now lists the tenant’s actual Enterprise Applications (the apps that appear in sign-ins), instead of the manually-flagged investigation app list.',
    ],
  },
  {
    version: '1.85.0',
    date: '2026-06-09',
    title: 'New suppression tier: Group / Application Pair Allowlist',
    changes: [
      'New admin page (Alerting → Group/App Pair Allowlist) to allowlist benign (Entra group, application) pairs for a detection rule. When a rule has this turned on, a match is suppressed if the signing user is a member of an allowlisted group AND the sign-in app is on that group’s allowlist.',
      'Add a pair by picking a live Entra group for the tenant and a known application (or entering an app GUID / choosing "any app"). Group membership is refreshed once daily, and a known-malicious IP still alerts.',
      'New "Suppress allowlisted (group membership, application) pairs" toggle in the rule editor, alongside the existing suppression options.',
    ],
  },
  {
    version: '1.84.4',
    date: '2026-06-09',
    title: 'Identity Inventory: default to 10 rows per page',
    changes: [
      'The Identity Inventory tables now show 10 rows per page by default.',
    ],
  },
  {
    version: '1.84.3',
    date: '2026-06-09',
    title: 'Identity Inventory: fix Role Members sorting and rows-per-page default',
    changes: [
      'Fixed sorting on the Role Members tab (a person holding more than one role could prevent the table from reordering).',
      'Fixed the rows-per-page control, which now defaults to a valid value (50) so paging works consistently on every tab.',
    ],
  },
  {
    version: '1.84.2',
    date: '2026-06-09',
    title: 'Identity Inventory: fix column sorting and column-resize drag',
    changes: [
      'Fixed column sorting on all Identity Inventory tabs — clicking a column header now sorts the table.',
      'Fixed a bug where dragging a column divider could make the divider jump away from the cursor, making columns hard to resize. Each tab now tracks its own column widths.',
    ],
  },
  {
    version: '1.84.1',
    date: '2026-06-09',
    title: 'Identity Inventory: show the role’s name on Role Members',
    changes: [
      'The Role Members tab now shows each role’s friendly name (e.g. "Global Administrator") as the first column, resolved from the Directory Roles snapshot, instead of only the role’s template ID.',
    ],
  },
  {
    version: '1.84.0',
    date: '2026-06-09',
    title: 'New Identity Inventory page: browse Entra users, groups, and roles',
    changes: [
      'Coverage → Identity Inventory shows a daily snapshot of a tenant’s Entra users, groups, directory roles, and role holders ("who is an admin"). Pick a tenant and snapshot date, then sort, filter, and page through each tab — handy for looking up a group’s friendly name and its ID, or seeing who holds Global Administrator.',
      'Group membership (which users belong to a group) isn’t part of this snapshot — a note on the Groups tab calls that out.',
    ],
  },
  {
    version: '1.83.1',
    date: '2026-06-09',
    title: 'Suppress (user, app) pairs directly from test-run alerts',
    changes: [
      'The one-click Suppress action for (user, application) pairs now also appears on Test-Run alert rows, not just live alerts — so you can seed a rule’s allowlist from its test results while it’s still in TEST, before promoting it.',
    ],
  },
  {
    version: '1.83.0',
    date: '2026-06-09',
    title: 'Suppress benign sign-ins by user + application',
    changes: [
      'New "User / Application Pair Allowlist" (Alerting settings): tell a detection rule that a specific user signing in via a specific application is benign, and matching sign-ins stop alerting. Use "Any user" or "Any app" to broaden the rule (e.g. any user via "Windows Sign In"). A known-malicious IP still alerts.',
      'One-click suppress from an alert: on rules that support it, a Suppress action on the alert row pre-fills the user and application from the alert, so you can allowlist the pair without retyping anything. You can widen it to "any user" or "any app" before confirming.',
      'New rule-editor toggle, "Suppress allowlisted (user, application) pairs," turns the tier on for a rule. Matching is on the application’s identifier; the friendly name is shown for convenience.',
    ],
  },
  {
    version: '1.82.4',
    date: '2026-06-09',
    title: 'Logging time on ConnectWise tickets now works',
    changes: [
      'Fixed an issue where logging time when closing an alert linked to a ConnectWise ticket could be rejected. Time entries now include the required start and end times, so your time is recorded reliably on ConnectWise tickets.',
    ],
  },
  {
    version: '1.82.3',
    date: '2026-06-09',
    title: 'Logging time now works even when the ticket was already closed',
    changes: [
      'When you close an alert and log time, the time entry now succeeds even if the linked ticket was already completed. Ticketing systems reject time on a closed ticket, so the portal briefly reopens it, records your time, then closes it again — so your time is always captured.',
    ],
  },
  {
    version: '1.82.2',
    date: '2026-06-08',
    title: 'Ticket time logging: match AutoTask’s exact datetime format',
    changes: [
      'Time entries now send their timestamps in the exact format AutoTask documents (UTC, no trailing time-zone marker), removing any ambiguity in how the ticketing system reads the start, end, and worked dates.',
    ],
  },
  {
    version: '1.82.1',
    date: '2026-06-08',
    title: 'Ticket time logging: also send start/end times for AutoTask',
    changes: [
      'Follow-up to the time-logging fix: AutoTask companies that require start and end times on time entries were still rejecting the entry. The portal now sends a start and end time (derived from the minutes you enter) along with the hours, so logging time on close works regardless of that setting.',
    ],
  },
  {
    version: '1.82.0',
    date: '2026-06-08',
    title: 'Time logs to the ticket reliably, and a consistent loading experience',
    changes: [
      'Logging time when you close an alert now works against AutoTask even when your ticketing-system user has no role pre-filled — the portal automatically uses your default service-desk role, so the time entry no longer fails.',
      'Loading and refreshing now look and behave the same across the portal — Detection Alerts, Investigations Avoided, Alert Rules, Cases, Tenants, and Recent Events all show the same clear "Refreshing…" button, in-progress banner, and dimmed data while new results load.',
    ],
  },
  {
    version: '1.81.1',
    date: '2026-06-08',
    title: 'PSA User Mapping: the likely match floats to the top',
    changes: [
      'On the PSA User Mapping page, the PSA-user dropdown is now sorted alphabetically, and when a ticketing-system user\'s email matches the portal user\'s sign-in name it jumps to the top of the list marked "★ likely match" — so the right person is usually the first choice.',
    ],
  },
  {
    version: '1.81.0',
    date: '2026-06-08',
    title: 'Log time to the ticket when you close an alert',
    changes: [
      'When you close an alert that has a linked ticket, you can now log time to that ticket in the same step. Check "Log time on the ticket", enter the minutes, and the time is recorded against your ticketing-system user — using your resolution note as the summary — before the ticket is closed.',
      'Logging time requires your portal account to be matched to a user in the ticketing system. If there\'s no match you simply won\'t see the time option, and the ticket still closes normally.',
      'New admin page "PSA User Mapping": admins match each portal user to their AutoTask resource or ConnectWise member, per MSP. Pick an MSP, fetch its users from the ticketing system, and assign each person. The portal user list builds itself as people sign in.',
    ],
  },
  {
    version: '1.80.0',
    date: '2026-06-08',
    title: 'Investigations Avoided: jump to a customer, filter suppressed events, and clearer loading',
    changes: [
      'In the "By Customer" breakdown, click any customer to instantly audit its individual suppressed events — no need to find it again in the tenant dropdown. The selected customer is highlighted.',
      'The "Suppressed Events" audit table is now filterable per column — pick a Rule, Severity, or Suppression method from a dropdown, or type to filter by User, IP, or Operation — so you can find a specific suppressed alert fast. A "Clear filters" button and a live "showing N of M" count appear while filters are active.',
      'Refreshing the page is now obvious: the button reads "Refreshing…", a banner appears, and the data dims while the new numbers load.',
    ],
  },
  {
    version: '1.79.10',
    date: '2026-06-08',
    title: 'Detection Alerts: 3-day filter, cleaner IP/Status layout, and a fixed status menu',
    changes: [
      'Added a "3d" (3-day) option to the alert date-range filter, between 24h and 7d. The Alerts page now defaults to the 3-day view on Mondays — so the weekend\'s alerts are in front of you — and to 24h on every other day.',
      'Fixed two display issues on the alerts table: long IPv6 addresses no longer overflow into the Status column (the full address still shows on hover), and the Status dropdown is no longer cut off when you open it on a row near the bottom of the table.',
      'The alert "Actions" quick-suppress button now uses a mute/silence icon instead of a mail envelope, so it clearly represents suppressing an alert (and will host more quick-suppression actions over time).',
    ],
  },
  {
    version: '1.79.7',
    date: '2026-06-08',
    title: 'New "Investigations Avoided" dashboard — see the benign alerts we auto-cleared for you',
    changes: [
      'A new Investigations Avoided page (under Administration) shows the value of M365Signal\'s suppression engine: every detection that matched suspicious activity but was automatically cleared as provably benign — a known managed device, a baselined sign-in IP, a trusted VPN, an owner-authorized delegate, a vendor file-hash false positive, and more. These never reached an analyst.',
      'See the headline "investigations avoided" count, a daily trend, and breakdowns by suppression method, alert type, severity, and customer — for any time window (7/30/90 days) and tenant. Defaults to live (PROD) rules; you can include TEST-mode rules to validate a rule\'s suppression before promoting it.',
      'Drill into a specific tenant to audit individual suppressions: each cleared event renders with the same Source Event view as a real alert, under a "Suppressed by: {method}" banner — so you can confirm a suppression was correct or flag a too-aggressive allowlist.',
    ],
  },
  {
    version: '1.78.0',
    date: '2026-06-07',
    title: 'Silence Defender false-positive waves by allowlisting a (file hash, threat) pair',
    changes: [
      'When Microsoft Defender mass-flags a known-good file as malware (for example a signed remote-support installer caught by a generic ML detection), you can now stop those alerts without turning the rule off. Add the file\'s hash together with the threat family it\'s being mis-flagged as on the new Defender File-Hash Allowlist page (under the alerting settings), and matching Defender alerts stop firing across all tenants.',
      'Pairing the hash with the threat family keeps you safe: the same file flagged as a different threat still alerts, and a file seen from a known-malicious IP still alerts. Entries are soft-disabled (never hard-deleted) so you keep an audit trail, and you can trust a file outright with a "*" any-threat entry if you choose.',
      'Turn it on per rule with the new "Suppress Defender alerts for allowlisted (file hash, threat) pairs" toggle on the Defender pass-through rule.',
    ],
  },
  {
    version: '1.77.0',
    date: '2026-06-07',
    title: 'Microsoft Defender alerts now show the file, process, and network evidence',
    changes: [
      'Defender for Endpoint alerts (malware, suspicious process, command-and-control, credential access, and more) used to show only "No structured view for this log_type yet." Expanding one now shows the actual threat evidence — the file name, path, hashes and signer; the process command line and image file; the destination URL/IP; and the device and user involved.',
      'Each alert leads with what matters for that kind of threat: a malware alert leads with the file, a command-and-control alert leads with the blocked URL and destination IP (and still surfaces the file to investigate), and a credential-theft alert leads with the process and the acting user. The headline evidence is also lifted into a colored banner at the top of the expanded alert so you can triage at a glance.',
      'Unsigned files and malicious verdicts are flagged, MITRE technique IDs link out to ATT&CK, and an "Open in Microsoft Defender" link takes you straight to the alert in the Defender portal. Applies on the Detection Alerts page and Test Run Results.',
    ],
  },
  {
    version: '1.76.0',
    date: '2026-06-04',
    title: 'PSA ticket on an alert is now a clickable link (with an admin-set URL)',
    changes: [
      'The PSA Ticket shown on an alert is now a clickable link that opens the ticket in your PSA in a new tab. The ticket number is still copyable too.',
      'Admins can set the ticket link format per MSP on the Ticketing page — a new "Ticket URL template (web-UI link)" field. Enter your PSA ticket URL (e.g. AutoTask\'s https://ww14.autotask.net/Mvc/ServiceDesk/TicketDetail.mvc?ticketId={ticketId}); use {ticketId} where the id goes, or leave it at the end. Leave blank to auto-derive from the PSA host.',
    ],
  },
  {
    version: '1.75.1',
    date: '2026-06-04',
    title: 'Opening an alert link from a ticket is now instant',
    changes: [
      'When you open the "View in portal" link from a PSA ticket, the page now loads just that one alert instead of pulling the tenant\'s entire alert history first — so it opens immediately. A "Load all alerts for this tenant (last 30 days)" button is there if you want the full list.',
    ],
  },
  {
    version: '1.75.0',
    date: '2026-06-04',
    title: 'See the linked PSA ticket on an alert — and close it when you close the alert',
    changes: [
      'Detection Alerts: expanding an alert now shows the PSA ticket the system opened for it (ConnectWise or AutoTask) with the ticket number, so you can find it in your PSA without hunting.',
      'Closing an alert can now close its PSA ticket too: the Close dialog offers "Also close linked ticket #…" (on by default). When checked, your resolution note is added to the ticket and the ticket is moved to its completed status. The alert always closes even if the PSA step fails — you\'ll get a brief notice if it didn\'t go through.',
    ],
  },
  {
    version: '1.74.0',
    date: '2026-06-04',
    title: 'Ticketing page shows real default values (no more guessing "(saved)")',
    changes: [
      'Ticketing page: the MSP-default fields (Company, Queue/Board, New Status, Completed Status, Default Priority, and the per-severity Priority Map) now display their actual stored values instead of an opaque "•••••• (saved)". These IDs aren\'t secret, so you can see exactly what\'s configured — e.g. a leftover/invalid Company or Queue is now obvious. Connection credentials remain write-only and still show "(saved)".',
      'After "Fetch from PSA" the stored values resolve to their friendly names in the dropdowns; before fetching they show as "current: <id>".',
    ],
  },
  {
    version: '1.73.2',
    date: '2026-06-04',
    title: 'Ticketing: priority map shows "(saved)" after saving',
    changes: [
      'Ticketing page: after saving the MSP default Priority Map, the per-severity dropdowns now show "•••••• (saved) — pick to change" (consistent with the other saved fields) instead of "— none —", which made it look like the mapping had been cleared. The values are stored; the form just doesn\'t echo saved values back.',
    ],
  },
  {
    version: '1.73.1',
    date: '2026-06-04',
    title: 'Save MSP defaults without re-entering credentials',
    changes: [
      'Ticketing page: you can now change the MSP defaults (Queue/Board, statuses, priority map) and Save without re-typing the connection credentials. Previously it errored with "Fill all required credential fields" because credentials are write-only. The defaults are now merged into the saved configuration.',
    ],
  },
  {
    version: '1.73.0',
    date: '2026-06-04',
    title: 'MSP ticketing defaults that auto-fill new client routing',
    changes: [
      'Ticketing page: the form now clearly separates required Connection credentials (above the divider) from optional MSP Defaults (below) — default Queue/Board, New Status, Completed Status, and a per-severity Priority Map (Critical→Informational), all picked from the PSA via "Fetch from PSA". The old raw JSON priority box is gone.',
      'Ticket Routing page: when you open a client that has no routing yet, its Queue/Board, statuses, and priority map are pre-filled from the MSP defaults — you just pick the client Company and Save. Company is never defaulted (it is per-client).',
    ],
  },
  {
    version: '1.72.3',
    date: '2026-06-04',
    title: 'Ticket Routing: PSA options cached per MSP + AutoTask test-ticket fix',
    changes: [
      'Ticket Routing page: "Fetch from PSA" now caches the company / queue/board / status / priority lists per MSP, so after fetching once you can configure every client under that MSP without re-fetching. Rows show "Using cached … PSA options" and the button becomes "Refresh from PSA".',
      'Fixed: creating an AutoTask test ticket failed with "Missing Required Field: priority". The test ticket now always includes a valid priority — the MSP default if set, otherwise the tenant\'s severity→priority map, otherwise the first active priority from the PSA.',
    ],
  },
  {
    version: '1.72.2',
    date: '2026-06-04',
    title: 'AutoTask ticket routing now works end-to-end',
    changes: [
      'Ticket Routing page: the "Fetch from PSA" pickers (Company, Queue, Status, Priority) now work for AutoTask — previously the Queue/Status/Priority dropdowns errored or showed a single "undefined" entry. Companies are no longer capped at 500, so larger AutoTask instances show every active company.',
      'MSP Ticketing page: the per-client Company, Queue/Board, and Status fields are now optional — the authoritative per-tenant routing lives on the Ticket Routing page. They remain on the credentials page (optional) so the Create/Update/Close test-ticket buttons can run against the MSP credentials when no tenant is selected.',
    ],
  },
  {
    version: '1.71.0',
    date: '2026-06-03',
    title: 'Stop false alerts from Teams-certified desk phones enrolling',
    changes: [
      'Microsoft-Teams-certified desk phones (Poly, Yealink, AudioCodes) sign in with the same OAuth device-code flow that attackers abuse, which produced false "device code phishing" alerts when an appliance account (e.g. a cashier station) enrolled a phone. A new rule option suppresses these — but only when the account is on an approved list AND a matching Teams phone actually registered within ~2 minutes. A known-malicious IP still alerts.',
      'New Account Allowlist page (under Alerting): list the appliance/station accounts allowed to enroll Teams phones, per rule and tenant. Read-only for non-admins.',
      'New Teams-Phone Models page (under Alerting): manage the device-name patterns (e.g. polyccx, yealink mp, audiocodes) used to recognize a certified phone.',
      'Rule editor: a new "Suppress Teams-phone device-code enrollments" toggle turns the behavior on for a rule.',
    ],
  },
  {
    version: '1.70.1',
    date: '2026-06-03',
    title: 'Mailbox-pair allowlist now works for more detection rules',
    changes: [
      'The mailbox-pair allowlist — which suppresses approved (mailbox, accessor) pairs — is no longer tied to a single rule. Any detection rule with the new "Skip if in Mailbox-Pair Allowlist" option turned on can use it, including the new "Mailbox Accessed by Non-Owner" rule.',
      'Rule editor: a new "Skip if in Mailbox-Pair Allowlist" toggle lets admins turn the allowlist on for a rule.',
      'Mailbox-Pair Allowlist page: switch between rules using clear tabs at the top of the page; the Add form names the selected rule so it\'s obvious which rule an entry applies to.',
      'Detection Alerts: the "Allowlist" quick-action now appears on alerts from any allowlist-enabled rule and files the entry under that alert\'s own rule.',
    ],
  },
  {
    version: '1.69.4',
    date: '2026-06-03',
    title: 'Per-tenant ticket routing, webhooks, and live PSA pickers',
    changes: [
      'New Ticket Routing page (Admin → Ticket Routing): set each client tenant\'s PSA company, board/queue, and open + closed status so alerts open tickets under the right client. Use "Fetch from PSA" to pick real values from ConnectWise/AutoTask instead of typing IDs.',
      'Map each alert severity (critical/high/medium/low/informational) to a specific PSA priority per tenant — so a critical alert can open a P1 while a low alert opens a P4.',
      'New Webhooks page (Admin → Webhooks): set a per-MSP webhook URL for alert delivery, independent of Teams and email.',
      'MSP Ticketing page: Create test ticket / Update / Close buttons now let you verify the full ticket lifecycle against your PSA without waiting for a real alert — and you can optionally test "as a tenant" to exercise that tenant\'s exact routing (including its close status).',
      'MSP admins can manage their own MSPs\' ticket routing and webhooks; global admins see all.',
    ],
  },
  {
    version: '1.68.7',
    date: '2026-06-02',
    title: 'Ticketing: test the connection after saving credentials',
    changes: [
      'You can now use "Test connection" to validate a ticketing configuration after it has been saved — leave the credential fields blank and click Test, and the portal checks the stored credentials. (Previously you could only test before the first save, since saved credentials are never shown back on screen.)',
      'Saved ticketing fields now clearly show "•••••• (saved)" — including the Site URL, which previously looked like it had reverted to the example value.',
    ],
  },
  {
    version: '1.68.6',
    date: '2026-06-02',
    title: 'Fix: ConnectWise ticketing setup rejected a valid Company ID',
    changes: [
      'When configuring ConnectWise ticketing, a correctly filled-in Company ID (login) — e.g. a name like "skynet_cs2" — was wrongly reported as a missing required field on Save and Test connection. The login is now accepted as entered. AutoTask setup is unaffected.',
    ],
  },
  {
    version: '1.68.5',
    date: '2026-06-02',
    title: 'Fix: Detection Alerts now filter test alerts by the selected tenant',
    changes: [
      'On the Detection Alerts page, selecting a tenant with "Show Test Alerts" turned on no longer shows test alerts from other tenants — the tenant filter now applies to test alerts too, matching the live alerts.',
    ],
  },
  {
    version: '1.68.4',
    date: '2026-06-02',
    title: 'MSP admins can set up their own ticketing integration',
    changes: [
      'MSP administrators can now configure ticketing for the MSPs they manage directly from the Ticketing screen — they see and edit only their own MSPs. Global administrators continue to see and manage every MSP.',
      'The ticketing setup form now requires the routing fields needed to actually open a ticket (AutoTask: Company ID, Queue ID, New Status ID; ConnectWise: Board ID, Ticket Company ID), so a saved configuration is always complete enough to create tickets.',
    ],
  },
  {
    version: '1.68.1',
    date: '2026-06-02',
    title: 'Open an alert straight from its email link + per-MSP ticketing setup',
    changes: [
      'Alert notification emails can now link straight to the specific alert in the portal. Clicking "View in portal" opens that exact alert — already expanded — instead of just the alert list. You only see it if you have access to that tenant; if the alert was removed or you lack access, the portal shows the alert list with a brief notice rather than an error.',
      'New admin screen, "Ticketing", lets you connect each MSP to its PSA ticketing system (Kaseya AutoTask or ConnectWise Manage). Pick the system, enter the credentials, test the connection, and turn ticketing on or off per MSP. Credentials are stored securely and are never shown back on screen. (Ticket creation goes live once the alert engine starts sending tickets.)',
    ],
  },
  {
    version: '1.67.2',
    date: '2026-06-01',
    title: 'Fix: Alert Email Routing now saves recipients',
    changes: [
      'Fixed a bug on the Alert Email Routing admin screen where email recipients appeared to save but were gone after a page refresh — the addresses are now stored correctly.',
    ],
  },
  {
    version: '1.67.1',
    date: '2026-06-01',
    title: 'Resolution notes when closing alerts + per-MSP alert email routing',
    changes: [
      'Closing an alert now prompts for a resolution note (False positive / Benign true positive / Remediated, or your own text). The note, who closed the alert, and when are shown on the alert so triage stays auditable. Acknowledging an alert does not require a note, and reopening clears it.',
      'New admin screen, "Alert Email Routing", lets you configure per-MSP alert email recipients with an on/off toggle for each MSP — independent of the Teams channel, so turning email on or off never affects Teams.',
    ],
  },
  {
    version: '1.66.0',
    date: '2026-06-01',
    title: 'Change an alert\'s status right from the Status badge',
    changes: [
      'You can now change an alert\'s status by clicking its Status badge in the Alerts table — a dropdown lets you set it to New, Acknowledged, or Closed directly. Previously the only way to close an alert was a small, easy-to-miss icon in the far-right Actions column; that icon has been replaced by this clearer dropdown.',
    ],
  },
  {
    version: '1.65.5',
    date: '2026-05-29',
    title: 'MSP column on Tenants, clearer Conditional Access changes, baseline shown by default for sign-ins',
    changes: [
      'The M365 Tenants admin page now has an MSP column, and you can filter the list by MSP (dropdown next to the search box). MSP is also matched by the search box and the column is sortable.',
      'Conditional Access policy create/delete alerts are now much easier to read. Instead of dumping the entire policy as one long line of raw JSON, the policy\'s settings are now listed field-by-field — green for a newly created policy, red for a deleted one — so you can see exactly what the policy did at a glance.',
      'On sign-in alerts (and sign-in test results), the Baseline Comparison panel is now expanded by default, so the location/device/provider comparison is visible without an extra click.',
    ],
  },
  {
    version: '1.65.4',
    date: '2026-05-29',
    title: 'Wider data tables — less wasted space on the sides',
    changes: [
      'The Alerts, Alert Rules, Tenants, and Pipeline Health pages now use the full width of the window instead of being capped to a centered column. On wide monitors this removes the large empty margins on the left and right, giving the data tables more room to breathe.',
    ],
  },
  {
    version: '1.65.3',
    date: '2026-05-28',
    title: 'Device Inventory Comparison: stale "ghost" records no longer hide active devices',
    changes: [
      'Fixed cases where the Device Inventory Comparison page flagged a real, active device as "Stale" (red dot) because DRMM still had an old record for the same machine — typically after a device rename, where DRMM keeps both the old and new entries and the old one\'s last-seen timestamp is frozen in the past. The page now picks the freshest entry per workload when multiple records point at the same physical device, so active devices show green even when a stale duplicate exists upstream.',
    ],
  },
  {
    version: '1.65.2',
    date: '2026-05-28',
    title: 'Device Inventory Comparison: same device, one row',
    changes: [
      'Fixed the Device Inventory Comparison page showing the same macOS (or Windows) device as 2-3 separate rows when different tools reported different names — e.g. Apple\'s user-facing name "Jim\'s MacBook Air" in M365/Entra vs the local hostname "Jims-MacBook-Air-5" in Intune vs "Jims-MacBook-Air-5.local" in DRMM.',
      'The page now joins workloads using stable hardware identifiers — Microsoft AAD device ID across M365/Entra/Intune, serial number between Intune and DRMM, and MAC address between DRMM and BPC. Hostname matching is still used as a fallback. Randomized macOS Wi-Fi MACs and placeholder all-zero MACs are excluded so they don\'t false-match across devices.',
      'Known gap: BPC still doesn\'t expose a serial number to us, so BPC-only devices may still appear as separate rows when their MAC addresses don\'t overlap with DRMM. We\'ve flagged this with the data team.',
    ],
  },
  {
    version: '1.65.1',
    date: '2026-05-28',
    title: 'Backfill: 60-min stagger, defaults to "Schedule for tonight"',
    changes: [
      'Scheduled backfills now stagger 60 minutes apart by default (up from 30) so mid-market and larger tenants don\'t overlap. Up to 13 tenants still fit in tonight\'s 5 PM ET → 6 AM ET window.',
      'The "When to run" choice now defaults to "Schedule for tonight" instead of "Start now". You can still pick "Start now" if you\'re already in the off-peak window.',
      'Internal fix: improved the Activity Feed startDate guardrails so older operator-picked date ranges no longer trip Microsoft\'s 7-day retention cutoff for Exchange / SharePoint / General / DLP / AAD-Activity-Feed types.',
    ],
  },
  {
    version: '1.65.0',
    date: '2026-05-28',
    title: 'Backfill: smart per-type lookback + "Schedule for tonight"',
    changes: [
      'You can now choose "Schedule for tonight" when starting a backfill. The job runs automatically after 5:00 PM Eastern — the local equivalent (e.g. 2:00 PM PDT) is shown in the dialog so you know exactly when. Multiple tenants scheduled the same night don\'t overlap: each new one starts 30 minutes after the latest one already queued.',
      'Each log type now fetches its own sensible default window: Graph Sign-Ins goes back 30 days, every other type stops at 7 days. The outer date picker is still a ceiling — narrow it and everything narrows — but you no longer waste API calls querying Exchange or DLP past their 7-day Microsoft retention.',
      'The drawer\'s log-type table now shows two columns: "Lookback" (the per-type default fetch window, what we actually request) and "Microsoft retention" (informational, the wall beyond which data is unavailable).',
      'Cancelling a scheduled backfill from the UI is coming in a later release — for now, contact a global admin if you need to cancel.',
    ],
  },
  {
    version: '1.64.2',
    date: '2026-05-28',
    title: 'Backfill: Activity Feed fix, readable errors, non-peak hours reminder',
    changes: [
      'Fixed Exchange / SharePoint / General / DLP / AAD Sign-In backfills failing with AF20055 (Microsoft\'s Activity Feed API caps each call at 24 hours; the orchestrator now sends 1-day windows for those types).',
      'When a chunk errors, the full error message now wraps under the row instead of being truncated. The error code (e.g. AF20055) is pulled out into a small label so you can see at a glance what kind of failure it is.',
      'New red banner at the top of the drawer reminds you to run backfill outside business hours — after 5:00 PM Eastern, with the local equivalent (e.g. "2:00 PM PDT") shown for your time zone.',
    ],
  },
  {
    version: '1.64.1',
    date: '2026-05-28',
    title: 'Backfill drawer: clearer "live" feedback while waiting',
    changes: [
      'The Backfill progress view now shows "Auto-refreshing every 5s · updated Xs ago" with a spinning icon under the status badge, so you can see the page is alive while the job is queued and the table is still showing "—". The indicator disappears once the job finishes.',
    ],
  },
  {
    version: '1.64.0',
    date: '2026-05-28',
    title: 'Backfill historical log data for a newly onboarded tenant',
    changes: [
      'A new "Backfill" button on the Tenants page (next to Probe / Edit / Consent Link, shown for active tenants) lets admins pull historical M365 log data into a tenant that was just onboarded — no more pinging the log-collection team to run a script.',
      'The drawer preselects the recommended log types based on the tenant\'s license tier, with Identity-only and Defender-only types greyed out when the tier doesn\'t support them.',
      'Before submitting, a confirmation dialog reminds you that backfill is only useful within the first few days of onboarding — Microsoft retains most workload data for only 7–30 days, so older history is no longer available from their APIs.',
      'The backfill runs server-side and can take 5–30 minutes for a fresh tenant. You can close the drawer (or the browser tab) and reopen Backfill for the same tenant later to re-attach to live progress. Re-running an overlapping window is safe — days that already have data are skipped.',
    ],
  },
  {
    version: '1.63.2',
    date: '2026-05-27',
    title: 'Fix: activating and managing tenants was failing',
    changes: [
      'Activating a tenant (and deactivating, editing, license-probing, or managing a tenant’s safe IPs) was erroring out with an internal "Stop-IfNotMspAdmin is not recognized" message. This affected both global admins and MSP admins and has been fixed — those actions work again.',
    ],
  },
  {
    version: '1.63.1',
    date: '2026-05-27',
    title: 'IP user list: app names resolved, cleaner layout',
    changes: [
      'When the IP was used by an app or service (a "service principal" sign-in rather than a person), the user list now shows the app’s name instead of a bare ID — resolved straight from the sign-in event, with the underlying ID kept alongside for reference.',
      'Empty "Not Available" entries are no longer listed.',
      'The "List users" button moved to the right of the cross-tenant summary, matching the "Run Trace" button. The Session Trace shortcut icon was removed from the alert row’s Actions column — you now start a session trace from the "Run Trace" button inside the expanded alert’s Session Trace section.',
    ],
  },
  {
    version: '1.63.0',
    date: '2026-05-27',
    title: 'See which users share an alert’s IP address',
    changes: [
      'The IP Intelligence panel on a Detection Alert now has a "List users" button next to the cross-tenant summary. Click it to pull the list of users (by UPN) who signed in from that same IP address in the last 30 days, shown right below the summary with each user\'s sign-in count and when they were last seen.',
      'For privacy across managed clients, the list shows full UPNs only for the alert\'s own tenant. If the IP was also seen in other tenants, you\'ll see a count of how many users and tenants — without exposing those users\' identities.',
    ],
  },
  {
    version: '1.62.1',
    date: '2026-05-27',
    title: 'Onboarding wizard: clearer URL wording + DRMM and BlackPoint follow-up reminders',
    changes: [
      'The admin-consent step now says "Send this Client specific URL to the client\'s M365 Global Admin" so it\'s obvious the link is unique to that tenant and shouldn\'t be reused.',
      'The final review step before creating a tenant now lists two follow-up reminders: if the client is in DRMM, add a DRMM Site variable named M365_Tenant_ID set to the tenant\'s Microsoft tenant ID (shown ready to copy); and if the client is in an accessible BlackPoint Cyber instance, update the "BlackPoint Cyber MDR Tenants" page with the tenant-to-client mapping.',
    ],
  },
  {
    version: '1.62.0',
    date: '2026-05-27',
    title: 'MSP admins can now manage their own MSP’s tenants',
    changes: [
      'If you administer an MSP (you’re in your MSP’s admin group) but aren’t a global admin, the Administration → Tenants area is now available to you. You can onboard, edit, activate, deactivate, and license-probe the M365 tenants in your own MSP — the list and every action is automatically scoped to your MSP, and you can’t see or touch another MSP’s tenants. Removing a tenant entirely is still reserved for global admins.',
      'When onboarding a tenant, the MSP field is now a dropdown limited to the MSP(s) you administer (global admins still choose any MSP), so you can’t accidentally file a tenant under the wrong MSP.',
      'BlackPoint Cyber (BPC) tenants now carry an MSP tag and are scoped the same way: you manage the BPC tenants belonging to your MSP, including adding, editing, and removing them. A new MSP column appears on the BPC Tenants page. Moving a BPC tenant to a different MSP remains a global-admin action.',
    ],
  },
  {
    version: '1.61.1',
    date: '2026-05-26',
    title: 'Detection alerts now show whether the sign-in device is in DRMM',
    changes: [
      'The Baseline Comparison panel on Detection Alerts and Test Run Results now has a "Device in DRMM" row, sitting just below the two Intune rows. "Yes" (green) means the device that signed in was found in that tenant\'s DRMM inventory — a snapshot taken before the alert, so a device added to DRMM after an account is compromised can\'t make itself look managed. Treat "Yes" as the reassurance signal.',
      '"No" (amber) does NOT automatically mean an unmanaged device — phones and tablets are never in DRMM, and the device name may differ from its DRMM hostname. So "No" is shown for attention but is intentionally not counted as a baseline deviation. A dash (—) means there was nothing to evaluate: either the alert predates this feature, or the event isn\'t a sign-in (e.g. an Azure resource change) and carries no device name to look up.',
    ],
  },
  {
    version: '1.60.3',
    date: '2026-05-26',
    title: 'Mailbox-Pair Allowlist: clearer messaging on which fields can be edited',
    changes: [
      'On the Mailbox-Pair Allowlist admin page, the helper text under the header and below the Add form now spell out that Tenant, Target Mailbox, Actor Type, and Actor Value are all immutable once a row is saved — only Notes can be edited afterward. To change any identity field, delete the entry and add a new one (re-adding the same triple is a no-op upsert, so it\'s safe). The Edit pencil tooltip says the same thing. Copy-only change; the storage rules haven\'t changed.',
    ],
  },
  {
    version: '1.60.2',
    date: '2026-05-26',
    title: 'Detection alerts now show the app name for app-only mailbox access',
    changes: [
      'When a rule-008 / rule-014 alert is driven by an app-only Graph identity (Druva, Veeam, sanctioned Microsoft service infrastructure, etc.), the User column on the Detection Alerts page now reads "App: Druva M365 Advanced" (or whichever app it is) instead of the mailbox-owner UPN with "(owner)" suffix. The friendly app name was already on the alert row in a sibling column — the portal just wasn\'t reading it. The original SID (PUID) is preserved for hashing and CSV export; only the rendered label changes.',
      'The Allowlist quick-action modal on rule-008 alerts now offers the app name and app GUID from the alert row as preferred candidates per mailbox, with the app name pre-checked. Picking the app name produces a narrower and more durable allowlist entry than the v1.60.1 "Service SID" approach, since display names survive PUID rotation.',
    ],
  },
  {
    version: '1.60.1',
    date: '2026-05-26',
    title: 'Mailbox-pair allowlist now supports Service SID identities',
    changes: [
      'Some sanctioned mailbox-access services (Druva backup, Veeam, Microsoft Exchange REST infrastructure, etc.) show up in audit logs as a service SID — an identifier like `S-1-8-618832045-...` — with no resolvable username, app GUID, or app display name. In v1.60.0 the only way to suppress alerts caused by these accessors was to allowlist the affected mailbox to "Any actor (*)", which was broader than ideal.',
      'You can now pick "Service SID" as the actor type and paste the SID directly. The allowlist will match that one specific SID against the target mailbox — a different unknown SID hitting the same mailbox later will still fire the alert.',
      'On the quick-action button on rule-008 alert rows: the `service-SID · S-…` row in the modal\'s candidate grid is now checkable. Pre-existing "Any actor (*)" entries from v1.60.0 still work; replace them with the narrower Service SID shape when you get a chance.',
    ],
  },
  {
    version: '1.60.0',
    date: '2026-05-26',
    title: 'New: Mailbox-Pair Allowlist for rule-008 false positives',
    changes: [
      'New admin page at Settings → Mailbox-Pair Allowlist. Lets admins suppress rule-008 ("Anomalous Mailbox Access") false positives by allowlisting specific (target_mailbox, actor) pairs per tenant. Four actor identity types: UPN, App ID, App Name, or "Any actor (*)" — the wildcard is the right choice for shared service-team mailboxes touched by a mix of humans and apps.',
      'Quick action on rule-008 alerts: admins now see a green envelope icon in the Actions column. Click it to open a modal that lists every (mailbox, actor) pair found in that alert\'s source events as a checkbox grid. The most-likely pair is pre-checked; tick additional rows to allowlist multiple pairs at once. Apply submits them all sequentially.',
      'The detection pipeline picks up portal changes automatically within ~90 seconds (no stream restart needed); a background retry process handles transient storage hiccups, and a "Pending sync" banner surfaces if anything stalls. Admins also get a "Reconcile sentinel" recovery button if needed.',
      'Read access is open to everyone (so any analyst can see what\'s allowlisted); writes require admin.',
    ],
  },
  {
    version: '1.59.2',
    date: '2026-05-22',
    title: 'Country normalization extended to cover 25 more countries',
    changes: [
      'The Baseline Comparison Country row now paints green correctly for alerts originating from countries that ProxyCheck.io emits under a different name than ISO\'s canonical English spelling. The most common case was "The Netherlands" (with the article) not matching "NL"; smaller cases included Cabo Verde / Cape Verde, Ivory Coast / Côte d\'Ivoire, Eswatini / Swaziland, Brunei / Brunei Darussalam, Syria / Syrian Arab Republic, and the U.S. Virgin Islands. Affects classic `signin` alerts where the portal has to fall back to the IP enrichment table for the country value.',
      'Companion data-side fix is queued — once it ships, the IP enrichment table will be ISO-populated end-to-end for ~99.99% of rows and this portal-side normalization becomes a defensive layer instead of a load-bearing one.',
    ],
  },
  {
    version: '1.59.1',
    date: '2026-05-22',
    title: 'IP enrichment refresh now also populates State and City',
    changes: [
      'The on-demand IP enrichment refresh button (introduced in v1.59.0) now also populates the State and City rows in the Baseline Comparison panel for classic sign-in alerts. Previously only Country and Provider were wired to the refreshed data; State and City stayed blank even though the enrichment table had values for them. The Microsoft Activity-Feed sign-in schema doesn\'t carry a per-event State or City, so the panel now falls back to the IP-enrichment table for those two rows in the same way Country already does.',
      'Rows that still cannot be populated by the refresh button: Hour of day (comes from the alert\'s own timestamp), OS, Intune Managed device, Intune Compliant device — those four come from the device-detail block in the source event JSON and Microsoft genuinely doesn\'t ship a device block on classic Activity-Feed sign-in events. They\'ll continue to show "—" on `signin`-source alerts regardless of refresh; that\'s a separate Microsoft-schema gap.',
    ],
  },
  {
    version: '1.59.0',
    date: '2026-05-22',
    title: 'On-demand IP enrichment refresh from the Baseline Comparison panel',
    changes: [
      'Admins now see a small refresh icon next to "Event Value" in the Baseline Comparison header on Detection Alerts and Test Run Results. Clicking it re-fetches the IP enrichment for that alert\'s IP directly from ProxyCheck.io and stores it back to the shared enrichment table. The panel updates in place — Country, Provider, and the IP Intelligence block all repopulate without a page reload. Useful when an IP first appears between the daily enrichment runs and the alert lands before the next scheduled refresh.',
      'If the IP was refreshed in the last 24 hours, the button prompts to confirm before re-running so we don\'t burn ProxyCheck quota unnecessarily. Visible to admins only.',
    ],
  },
  {
    version: '1.58.3',
    date: '2026-05-21',
    title: 'Removed: Intune Coverage Gap page',
    changes: [
      'The standalone "Intune Coverage Gap" page under Coverage Analysis has been removed. The same signal — M365-active users with no Intune-enrolled device — is available from the Device Inventory Comparison page by setting Source = "Seen By M365", Target = "Intune", and using the "Source not in Target" filter, with the added benefit of cross-referencing the other workloads (DRMM, Entra, BPC) at the same time. The Coverage hub now shows two cards instead of three.',
    ],
  },
  {
    version: '1.58.2',
    date: '2026-05-21',
    title: 'Fix: Baseline Comparison Country row now matches "United States" against "US"',
    changes: [
      'On classic sign-in alerts (the ones whose rule footer says "signin" rather than "graph_signin"), the Baseline Comparison Country row was painting red even when the event country and the baseline country were the same place. The event side carried the full country name from IP enrichment ("United States") while the baseline side carried the ISO-2 code ("US"), and the comparator did exact-string match. The two sides are now normalized to ISO codes before comparing, so legitimate matches paint green like they should. A pipeline-side follow-up is filed to fix the impedance mismatch at the source.',
    ],
  },
  {
    version: '1.58.1',
    date: '2026-05-21',
    title: 'Session Trace section title clarified',
    changes: [
      'The Session Trace section header now reads "Session Trace (from Event timestamp to +24h)" so it\'s explicit that the 24-hour window starts at the alert event time and runs forward — not centered on it.',
    ],
  },
  {
    version: '1.58.0',
    date: '2026-05-21',
    title: 'Detection Alerts: Cross-tenant IP prevalence + on-demand Session Trace',
    changes: [
      'IP Intelligence panel now shows a cross-tenant prevalence pill: "9 tenants · 25 users · same-tenant 13 users · first seen 30d ago." Sky-blue when the IP is shared across many users in this tenant (likely corporate VPN), amber when only one tenant and one user are using it (likely targeted), neutral otherwise. Counts cover the last 30 days. Lazy-loads on row expand.',
      'New Trace Session button in the Actions column (magnifying-glass-plus icon) on any alert whose source event has an AAD session ID. Clicking it expands the row and pulls every event tied to that session — direct AAD-session matches plus a fallback user-window for log types that don\'t carry the session ID at the top level (SharePoint, Teams, directory audits, URL clicks, message trace, risk detections) — for the 24 hours starting at the alert event time. Returns in 5-15 seconds. The inline panel shows a sortable Timeline (sensitive operations like ModifyFolderPermissions / New-InboxRule / role assignments highlighted in amber) and a Summary tab (per-IP enrichment, top operations, providers touched). Admin-only. Works on both production alerts and test-run replays shown via "Show Test Alerts".',
    ],
  },
  {
    version: '1.57.6',
    date: '2026-05-21',
    title: 'Baseline Comparison: Country row no longer shows "—" on classic sign-in alerts',
    changes: [
      'On alerts that came from the classic Activity-Feed sign-in source (the bottom of the alert header shows "signin" under the rule name, not "graph_signin"), the Baseline Comparison panel\'s Country event value was showing "—" even though the IP Intelligence panel directly above it had a country populated. The two panels were pulling from different fields; the Baseline panel now falls back to the same IP-enrichment country the IP Intelligence panel uses, so Country renders with the correct green/red verdict on every alert regardless of source.',
      'Other rows on classic sign-in alerts (State, City, Hour, OS, Compliant, Managed) still show "—" because Microsoft genuinely doesn\'t ship those values on the classic Activity-Feed schema — there\'s no IP-derived equivalent to fall back to at the alert level. The detection team is looking at enriching those alerts at write-time so the panel populates fully regardless of source.',
    ],
  },
  {
    version: '1.57.5',
    date: '2026-05-21',
    title: 'Baseline Comparison: the event value (not the baseline cells) is now what turns green or red',
    changes: [
      'On the Baseline Comparison panel (Detection Alerts + Test Run Results), the green / red coloring is now on the Event Value column instead of the 7-day and 30-day baseline columns. Green means the event matches at least one baseline and neither baseline shows a deviation; red means at least one baseline shows the event is outside it; plain gray means the event has no value or there\'s no baseline yet. Both baseline columns now render in the standard light-gray text, so a row can be read in one direction: the event value carries the verdict, the baselines are just reference data.',
      'Same change applies to all rows (Country, Provider, IP-in-baseline, Hour, OS, Compliant, Managed) and to all detection rules. The "N deviations" count in the panel header is unchanged.',
    ],
  },
  {
    version: '1.57.4',
    date: '2026-05-20',
    title: 'Alerts + Test Run Results: Baseline Comparison now includes the user\'s typical ISP / Provider',
    changes: [
      'The Baseline Comparison panel on the Detection Alerts page and the Test Run Results page now has a Provider row. It shows the ISP / carrier of the event IP next to the list of providers the user has signed in from over the last 7 and 30 days, with the same green-match / red-deviation coloring used for Country, City, etc. Useful for catching residential-proxy and VPN events where the country looks fine but the carrier is unusual — and conversely for fast-acquitting an unfamiliar IP that turns out to be the user\'s same carrier on a different cell tower.',
      'Alerts that were generated before 2026-05-20 ~3:30pm Eastern will show "No baseline data" in the new row — the data didn\'t exist yet at the time those alerts were written. New alerts (and any test runs you trigger from here on) will be populated.',
    ],
  },
  {
    version: '1.57.3',
    date: '2026-05-19',
    title: 'Detection Alerts: the visible row count now matches what you asked for',
    changes: [
      'Fixed a bug where the Detection Alerts page would show only a handful of rows in the table (e.g. "1–4 of 4") even though the summary cards above it claimed there were hundreds of alerts. The page was pulling the entire alerts table on every load and then filtering by date in the browser; on the All-tenants view that scan was silently timing out partway through, so you\'d get an inconsistent slice and the visible-vs-summary numbers drifted. Refreshing would return a different slice and a different count. The date range is now pushed down to the API, so the server returns the right rows the first time and the table + cards agree.',
      'Recent Events: Device Security is no longer checked by default in the workload picker. Those rows are usually high-volume / low-signal for the quick-look view and were crowding out the workloads analysts actually come to scan. The dropdown still lists it — tick it on when you need it.',
    ],
  },
  {
    version: '1.57.2',
    date: '2026-05-19',
    title: 'Test Run Results: timezone picker',
    changes: [
      'The Test Run Results page now has a timezone selector in the summary bar (next to the filter box). All times on the page — Event Time column, AiTM click time, correlated sign-in time, aggregation summary, source-event details, and the run started/completed labels — render in the zone you pick. The choice is saved and shared with the Alerts page and Case page, so you only have to set it once.',
    ],
  },
  {
    version: '1.57.1',
    date: '2026-05-19',
    title: 'Fix: cloning an "operation + baseline" detection rule now saves',
    changes: [
      'Cloning a rule of type "Op + Baseline" (operation_baseline_deviation) was failing with "Validation failed.: operation_baseline_deviation requires operation in parametersJson." Editing the same rule worked, only cloning failed. The drawer was already writing the correct field name; the server-side validator on the Create endpoint was still checking the old field name. Validator updated to match.',
    ],
  },
  {
    version: '1.57.0',
    date: '2026-05-18',
    title: 'MSP-based data separation — Phase 2 (enforcement plumbing in place, kill switch still OFF)',
    changes: [
      'Phase 2 of the MSP data separation work. Every tenant-scoped API endpoint now runs an MSP authorization check, and every cross-tenant list endpoint runs an MSP filter. The kill-switch app setting MSP_ENFORCEMENT_ENABLED stays OFF so the new checks log "would-deny" / "would-filter" diagnostics but do not change any response. End users (you) should see no visible difference from 1.56.0.',
      'Detection Alerts → Alert Rules: MSP users (non-global-admin) will get a read-only view of detection rules once enforcement turns on. The Create / Edit / Clone / Delete / Test-Run buttons hide for them; the table still renders the rules they\'re authorized to see (rules scoped to their MSPs, plus any rule with no scope = "applies to all tenants").',
      'Mock-data fallback removed. The portal stopped using its built-in offline mock dataset when the backend was unreachable; that fallback is gone in this release. If the API is down you\'ll now see error toasts instead of fake data.',
      'Next release will flip MSP_ENFORCEMENT_ENABLED=true on DEV (then PROD after validation) — that\'s when filtering becomes user-visible. No other changes expected at that point.',
    ],
  },
  {
    version: '1.56.0',
    date: '2026-05-18',
    title: 'Behind the scenes: MSP data separation plumbing (no visible change yet)',
    changes: [
      'Phase 1 of the MSP-based data separation work. The portal now has the server-side identity plumbing in place — a new /api/me endpoint returns your authoritative permissions (global admin? which MSPs do you belong to? which tenants are you allowed to see?) and the React frontend reads from it instead of inspecting your token claims directly.',
      'No visible change in this release. Enforcement is intentionally off (MSP_ENFORCEMENT_ENABLED=false) — every existing screen still works exactly as it did in 1.55.5. The follow-up release will switch enforcement on and start filtering tenants, cases, alerts, and events per MSP.',
    ],
  },
  {
    version: '1.55.5',
    date: '2026-05-15',
    title: 'Mailbox aggregation alerts: the actor shows as a UPN (not a SID) when the pipeline resolved it',
    changes: [
      'Follow-up to 1.55.4. On the Mailbox Accessed by Non-Owner rule (and any future rule the pipeline resolves a delegate UPN for), the "Accessed by:" line on the mailbox card and the per-event "actor → mailbox-owner" labels now show the resolved UPN (e.g. jane.doe@example.com) instead of the raw LogonUserSid (S-1-5-80-...). When the pipeline did not resolve a delegate UPN — for example, the "Anomalous access to other users\' mailboxes" rule where resolution is skipped — the panel still shows whatever the source event carries, even if that\'s a SID.',
    ],
  },
  {
    version: '1.55.4',
    date: '2026-05-15',
    title: 'Aggregation alerts: the actor is now visible without drilling into raw JSON',
    changes: [
      'On "Anomalous access to other users\' mailboxes" (and any future aggregation rule that lists Mailboxes Accessed), each mailbox card now shows an "Accessed by:" line in sky-blue under the mailbox owner — the actual person (or service) who touched the mailbox. Multiple distinct actors against the same mailbox are listed comma-separated.',
      'The per-event rows inside "Source Event Details" now read `time · MailItemsAccessed · actor → mailbox-owner` instead of just `… · mailbox-owner`. Same change applies on the Test Run Results page since both render through the shared module.',
      'No more drilling into Raw Event JSON to answer "who accessed which mailbox?"',
    ],
  },
  {
    version: '1.55.3',
    date: '2026-05-15',
    title: 'Detection Alerts: Risk column removed from the table',
    changes: [
      'The Risk column in the Detection Alerts table was removed — the same IP risk score (with full context: Country, Provider, Detections) is still shown in the row\'s expanded IP Intelligence panel, just a click away. The reclaimed ~70 px of width makes more room for the Rule and User columns.',
    ],
  },
  {
    version: '1.55.2',
    date: '2026-05-15',
    title: 'Behind-the-scenes refactor — finish the shared source-event module',
    changes: [
      'Internal cleanup only. Detection Alerts and Test Run Results now both render the aggregation-rule summary, the source-events blob panel, and the per-event raw JSON viewer through the same shared module that Recent Events uses. No behavior change in the portal UI — same fields, same layout, same data.',
    ],
  },
  {
    version: '1.55.1',
    date: '2026-05-15',
    title: 'Detection Alerts: resizable column widths',
    changes: [
      'The Detection Alerts table now supports drag-to-resize column widths — same behavior as Test Run Results, Recent Events, and the Coverage pages. Hover the right edge of any column header and drag. Widths persist for the session.',
    ],
  },
  {
    version: '1.55.0',
    date: '2026-05-15',
    title: 'Detection Alerts and Test Run Results: Source Event panel polish + shared rendering with Recent Events',
    changes: [
      'The "Source Event (Microsoft data)" panel on Detection Alerts and Test Run Results now uses the same compact stacked layout introduced for Recent Events — small uppercase label above the value, instead of the previous "Label : value" inline rows. Same fields, tighter readability.',
      'Detection Alerts, Test Run Results, and Recent Events now share one underlying renderer for the Source Event panel and the Raw Event JSON viewer. From this version forward, a per-log_type display change on any one of the three pages automatically lands on the other two — no more drift.',
      'No behavior changes to Detection Alerts status / Match Reason / aggregation summary / blob-events panel / Baseline Comparison / IP Intelligence / Permissions Granted / Conditional Access — those panels are unchanged.',
    ],
  },
  {
    version: '1.54.4',
    date: '2026-05-12',
    title: 'Recent Events: 4h lookback minimum + Workloads filter is now a dropdown',
    changes: [
      'The "1 hour" lookback option was removed — per-tenant volumes are low enough that 1h returned very little data. Minimum is now 4 hours.',
      'The workload picker is now a labeled dropdown ("Workloads (3 of 7)") with checkboxes inside, plus Select all / Clear all helpers — clearer that these are filter options. The button turns amber if you have zero workloads selected.',
    ],
  },
  {
    version: '1.54.3',
    date: '2026-05-12',
    title: 'Recent Events: per-workload row cap + Copy button on raw JSON + Tenant column removed',
    changes: [
      'Row cap is now applied per workload (500 each) instead of 500 total across all seven. A single noisy workload (e.g. device_security) can no longer crowd out the quieter ones — filtering by Log Type in the column header will always reveal what\'s actually there for that workload.',
      'The Truncated chip now names the specific workloads that hit their cap (e.g. "Truncated: Device Security · most-recent 500 returned") instead of a generic "first 500 rows" message.',
      'Raw Event JSON panel now has a visible "Copy" button next to its title, matching the Detection Alerts page pattern. (Was hidden behind a hover state before.)',
      'Removed the Tenant column from the table — since you have to pick a tenant before loading, every row belongs to that tenant and the column was redundant.',
    ],
  },
  {
    version: '1.54.2',
    date: '2026-05-12',
    title: 'Recent Events: tenant selection now required',
    changes: [
      'Recent Events now requires you to pick a specific tenant before loading. The "All tenants" cross-tenant scan in 1.54.0/1.54.1 was OOM-ing the API on first try (the partition layout makes cross-tenant range queries unsafe at scale). Pick a tenant, choose a lookback window, hit Refresh — Refresh is disabled until both are set.',
    ],
  },
  {
    version: '1.54.1',
    date: '2026-05-12',
    title: 'Recent Events: don\'t auto-load on page open',
    changes: [
      'Recent Events no longer auto-fetches when you open the page — the default 1h all-tenants scope was too slow as a first-impression load. Set your tenant, workloads, and lookback in the toolbar, then click Refresh to load. The load-time indicator next to Refresh will show how long the chosen scope took.',
    ],
  },
  {
    version: '1.54.0',
    date: '2026-05-12',
    title: 'New: Recent Events viewer — fast quick-look across seven workloads',
    changes: [
      'New page at Recent Events in the sidebar. Read-only view of the last 14 days of events across seven Microsoft workloads — sign-ins, directory audit, URL clicks, risk detections, risky users, security incidents, and device-security events.',
      'Pick a tenant (or leave on "All tenants"), choose which workloads to include, set the lookback window (1h / 4h / 12h / 24h / 72h), and hit Refresh. A small load-time indicator next to Refresh shows how long the fetch took so you can judge whether the current scope is too broad before widening further.',
      'Every column sorts and filters; pagination caps row count per page. Click any row to expand a structured view of the source event plus the full raw JSON (Copy button on hover).',
      'This page is purely a viewer — investigations, detection alerts, and runbook behaviour are unchanged.',
    ],
  },
  {
    version: '1.53.13',
    date: '2026-05-12',
    title: 'Tenant dropdown short names now actually populate',
    changes: [
      'The tenant filter dropdowns on Detection Alerts, Device Inventory, Device Inventory Comparison, and Intune Coverage Gap now correctly show "(shortName) friendlyName" (e.g. "(BA) Blue Alliance"). They had been silently falling back to just the friendly name because the underlying API wasn\'t including the short-name field. The API fix was deployed alongside this build.',
    ],
  },
  {
    version: '1.53.12',
    date: '2026-05-12',
    title: 'Device Inventory + Intune Coverage Gap: tenant filter formatting',
    changes: [
      'The tenant filter dropdowns on Device Inventory and Intune Coverage Gap now render each option as "(shortName) friendlyName" and sort alphabetically — same pattern that already shipped for Pipeline Health, Detection Alerts, and Device Inventory Comparison.',
    ],
  },
  {
    version: '1.53.11',
    date: '2026-05-12',
    title: 'Source Event: resourceId fallback now an amber badge',
    changes: [
      'When a graph_signin event has no friendly resource name, the resourceId fallback added in 1.53.10 now renders as an amber pill with a small "resourceId · no friendly name" hint beside it, so it stands out instead of blending into the rest of the Source Event fields.',
    ],
  },
  {
    version: '1.53.10',
    date: '2026-05-12',
    title: 'Source Event: surface resourceId when resource has no friendly name',
    changes: [
      'On graph_signin events, the Source Event panel now shows the `resourceId` (e.g. "radius") when the resource has no `resourceDisplayName`. Previously the Resource line was hidden entirely, which made it impossible to tell which auth surface an MFA failure or sign-in attempt was targeting without opening the raw JSON. Applies to both Detection Alerts and Test Run Results.',
    ],
  },
  {
    version: '1.53.9',
    date: '2026-05-12',
    title: 'Detection Alerts + Device Inventory Comparison: tenant filter formatting',
    changes: [
      'The tenant filter dropdowns on Detection Alerts and Device Inventory Comparison now render each option as "(shortName) friendlyName" (e.g. "(BA) Blue Alliance") and sort alphabetically — same pattern that landed for Pipeline Health.',
    ],
  },
  {
    version: '1.53.8',
    date: '2026-05-12',
    title: 'Baseline Comparison: now includes State',
    changes: [
      'The Baseline Comparison panel on Alerts and Test Run Results now shows a "State" row between Country and City. It compares the event\'s state to the user\'s typical state in both the 7-day and 30-day baselines, and counts toward the deviation tally in the panel header.',
    ],
  },
  {
    version: '1.53.7',
    date: '2026-05-12',
    title: 'Device Inventory Comparison: per-workload last-seen columns + column picker',
    changes: [
      'Added a Last-Seen column for each of the five workloads (M365, DRMM, Entra, Intune, BPC). Each renders in the timezone you have selected and is sortable + date-filterable.',
      'All five Last-Seen columns are hidden by default, including the M365 one that used to be visible. The default table is now more compact; enable any of them as needed.',
      'New "Columns" button next to Export CSV opens a popover where you can show / hide any column except Device Name. Your choice persists per-browser. There\'s a "Reset to defaults" link at the bottom of the popover.',
      'Hidden columns also drop from the CSV export, so exports match what you\'re looking at.',
    ],
  },
  {
    version: '1.53.6',
    date: '2026-05-12',
    title: 'Device Inventory Comparison: exclude inactive Entra and DRMM devices',
    changes: [
      'Disabled Entra directory records and deleted DRMM devices were being counted as present on the Comparison page, which inflated gap analysis and made already-decommissioned hardware look like coverage gaps. The Comparison page now skips these rows for both workloads (applies to Entra Joined, Entra Registered, and DRMM). Intune, M365, and BPC have no equivalent flag so nothing changes there.',
    ],
  },
  {
    version: '1.53.5',
    date: '2026-05-12',
    title: 'Pipeline Health: tenant filter sorted alphabetically',
    changes: [
      'Tenants in the Pipeline Health tenant filter dropdown are now sorted alphabetically (case-insensitive) by their displayed label, so it\'s easier to find a specific tenant in the list.',
    ],
  },
  {
    version: '1.53.4',
    date: '2026-05-12',
    title: 'Pipeline Health: tenant filter shows full tenant name',
    changes: [
      'The tenant filter dropdown on Pipeline Health now renders each option as "(shortName) friendlyName" — e.g. "(BA) Blue Alliance" — so it\'s easier to scan the list without memorising every short code. Per-tenant labels inside each tab still use the short name to keep the heatmaps compact.',
    ],
  },
  {
    version: '1.53.3',
    date: '2026-05-08',
    title: 'Bug fixes: lookback re-injection + session classifications now actually persist',
    changes: [
      'Fix: marking a session as Safe correctly hid most events but lookback / correlation sign-ins under that session kept reappearing in the timeline. The internal lookback re-injection step (which exists so the "Lookback hidden" toggle has events to reveal) only knew about the Safe-IP filter — it now also respects the Pre-compromise and Safe-sessions filters that landed in 1.53.0 and 1.53.2.',
      'Fix: marking a session Safe wasn\'t saved across page refreshes. The frontend was calling the case-update API correctly, but the API\'s allowlist of accepted fields didn\'t include the new `sessionClassifications` field, so the save was silently dropped. The allowlist now accepts it; existing in-memory classifications get persisted on the next save.',
    ],
  },
  {
    version: '1.53.2',
    date: '2026-05-08',
    title: 'Case Investigation: mark sessions as Safe + Pipeline Health terminology',
    changes: [
      'New per-case Safe classification for AAD session IDs — the mirror image of the compromised-session feature added in 1.53.0. When a session id is clearly unrelated to the attacker (e.g., a SaaS app doing background mailbox-bind from a Microsoft backend IPv6 under a different session GUID), you can now classify it as Safe and suppress its noise in one click rather than dismissing every event individually.',
      'Two ways to mark Safe: (1) per-row in the expanded event body — Sign-in rows get a Mark-session-safe chip next to their Session ID, and Exchange/SharePoint rows now surface their AAD Session with the same chip (previously hidden). (2) bulk in a new "Other Sessions Seen" block in the Attacker Window panel, listing every distinct non-compromised session id with its event count and classification chip.',
      'New toolbar button "Safe sessions hidden (N)" sits next to the other case toggles (standard blue). Default ON. Hides every event under a session classified Safe.',
      'Compromised sessions still win — if you mark a session Safe and then later classify a malicious IP that touches it, the auto-compromise takes priority. Your Safe classification is preserved in storage and re-applies if you un-classify the malicious IP later. The per-row chip is disabled on those rows with a tooltip explaining why.',
      'The Pre-compromise toolbar button recolored from purple to standard blue, matching the other toolbar toggles. Purple is reserved for the Attacker Window panel\'s themed sibling pill.',
      'Pipeline Health terminology: every user-visible "Breach" / "BREACH" string is now "Exceeded" / "EXCEEDED" — "Breach" is loaded language in security and shouldn\'t be reused for SLO violations.',
    ],
  },
  {
    version: '1.53.1',
    date: '2026-05-08',
    title: 'Pre-compromise toggle now in the main toolbar',
    changes: [
      'The new "pre-compromise" toggle that came with 1.53.0 was tucked into the Attacker Window panel only. It\'s now also in the main case toolbar alongside Safe IPs hidden / Post-window / Failed / DLP / etc., where you expected to find it.',
    ],
  },
  {
    version: '1.53.0',
    date: '2026-05-08',
    title: 'Case Investigation: compromised-session awareness',
    changes: [
      'Once you classify any IP as Malicious in a case, the system now tracks every session that IP touched (the sign-in SessionId or Exchange/SharePoint AADSessionId) and the time it was first compromised. Pre-compromise events under those sessions are legitimate user activity that happened *before* the attacker arrived — they\'re hidden by default via a new "pre-compromise" pill in the Attacker Window panel (sibling of "post-window"). Toggle it off if you want to see that prior activity.',
      'Post-compromise events on a stolen session are no longer hidden by the Safe IPs / Service IPs / Email-only filters, even when the source IP looks legitimate (e.g. the user\'s home IP). The reasoning: an attacker can use the stolen token from any IP, and you\'d otherwise lose visibility on the user\'s home-IP traffic during the compromise window. Each surfaced row gets a purple `session-compromised` badge so it\'s easy to spot. Your *explicit* Safe classification or row dismissal still wins — analyst intent is respected.',
      'The expanded Attacker Window panel now lists every compromised session with its first-malicious-touch timestamp.',
      'Bundles in two small follow-ups from the IP Activity Search iteration: `deleted` / `purged` operation tags are now purple instead of red (avoids clashing with the red `attacker` attribution), and the Mail-items-accessed rows in event expand-rows now drop the InternetMessageId noise and inline the size as `Subject (16161 bytes)`.',
    ],
  },
  {
    version: '1.52.7',
    date: '2026-05-08',
    title: 'IP activity search: what happened to each subject + richer Exchange event detail',
    changes: [
      'Each mail subject in the rollup now shows what happened to it — small inline tags for `read`, `sent`, `deleted`, `purged`, `attachment-download`, etc. Tagged by tone too: yellow for outbound/exfil-y actions, red for destructive (deleted/purged), neutral for reads. Same treatment on SharePoint/OneDrive file paths.',
      'Expanding an event row in the Events tab now shows a **per-log-type detail panel** instead of a raw key/value dump. For Exchange events: a card with the mail item (subject + Internet message ID + parent folder), a folder-grouped "Mail items accessed" card for MailItemsAccessed bind/sync, a red "Affected items" card for deletions, and an amber "Inbox-rule parameters" card with a persistence warning if ForwardTo / RedirectTo / MoveToFolder / DeleteMessage shows up. SharePoint and Sign-in events get their own structured renderers too.',
      'Answers the question "yes I see the subject — but did the attacker SEND it, READ it, or DELETE it?" without leaving the page.',
    ],
  },
  {
    version: '1.52.6',
    date: '2026-05-08',
    title: 'IP activity search: user-baseline tier + sectioned subject lists',
    changes: [
      'Added a fourth attribution tier — **user baseline** (green) — sourced from each affected user\'s 7-day normal sign-in IPs. Now an IP that the user normally signs in from gets tagged "likely the legitimate user" instead of falling into the catch-all "unknown" bucket. Closes the gap where every mail subject was rendering as `?` because residential / home IPs weren\'t in MS infrastructure.',
      'Mail subjects and SharePoint/OneDrive object lists are now **sectioned by attribution** with coloured headers — attacker (red) on top, MS backend (blue), user baseline (green), unknown (gray). A subject seen from multiple tiers appears in each section so cross-tier overlap (e.g. attacker AND user touched the same mail) stays visible.',
      'Tier precedence: `attacker > ms_backend > user_baseline > unknown`. The malicious IP always wins so a baseline that previously contained the attacker IP can\'t silently downgrade the classification.',
    ],
  },
  {
    version: '1.52.5',
    date: '2026-05-08',
    title: 'IP activity search: per-event attribution',
    changes: [
      'Every event now carries an attribution badge: **attacker** (red — clientIP is the malicious IP, definitive), **MS backend** (blue — Microsoft infrastructure IP, could be either side, indeterminate), or **unknown** (gray — could be the legitimate user or an attacker pivot, analyst\'s call). Sortable column in the Events tab.',
      'Each user\'s expand-row shows an attribution-split summary at the top (A:n / MS:n / ?:n), and every mail subject + SharePoint/OneDrive file path in the rollup shows the same split per row — so you can see at a glance which mails were attacker-only, which were MS-backend-only (indeterminate), and which were touched by multiple attribution classes.',
      'Source IPs panel tags each IP with its Azure Service Tag tier (MS-Backend / MS-Shared-Infra / User-Compute) plus an attribution badge.',
      'Answers the question "of these N mail subjects, which can I rule out as legitimate user activity?" without leaving the page.',
    ],
  },
  {
    version: '1.52.4',
    date: '2026-05-08',
    title: 'IP activity search: compromise-start time filter',
    changes: [
      'Bug fix: events from BEFORE the malicious IP first appeared were getting flagged as session-replay if they happened to share a session ID that the attacker later used. The session map only tells us a session was eventually touched by the bad IP, not when — so a long-running legitimate session would carry its pre-compromise events into the result.',
      'Now: the search computes a "compromise start" timestamp from the earliest event from the malicious IP itself (the first failed login attempt typically) and filters out any session-replay events earlier than that. Pre-compromise legitimate session use is no longer misattributed to the attacker.',
      'A new "Compromise window" banner above the seed-sessions panel shows the cutoff time so the filter is explicit.',
    ],
  },
  {
    version: '1.52.3',
    date: '2026-05-08',
    title: 'IP activity search: per-IP aadSessionId annotation',
    changes: [
      'The Source IPs panel for a user now shows, beneath each IP, the aadSessionId(s) that tie that IP back to the malicious IP. Makes it visible at a glance that every "other IP" in the result is connected to the same stolen session — not random unrelated traffic.',
      'A new "seed sessions" banner under the summary tiles spells out the inclusion rule: clientIP = malicious IP OR aadSessionId ∈ seed set. The seed set lists every session that touched the malicious IP.',
      'The 4 sign-in events that came directly from the malicious IP correctly show "no aadSessionId" because the M365 sign-in / graph_signin schema doesn\'t carry AppAccessContext.AADSessionId — those events match by IP, not by session.',
    ],
  },
  {
    version: '1.52.2',
    date: '2026-05-08',
    title: 'IP activity search: investigator detail on bridge events',
    changes: [
      'Each event row in the search results is now expandable. Click the chevron to see the file path that was touched (SharePoint/OneDrive), the mail subject and folder that was accessed (Exchange), the user-agent string, and any inbox-rule parameters that were changed. No more guessing what the attacker did from just an Operation name.',
      'When a user shows up in the results because of session replay (their resources got touched via someone else\'s stolen session — they didn\'t sign in from the bad IP themselves), they\'re now flagged with an amber "session-touched" badge and the row gets a subtle highlight. Click to expand and see exactly what files were touched, what mail was accessed, and an "Investigate next" checklist tailored to bridge events.',
      'Each user row also has a funnel button that filters the Events tab to just that user — useful when one user has dozens of events and you want to focus.',
      'If new inbox rules show up with parameters like ForwardTo / RedirectTo / MoveToFolder, the panel calls them out in red — that\'s a classic attacker-persistence pattern.',
    ],
  },
  {
    version: '1.52.1',
    date: '2026-05-08',
    title: 'IP activity search: enrichment lookup fix',
    changes: [
      'Fix: the cross-tenant IP search introduced in 1.52.0 was failing during the IP enrichment lookup (wrong column names on the threat-intel table). The bulk of the search — direct hits, session/token-replay pivot, and the per-tenant rollups — actually completed successfully, but the bad enrichment query was killing the whole job at the end. Now the enrichment failure degrades gracefully: the search returns its results and the header card simply shows "No IP enrichment record on file" if the IP isn\'t in the threat-intel table.',
    ],
  },
  {
    version: '1.52.0',
    date: '2026-05-08',
    title: 'Known Malicious IPs: cross-tenant activity search',
    changes: [
      'Each row on the Known Malicious IPs page now has a magnifying-glass action — click it, pick a date range (up to 90 days), and we run a search across **every tenant** for activity tied to that IP.',
      'The search also follows the attacker through token replay: any session the bad IP touched gets pivoted, so we surface activity that came from a *different* IP under the same stolen token. (This is the case where the attacker signs in from the malicious IP, then reuses the token from a clean address.)',
      'Results page shows: an IP enrichment header (country / provider / VPN / Tor / proxy / compromised flags), summary counts (direct hits, session replay, sessions touched, tenants affected, users affected), and two tabs — a per-tenant-and-user rollup, and a sortable/filterable per-event detail table. Each event is tagged "direct" (red) or "session-replay" (amber) so the two attack patterns are visually distinct.',
      'No tenant filter is applied — the goal is to know what *other* tenants and accounts the same attacker has touched.',
    ],
  },
  {
    version: '1.51.2',
    date: '2026-05-08',
    title: 'Pipeline Health Summary: per-cell sample count + p50',
    changes: [
      'Each stage cell on the Summary tab now shows three numbers stacked: the avg, the p50 (median), and the sample count (n=N). Easy to spot when a cell is being averaged over very few records.',
      'When a cell has fewer than 10 samples in the last hour, the n=N line turns amber with a ⚠ prefix — "thin data, treat the avg with skepticism."',
      'Headline footnote also lists the total sample counts feeding the headline (Stage 1 / 2 / 3) so the big number\'s reliability is visible at a glance.',
      'When the avg looks suspiciously low (or high), comparing it to the p50 in the same cell tells you whether the avg is being pulled by outliers in either direction.',
    ],
  },
  {
    version: '1.51.1',
    date: '2026-05-08',
    title: 'Pipeline Health Summary: clearer math + "min" instead of "m"',
    changes: [
      'The Summary tab\'s headline now renders the math explicitly: Microsoft + cadence + Our processing = End-to-end. The total is the largest number, with the Microsoft and "ours" pieces shown alongside it. Removes the ambiguity about whether Microsoft-side delay is included (it is).',
      'The per-workload table now shows the full equation across stages: Microsoft + cadence | + | Stage 1 (our collection) | + | Stage 2 | + | Stage 3 | = | Total. Gray for Microsoft, blue for our pipeline.',
      'Latency values now use "min" instead of "m" — was easy to misread "13.7m" as 13.7 million. Now reads as "13.7 min" everywhere.',
    ],
  },
  {
    version: '1.51.0',
    date: '2026-05-08',
    title: 'Pipeline Health: new Summary tab, v2 freshness metrics, polish',
    changes: [
      'New "Summary" tab is now the default landing on Admin → Pipeline Health. Shows the average end-to-end pipeline time as one big number (sum of average latencies at all 3 stages, last hour, daily-snapshot workloads excluded), with a per-workload breakdown table beneath.',
      'Stage 1 (Collection) now reports against the new SLO metric the data team shipped today: "our processing time" only — Microsoft\'s indexing delay and our deliberate poll cadence are reported separately so it\'s clear what\'s ours vs theirs. The current page shows a stacked bar per workload: gray = Microsoft + cadence, blue = our processing.',
      'Tenant filter now has a "Showing data for: X" chip on every section. When you change the filter, the chip turns amber with "→ updating to Y…" until the new data arrives, so it\'s never ambiguous which tenant the numbers are for.',
      'Subtle (i) icon at the top right of each section\'s status grid opens a popover with definitions for p50, p95, p99, avg, and other terms used on that tab.',
      'Bronze → Silver and Silver → Delta "Days-until-breach projection" tables now show values in minutes (was seconds). Slope shown in min/day.',
      'Silver → Delta page: workloads that emit ~once-per-day (ca_policy, secure_score) now show a sky-blue "DAILY (no recent)" badge instead of the alarming "no data" badge between runs.',
      'Silver → Delta "Top tenants by miss count" now displays the tenant name alongside the tenant ID.',
    ],
  },
  {
    version: '1.50.1',
    date: '2026-05-08',
    title: 'Pipeline Health: Bronze → Silver tab now loads',
    changes: [
      'Fixed a 400 Bad Request error on the Bronze → Silver tab. The query was referencing a table name that the App Insights data plane API does not expose for this workspace; switched to the supported alias. No retention or accuracy change.',
    ],
  },
  {
    version: '1.50.0',
    date: '2026-05-08',
    title: 'New: Pipeline Health dashboard for admins',
    changes: [
      'A new admin-only page at Admin → Pipeline Health surfaces the freshness of every pipeline stage end-to-end: Collection (M365 → bronze-staging, 30-minute freshness goal), Bronze → Silver ETL (bronze → silver-staging, 10-minute p95 goal), and Silver → Delta append (silver-staging → unified table, 10-minute p95 goal). Each stage has its own tab.',
      'Each tab shows: current status per workload (last hour aggregates), per-tenant heatmap, 14- to 30-day daily trend with the SLO threshold drawn as a dashed reference line, and a "days until breach" projection table powered by linear regression. Workloads about to breach the SLO sort to the top.',
      'Stage 3 (Silver → Delta) replaces the email alerts the data team had to disable because the high-volume workloads were generating noisy breach emails — the dashboard is now the user-facing surface for that telemetry.',
      'Tenant filter applies across all three tabs so you can see how one tenant moves through the whole pipeline. The dashboard auto-refreshes every 30 seconds; switching tabs pauses polling on hidden tabs.',
      'Available only to administrators in this release; a smaller "data is N minutes behind" freshness signal for all users is planned next.',
    ],
  },
  {
    version: '1.49.1',
    date: '2026-05-07',
    title: 'Rule editor: save error fixed for pipeline-created rules',
    changes: [
      'Saving an edit on rules that were created by the detection pipeline (rather than the portal) failed with a "cooldown_minutes cannot be found on this object" error. Affected the new Device Code Phishing rule and any future pipeline-shipped rules. The save path now adds missing columns rather than failing on them.',
    ],
  },
  {
    version: '1.49.0',
    date: '2026-05-07',
    title: 'Rule editor: JSON path match conditions are now clearly labelled',
    changes: [
      'Detection rules that match against raw event JSON fields (e.g. `$.ExtendedProperties contains "Cmsi:Cmsi"`) now show an amber note beneath each such condition in the rule editor: "JSON path — matched as a substring of the raw event field. Case-sensitive." This distinguishes JSON path conditions from standard top-level column filters and clarifies the pipeline\'s behaviour (substring match, not JSONPath evaluation).',
      'Common JSON path fields (`$.ExtendedProperties`, `$.AppAccessContext.AADSessionId`, `$.ClientInfoString`, `$.ClientUserAgent`) are now available as datalist suggestions in the Match Conditions field input when building `log_type_match` rules.',
      'PROD-mode rule view: match conditions are now shown as a structured table before the raw JSON, with "JSON path" badges on `$.`-prefixed fields.',
    ],
  },
  {
    version: '1.48.9',
    date: '2026-05-07',
    title: 'Aggregation alerts: delegate identity now shown when resolved',
    changes: [
      'The detection pipeline now resolves the actual delegate UPN (who accessed the mailbox) from the AAD session ID and includes it in the alert. When resolved, the expanded row\'s Aggregation Summary panel shows a new "Delegate" row in blue with the delegate\'s UPN (copy-able). The User column also promotes to show the delegate directly as the actor.',
      'When the session can\'t be resolved (delegate signed in outside the lookback window), the panel shows "Unresolved" in amber along with the AAD session IDs for manual investigation pivot.',
      'The "(owner)" fallback remains for all cases where the delegate is not resolved — the mailbox owner is still shown as the alert trigger context.',
    ],
  },
  {
    version: '1.48.8',
    date: '2026-05-07',
    title: 'Aggregation alerts: row detail now shows "who it was"',
    changes: [
      'Alerts from rules like "Mailbox Accessed by Non-Owner" now show the mailbox owner clearly in the expanded row detail. Previously the User column displayed an internal SID or "App: Microsoft Office" because the underlying source event records the delegate as a Windows logon SID rather than a person. The expanded row now leads with an Aggregation Summary panel showing Mailbox Owner (amber, copy-able), Detection Window, Access Count, Folders, Source IPs, and Client Info.',
      'The User column also falls back to the mailbox owner with a "(owner)" suffix on these alerts so it\'s clear the displayed identity is the victim being accessed, not the actor doing the accessing.',
      'Applies to both the Alerts page and the Test Run Results page. The panel is generic — any future detection rule that emits the same detection-window summary fields gets the same row-detail treatment without code changes.',
    ],
  },
  {
    version: '1.48.7',
    date: '2026-05-06',
    title: 'Fix: country picker now shows on the New Country rule',
    changes: [
      'Fixed a regression in v1.48.5 — the "Suppressed Event Countries" picker was hidden on the actual "Sign-in from New Country" rule because the picker was gated on the wrong rule type. The picker now appears at the top of the Parameters section for any rule whose Python function is `rule_new_country_signin`, regardless of which rule-type form is rendered below it.',
    ],
  },
  {
    version: '1.48.6',
    date: '2026-05-06',
    title: 'Coverage: DRMM "Last seen" now treats online devices as fresh',
    changes: [
      'Fixed a bug where actively-online DRMM devices were sometimes shown as stale (>30 days) on the Device Inventory Comparison page, because the DRMM agent\'s self-reported `lastSeen` timestamp can lag the snapshot by days or weeks for some sites. The API now uses the snapshot date itself as the effective last-seen for any DRMM device whose `online` flag is true at snapshot time. Offline devices continue to use the agent-reported timestamp.',
    ],
  },
  {
    version: '1.48.5',
    date: '2026-05-06',
    title: 'New-country rule: country picker for "always suppress" list',
    changes: [
      'The "Sign-in from New Country" detection rule now has a country picker in the rule editor. Add ISO 2-letter codes (e.g. US, GB) to suppress new-country alerts whose event country is in the list, regardless of whether the user has a baseline. Most common configuration is `US` for tenants where US sign-ins should never alert on this rule.',
      'Picker only appears for `rule_new_country_signin`. Other rules continue to use the raw Parameters JSON editor unchanged.',
      'If "Override if Malicious IP" is enabled on the rule, threat-intel hits still fire even when their country is in the suppression list — threat intel takes precedence.',
    ],
  },
  {
    version: '1.48.4',
    date: '2026-05-06',
    title: 'Coverage Comparison: Entra column re-order + tighter tooltips',
    changes: [
      '"Entra Registered" now sits to the left of "Entra Joined" — column header, table cells, CSV export, and the Source/Target dropdown all reordered.',
      'Hover tooltips on the two Entra cells no longer repeat the column name (the header already says that). Present-cells read e.g. "AzureAd: lastSignIn=…"; absent-cells still surface the trustType + last sign-in date for cross-referencing.',
    ],
  },
  {
    version: '1.48.3',
    date: '2026-05-06',
    title: 'Coverage Comparison: M365 staleness check fixed',
    changes: [
      'Fixed a bug where every M365 device was being treated as stale, so the "Seen By M365" column showed red dots everywhere, the "Yes" filter on that column matched zero devices, and the In-# count under-counted M365 coverage. The pipeline renamed an internal column and the API was still reading the old name. Affects all tenants on snapshots after the rename.',
    ],
  },
  {
    version: '1.48.2',
    date: '2026-05-06',
    title: 'Coverage Comparison: "Hide stale" toggle now shrinks the device list',
    changes: [
      'Flipping "Hide stale (>30d)" off and on now actually changes how many devices appear in the table — previously only the per-row dots and "In #" count moved, but the device list itself stayed identical. Stale-everywhere devices (no fresh activity in any workload within 30 days of the snapshot) now drop out when the toggle is on.',
    ],
  },
  {
    version: '1.48.1',
    date: '2026-05-06',
    title: '"Skip if Entra Joined" tooltip shortened',
    changes: [
      'Tooltip on the new "Skip if Entra Joined" toggle now reads "Suppress alerts for Azure AD-joined and Hybrid Azure AD-joined devices." — same tone as the other suppression toggles. No behavior change.',
    ],
  },
  {
    version: '1.48.0',
    date: '2026-05-06',
    title: 'New rule-suppression toggle "Skip if Entra Joined" + Coverage Comparison Entra column split',
    changes: [
      'New detection-rule suppression toggle "Skip if Entra Joined" — appears above "Skip if Intune Managed" in the rule editor. Suppresses alerts when the event\'s device is verified-present in the Entra directory as Azure AD-joined or Hybrid Azure AD-joined. Stronger than "device trusted" because it verifies the deviceId against the directory rather than trusting the event payload — defends against spoofed trustType claims. Excludes BYOD-registered devices.',
      'Coverage Inventory Comparison: the single "Entra" column has been split into "Entra Joined" and "Entra Registered" — so analysts can tell at a glance whether a device is corporate-managed (Joined / Hybrid) or BYOD (Workplace-registered). The In-# coverage count is unchanged: Joined and Registered are mutually exclusive sub-states of the same workload (max stays 5).',
      'When a device is Entra Joined, the "Entra Registered" cell shows a faint green dot rather than red — Joined is strictly stronger than Registered, so a red dot would have been a false alarm. A BYOD-registered-only device still shows red in the Joined column because that IS a real coverage gap (no corporate management).',
      'Source/Target gap analysis splits accordingly: pick "Entra Joined not in Intune" to find corporate-managed devices missing MDM, or "Entra Registered not in Intune" to find BYOD devices missing MDM. CSV export gains two columns.',
    ],
  },
  {
    version: '1.47.10',
    date: '2026-05-05',
    title: 'Coverage Comparison: AndroidForWork now treated as Android-family for DRMM/BPC',
    changes: [
      'AndroidForWork and AndroidEnterprise OS values now correctly show "—" for DRMM and BPC (same as plain Android), since none of these mobile variants run those endpoint agents.',
    ],
  },
  {
    version: '1.47.9',
    date: '2026-05-05',
    title: 'Coverage Comparison: smarter handling of mobile + VMware + Windows OS variants',
    changes: [
      'DRMM and BPC now show "—" (not a red dot) on iOS, iPadOS, Android, and VMware devices, since neither workload runs on those OSes. The In-# count and Source/Target gap analysis also skip those workloads on those devices, so an iOS device with M365 + Entra + Intune coverage reads 3/3 instead of 3/5.',
      'OS multi-select filter is now prefix-aware: selecting "Windows" matches Windows, Windows 10, Windows 11, etc. — picking a specific value still matches only that one. Case-insensitive.',
      'CSV export shows "N/A" for not-applicable workloads.',
    ],
  },
  {
    version: '1.47.8',
    date: '2026-05-05',
    title: 'Coverage Comparison: local time by default + TZ picker',
    changes: [
      '"Last Seen by M365" column now shows date + time, in your browser\'s local timezone by default.',
      'New time-zone picker on the filter bar with Local, UTC, and the six US zones. Your selection persists and is shared with the Alerts and Timeline pages.',
      'Cell tooltips also show formatted date+time in the selected timezone.',
    ],
  },
  {
    version: '1.47.7',
    date: '2026-05-05',
    title: 'Coverage Comparison: staleness filter toggle + tooltip refinements',
    changes: [
      'New "Hide stale (>30d)" toggle in the filter bar — on by default. Switch off to see raw inventory presence regardless of lastSeen age (reverts to pre-v1.47.6 behaviour).',
      'Stale tooltips now read "Stale: last seen 2026-03-01" instead of including a "more than 30 days before snapshot" suffix. The date alone answers the implicit question.',
      'Fixed: the loading spinner next to the Tenant picker no longer overlaps the Snapshot Date label.',
    ],
  },
  {
    version: '1.47.6',
    date: '2026-05-05',
    title: 'Coverage Comparison: stale workloads now show as missing',
    changes: [
      'Per-workload presence dot is now fresh-aware: a workload is shown as missing (red dot) if the device\'s last-seen for that workload is more than 30 days before the snapshot date — even if the device is in that workload\'s inventory.',
      'Stale propagates: the In-# count drops, Source/Target gap analysis treats stale as "not in", and CSV exports "No" for stale workloads.',
      'Hover the red dot to see whether it\'s genuinely missing or just stale (with the actual last-seen timestamp).',
      'Threshold is fixed at 30 days. The cutoff anchors to the snapshot date you picked, not today\'s date.',
    ],
  },
  {
    version: '1.47.5',
    date: '2026-05-05',
    title: 'Coverage Comparison: "Last User" now reflects M365 sign-ins only',
    changes: [
      'Renamed "Last User" to "Last User by M365" — same single-source scoping as "Last Seen by M365".',
      'Previously fell back to Intune\'s primary-assigned user and DRMM\'s last-logged-in user, both of which can differ from the actual last M365 signer.',
      'Devices outside the M365 sign-in inventory will show blank in this column.',
    ],
  },
  {
    version: '1.47.4',
    date: '2026-05-05',
    title: 'Coverage Comparison: "Last Seen" now reflects M365 sign-ins only',
    changes: [
      'Renamed "Latest Seen" to "Last Seen by M365" and changed it to reflect only the M365 sign-in-derived timestamp.',
      'Previously the column merged user-activity timestamps with agent check-ins from DRMM / Intune / BPC, which made the value confusing (an agent timestamp on a logged-out laptop is not the same as a user signing in).',
      'Devices not in the M365 sign-in inventory will now show blank in this column.',
    ],
  },
  {
    version: '1.47.3',
    date: '2026-05-05',
    title: 'Coverage Comparison: column reorder + OS filter dedup',
    changes: [
      'Columns now read: Device Name → Seen By M365 → DRMM → Entra → Intune → BPC → In # → OS → Latest Seen → Last User. Source/Target dropdowns, load-time strip, and CSV export reordered to match.',
      'OS filter no longer shows duplicate buckets for the same OS — values across the five sources are normalized to a single display form (e.g. "Windows" / "windows" / "WINDOWS" collapse to "Windows"; "OSX" / "MacOS" → "macOS").',
    ],
  },
  {
    version: '1.47.2',
    date: '2026-05-05',
    title: 'Coverage: "M365" workload renamed to "Seen By M365"',
    changes: [
      'In the Coverage Analysis pages, the "M365" workload is now labelled "Seen By M365" — clarifies that this is the sign-in-derived inventory (devices that have actually signed in), not "registered in the Entra ID directory" which is its own column.',
      'Applied on the Comparison page column, Source/Target dropdowns, CSV export, and load-time strip; and on the Device Inventory page workload toggle button.',
    ],
  },
  {
    version: '1.47.1',
    date: '2026-05-05',
    title: 'Device Inventory Comparison now includes Entra and Intune',
    changes: [
      'The Comparison page (Coverage → Device Inventory Comparison) now joins five workloads: Entra, Intune, M365, DRMM, and BPC.',
      'Two new presence columns ("Entra", "Intune") appear left of M365.',
      'Source / Target gap dropdowns gain Entra and Intune — e.g. pick "Entra not in Intune" to list directory-registered devices that aren\'t enrolled in MDM.',
      'Coverage count badge now reads up to 5/5 (or 4/4 when the tenant has no BPC mapping).',
      'CSV export includes the two new columns.',
    ],
  },
  {
    version: '1.47.0',
    date: '2026-05-05',
    title: 'New "Intune Coverage Gap" report under Coverage Analysis',
    changes: [
      'New page (Coverage → Intune Coverage Gap) shows every M365-active user who has no Intune-enrolled device on the same snapshot date.',
      'Required tenant picker; one row per user with their most-recent M365 device, OS, compliance state, and trust type. Resizable / sortable / per-column filterable, with CSV export.',
      'Amber banner when the selected tenant has no Intune device data — distinguishes tenants not yet on the DeviceManagementManagedDevices.Read.All re-consent from genuine zero-enrollment.',
      'Green confirmation banner when every M365-active user has an Intune device on file.',
    ],
  },
  {
    version: '1.46.4',
    date: '2026-05-05',
    title: '"Intune Managed" shown before "Intune Compliant Device" everywhere',
    changes: [
      'In the alert and test-run expansion suppression-applied lists and Baseline Comparison panels, "Intune Managed device" now precedes "Intune Compliant Device" — matching the rule editor order.',
    ],
  },
  {
    version: '1.46.3',
    date: '2026-05-05',
    title: 'Device Posture panel now appears on non-sign-in alerts',
    changes: [
      'Exchange, SharePoint, DLP, and similar non-sign-in alerts now also show the Device Posture panel when a correlated sign-in is attached.',
      'The panel resolves the device through the correlated sign-in\'s deviceDetail.deviceId, so an inbox-rule alert (or any action that came from a session) shows the directory + MDM state of the device that ran it.',
    ],
  },
  {
    version: '1.46.2',
    date: '2026-05-05',
    title: 'Device Posture panel on alerts',
    changes: [
      'Alerts expansion (and Test Run Results expansion) now includes a collapsible "Device Posture (Directory + MDM)" panel showing what Entra and Intune know about the device that triggered the alert.',
      'When the alert\'s device is registered: shows trust type, account enabled, last sign-in, owner, OS, and profile type.',
      'When the device is also Intune-enrolled (Defender-tier tenants): shows compliance state, primary user, last sync, encryption, jail-broken, and threat state side-by-side with the directory data.',
      'When the device is unknown to the directory: flags it as a likely AiTM/proxy session or unregistered BYOD.',
    ],
  },
  {
    version: '1.46.1',
    date: '2026-05-05',
    title: 'Suppression toggles reordered',
    changes: [
      '"Skip if Intune Managed" now appears at the top of the Suppression block in the rule editor, with "Skip if Intune Compliant Device" directly below it.',
    ],
  },
  {
    version: '1.46.0',
    date: '2026-05-05',
    title: 'New "Skip if Intune Managed" rule suppression',
    changes: [
      'Rule editor: new "Skip if Intune Managed" toggle alongside the existing suppression options. When on, alerts are dropped for users who have any Intune-enrolled device on file — compliant or not.',
      'Complementary to "Skip if Intune Compliant Device" — catches users whose device drifted out of compliance but is still managed.',
      'The applied suppression is shown on every alert and on every test-run result that triggered it.',
    ],
  },
  {
    version: '1.45.13',
    date: '2026-05-04',
    title: 'DRMM device inventory restored',
    changes: [
      'Coverage → Device Inventory and Comparison pages now show DRMM devices again after the pipeline team migrated to a new table on 2026-05-02.',
    ],
  },
  {
    version: '1.45.12',
    date: '2026-05-04',
    title: 'Application name shown in alert Source Event',
    changes: [
      'Sign-in alerts now display the resolved application name (e.g., "Ramp - Credit Cards") in the Source Event panel.',
      'Falls back to the raw Application ID when the name is not yet resolved.',
    ],
  },
  {
    version: '1.45.11',
    date: '2026-04-30',
    title: 'Rename "Consent" button to "Consent Link"',
    changes: [
      'Per-tenant button on the M365 Tenants page renamed from "Consent" to "Consent Link" for clarity.',
    ],
  },
  {
    version: '1.45.10',
    date: '2026-04-30',
    title: 'Per-tenant admin consent URL',
    changes: [
      'Admin \u2192 M365 Tenants now has a "Consent" button per tenant that opens a modal with the tenant-specific admin consent URL ready to copy.',
      'Useful when new permissions are added to the M365Signal app and existing tenants need their Global Admin to re-approve.',
    ],
  },
  {
    version: '1.45.9',
    date: '2026-04-30',
    title: 'Home page card order',
    changes: [
      'Alerts card now sits to the left of Investigations on the home page (Alerts \u2192 Investigations \u2192 Coverage Analysis).',
    ],
  },
  {
    version: '1.45.8',
    date: '2026-04-30',
    title: '"New Case" moved under Cases sub-nav',
    changes: [
      '"New Case" is now a sub-item under Cases instead of a top-level sidebar entry, consistent with the rest of the nav structure.',
    ],
  },
  {
    version: '1.45.7',
    date: '2026-04-30',
    title: 'Sidebar sub-nav consistency',
    changes: [
      'Cases, Alerts, and Coverage now show their sub-items indented directly under the parent (same pattern as Tenants).',
      'No more separate "Investigations" / "Alerting" / "Coverage" section headers \u2014 the related links live under their parent.',
    ],
  },
  {
    version: '1.45.6',
    date: '2026-04-30',
    title: 'Device Inventory BPC tab respects tenant selection',
    changes: [
      'Picking a specific M365 tenant on Device Inventory now scopes the BPC tab to only that tenant\u2019s BPC devices (via the mapping configured in Admin \u2192 Tenants \u2192 BPC).',
      'If the picked tenant has no BPC mapping, the BPC tab shows 0 devices and a banner explaining how to set the mapping.',
      'Picking "All tenants" still shows every BPC device (existing behavior).',
    ],
  },
  {
    version: '1.45.5',
    date: '2026-04-30',
    title: 'BPC Tenants page \u2014 column reorder + filters + resize',
    changes: [
      'BPC Account Name is now the leftmost column on the BlackPoint Cyber MDR Tenants page.',
      'Per-column filters (text, select, date) added \u2014 same UX as the device pages.',
      'Columns are now resizable. Notes is a real column instead of hidden behind a tooltip.',
    ],
  },
  {
    version: '1.45.4',
    date: '2026-04-30',
    title: 'Fix BPC tenant edit \u2014 audit field error',
    changes: [
      'Saving a BPC tenant edit no longer fails with "Cannot bind argument to parameter \u0027UpdatedBy\u0027" \u2014 the API was reading the wrong identity field for the audit stamp.',
    ],
  },
  {
    version: '1.45.3',
    date: '2026-04-30',
    title: 'Fix BPC tenant edit \u2014 mapping saves correctly',
    changes: [
      'Saving an M365 tenant mapping (or any field edit) on the BlackPoint Cyber MDR Tenants page now works. The previous build returned "No editable fields supplied" due to a request-body parsing mismatch in the UpdateBpcTenant API.',
    ],
  },
  {
    version: '1.45.2',
    date: '2026-04-30',
    title: 'Coverage Analysis polish',
    changes: [
      'Home page now has a Coverage Analysis card alongside Investigations and Alerts.',
      'Multi-OS filtering on all device pages \u2014 select Windows + MacOS + iOS at once via the new checkbox popover filter.',
      'Primary "Coverage" nav no longer stays highlighted while on Device Inventory or Comparison sub-pages.',
      'Comparison page tenant dropdown shows a clear loading state during function app cold-start.',
    ],
  },
  {
    version: '1.45.1',
    date: '2026-04-30',
    title: 'BPC Tenants page \u2014 schema fix + Comparison page navigation fix',
    changes: [
      'BPC Tenants page now reads/writes the actual columns in BpcAccounts.csv (BpcTenantName, BpcAccountName, KeyVaultSecretName) so editing preserves pipeline data correctly.',
      'Drawer exposes all account fields including the Key Vault secret name.',
      'Device Inventory Comparison no longer redirects to home if BPC lookup hits a transient auth blip \u2014 the lookup is deferred until Load Devices is clicked.',
    ],
  },
  {
    version: '1.45.0',
    date: '2026-04-30',
    title: 'Admin Tenants hub + BlackPoint Cyber MDR Tenants management',
    changes: [
      'Admin → Tenants is now a hub with two cards: M365 Tenants (existing) and BlackPoint Cyber MDR Tenants (new).',
      'Admins can add, edit, and delete BPC tenants and map them to M365 tenants for use in Device Inventory Comparison.',
      'Once a BPC tenant is mapped, the Device Inventory Comparison page automatically loads BPC devices for that tenant — the "no mapping" banner goes away.',
    ],
  },
  {
    version: '1.44.11',
    date: '2026-04-30',
    title: 'Device Inventory Comparison page',
    changes: [
      'New page at Coverage Analysis → Device Inventory Comparison joins M365 + DRMM device lists by hostname so coverage gaps stand out at a glance.',
      'Source/Target dropdowns isolate "devices in M365 not in DRMM" style remediation lists for handoff to IT.',
      'Export CSV button downloads the current view (filtered or not) for offline triage.',
      'BPC integration shows a banner until BlackPoint Cyber tenants are mapped to M365 tenants in the upcoming admin page.',
    ],
  },
  {
    version: '1.44.10',
    date: '2026-04-30',
    title: 'Coverage Analysis hub + Device Inventory rename',
    changes: [
      '"Coverage" in the sidebar now lands on a hub page with cards for Device Inventory and Device Inventory Comparison (comparison page coming next).',
      'The existing inventory page is now titled "Device Inventory" to match the sidebar label.',
    ],
  },
  {
    version: '1.44.9',
    date: '2026-04-30',
    title: 'Coverage Analysis — BPC device inventory',
    changes: [
      'BlackPoint Cyber agent devices now available as a third workload tab alongside M365 and DRMM.',
      'Shows endpoint posture: OS end-of-life status, firewall, Windows Defender, disk encryption, and agent version.',
      'EOL devices highlighted in red; missing security controls shown as red indicators.',
    ],
  },
  {
    version: '1.44.8',
    date: '2026-04-29',
    title: 'Coverage Analysis — M365 device inventory now loads',
    changes: [
      'M365 device inventory uses a fast server-side snapshot query (same approach as DRMM) — no more timeouts.',
      'Snapshot date column added to M365 device table.',
    ],
  },
  {
    version: '1.44.7',
    date: '2026-04-29',
    title: 'Coverage Analysis — M365 query performance fix',
    changes: [
      'M365 device inventory now filters by snapshot date server-side, preventing timeout errors on large tables.',
    ],
  },
  {
    version: '1.44.6',
    date: '2026-04-29',
    title: 'Coverage Analysis — snapshot date applies to both workloads',
    changes: [
      'M365 and DRMM both use the snapshot date picker; M365 will show devices once the pipeline adds collection_date to the inventory table.',
    ],
  },
  {
    version: '1.44.5',
    date: '2026-04-29',
    title: 'Coverage Analysis — fast DRMM query + smart date default',
    changes: [
      'DRMM snapshot queries now use a server-side RowKey range filter — dramatically faster than a full table scan.',
      'Default snapshot date is today; if today has no data, automatically falls back to yesterday and updates the date picker.',
    ],
  },
  {
    version: '1.44.4',
    date: '2026-04-29',
    title: 'Coverage Analysis — parallel workload loading',
    changes: [
      '"Load Devices" now fetches M365 and DRMM data simultaneously — no need to reload after switching workloads.',
      'Workload buttons (M365 / DRMM) now act as view filters over already-loaded data, showing live device counts.',
      'Load timing shown in the filter bar after each load (e.g. "M365: 1.2s · 245 devices | DRMM: 3.4s · 189 devices").',
    ],
  },
  {
    version: '1.44.3',
    date: '2026-04-29',
    title: 'Rename generic device labels to Intune-qualified names',
    changes: [
      'Column headers, panel fields, and suppression labels previously showing "Compliant" or "Managed" now specify "Intune" to distinguish from DRMM and other management tools.',
      'Updated across DevicesTab, IPAddressTable, TimelineView, Baseline Comparison panels (Alerts + Test Run Results), and alert rule suppression toggles.',
    ],
  },
  {
    version: '1.44.2',
    date: '2026-04-29',
    title: 'Coverage Analysis — per-column filters',
    changes: [
      'Each column in the M365 and DRMM tables now has its own filter directly under the header — text inputs, dropdowns, yes/no selects, and a ≥/≤ date picker for Last Seen.',
      'OS Type, Trust Type, and DRMM Instance dropdowns are populated automatically from the loaded data.',
      'Added "Clear N filters" button in the table toolbar when any filters are active.',
      'Added BPC Devices workload placeholder (BlackPoint Cyber MDR — coming soon).',
    ],
  },
  {
    version: '1.44.1',
    date: '2026-04-29',
    title: 'Coverage Analysis — UX improvements',
    changes: [
      'Load Devices button repositioned inline with other controls.',
      'Both M365 and DRMM tables now have resizable columns — drag the divider on any column header.',
      'Click any column header to sort results ascending or descending.',
      'Last Seen filter added below the filter bar once results are loaded — choose "on or after" or "on or before" a date to narrow results.',
      'Snapshot date picker now applies to both M365 and DRMM workloads.',
      'Pagination default corrected to 10 rows to match the rows-per-page selector.',
    ],
  },
  {
    version: '1.44.0',
    date: '2026-04-29',
    title: 'Coverage Analysis — Device Inventory',
    changes: [
      'New "Coverage" section in the sidebar with a Devices page for cross-tenant device inventory analysis.',
      'M365 workload: shows all named devices seen authenticating in M365 (from graph_signin events) with OS type, trust type, compliance state, managed state, last seen date, and last user.',
      'DRMM workload: shows devices from the DRMM agent inventory, deduplicated to the most recent daily snapshot per device, with hostname, OS, DRMM instance, site, online status, and last seen date.',
      'Controls: tenant picker (all tenants by default), Last Seen Since presets (7d / 30d / 90d / All, default 30 days), and workload toggle (M365 / DRMM).',
      'Results are searchable by device name and last user, with pagination.',
    ],
  },
  {
    version: '1.43.3',
    date: '2026-04-29',
    title: 'Alerts — improve Baseline Comparison readability',
    changes: [
      'Field names and column header text in the Baseline Comparison panel are now lighter gray (text-gray-400) and the header row is slightly larger, improving readability on dark backgrounds.',
    ],
  },
  {
    version: '1.43.2',
    date: '2026-04-29',
    title: 'Alerts — fix OS baseline comparison false mismatch',
    changes: [
      'The Baseline Comparison OS row was incorrectly showing red for Windows devices because Microsoft reports the OS as "Windows10" in sign-in events while the baseline stores it as "Windows". Version suffixes are now stripped before comparing, so "Windows10" correctly matches "Windows".',
    ],
  },
  {
    version: '1.43.1',
    date: '2026-04-29',
    title: 'Alerts — clean up redundant fields now covered by Baseline Comparison',
    changes: [
      'OS, Intune Compliant, and Intune Managed removed from the Correlated Sign-in panel — all three are now shown with baseline comparison context directly in the Baseline Comparison section.',
      'Removed the "No deviation — profile only" / "No baseline check" messages from Anomaly/IDs — the Baseline Comparison panel makes these states visually clear.',
      'Removed the rule name and event ID display from Anomaly/IDs.',
    ],
  },
  {
    version: '1.43.0',
    date: '2026-04-29',
    title: 'Alerts — Baseline Comparison panel (7-day + 30-day side by side)',
    changes: [
      'Expanded alerts now show a collapsible "Baseline Comparison" section after IP Intelligence. It displays a table comparing the current event\'s Country, City, IP, Hour of day, OS, and Device compliance/management against both the 7-day and 30-day user baselines side by side.',
      'Each baseline cell is color-coded: green if the event value matches the user\'s typical pattern, red if it falls outside, and neutral gray when no baseline data is available.',
      'The collapsed section header shows a live count of red rows ("Baseline Comparison · 2 deviations") in amber, so you can see at a glance how anomalous the sign-in is without expanding.',
      'The old narrow hover-tooltip on the Anomaly/IDs header has been removed — the new panel replaces it with a more scannable, always-accessible view.',
      'Rule editor: the Rule Name field is now at the top of the Edit Rule Drawer, making it immediately visible and editable.',
    ],
  },
  {
    version: '1.42.6',
    date: '2026-04-28',
    title: 'Alerts — show Device row even when name is unknown',
    changes: [
      'For sign-ins from unmanaged or non-Entra-joined devices (where Microsoft does not provide a device name), the Device row now still renders, marked as "Unknown" in muted italic. Previously the row was hidden, which made it ambiguous whether the device was unknown or simply not displayed. Surfacing "Device: Unknown" explicitly is itself a triage signal — typically the user signed in from a personal browser, an unmanaged endpoint, or a Cloud PC.',
    ],
  },
  {
    version: '1.42.5',
    date: '2026-04-28',
    title: 'Alerts — surface Device name in expanded sign-in details',
    changes: [
      'Expanded alerts (and Test Run Results) for graph_signin events now show the Device name (e.g. "Trevor\'s MacBook Air") inline with Application / Resource / Client App in the Source Event panel — no need to scroll down to the Correlated Sign-in panel to see it.',
      'Especially useful for the "Sign-in from User with No DRMM-Managed Devices" rule, where knowing whether the sign-in came from a known device vs a brand-new one is the primary triage signal.',
    ],
  },
  {
    version: '1.42.4',
    date: '2026-04-28',
    title: 'Rule editor — fix Config Function rule save',
    changes: [
      'Editing a Config Function rule (e.g. "Mailbox Accessed by Non-Owner") no longer fails on save with "Log type is required." These rules use pipeline-defined parameters (mailbox lists, accessor allowlists, operation filters) that don\'t fit the Log Type Match form, so the editor now shows them in a JSON textarea instead.',
      'Pipeline-defined fields (`vip_mailboxes`, `tenant_filter`, `whitelisted_accessors`, `operations`, `result_statuses`, etc.) are now preserved across portal saves instead of being silently dropped. The Python function name is also shown in the header for clarity.',
    ],
  },
  {
    version: '1.42.3',
    date: '2026-04-28',
    title: 'Rule editor — fix "operation_baseline_deviation" save',
    changes: [
      'Editing rules of type Op + Baseline (e.g. "Suspicious Inbox Rule Creation") no longer fails on save with "Operation is required." The form now reads the rule\'s plural `operations` array (the shape the detection pipeline actually consumes), and the textbox accepts comma-separated operations for rules that match more than one.',
      'Custom parameters set by the pipeline team (such as `office_ip_threshold` on rule-002) are now preserved across portal saves instead of being silently dropped.',
    ],
  },
  {
    version: '1.42.2',
    date: '2026-04-28',
    title: 'Rule editor — "Alert if a Known Malicious IP" override',
    changes: [
      'New suppression option in the rule editor: "Alert if a Known Malicious IP". When enabled, the rule fires on a match even if another suppression option (Skip if Compliant Device, DRMM Match, Tenant/User Baseline, CTAP Inbound Trust) would normally have dropped it — as long as the source IP is on the analyst-curated Known Malicious IPs list.',
      'Closes the failure mode where an attacker re-uses a victim\'s previously-seen IP and the user-baseline suppression silently drops the credential-compromise alert. Set this on rules where a malicious-IP match should always reach the analyst.',
      'Tenant/provider scoping options still apply — this only bypasses suppression, not scoping. Rule scope (Included Tenants / Excluded Tenants / Excluded Providers) is honored as before.',
    ],
  },
  {
    version: '1.42.1',
    date: '2026-04-28',
    title: 'Sidebar — context-aware Investigations / Alerting groups',
    changes: [
      'The single "Settings" group has been split into two: "Investigations" (Service IPs, Known Apps, Hidden Ops) and "Alerting" (AiTM Trusted Domains, AiTM Trusted Pairs, Known Malicious IPs).',
      'On Cases pages you only see Investigations; on Alerts / Alert Rules / Test Results pages you only see Alerting. On Home, Tenants, and Release Notes both groups are visible.',
    ],
  },
  {
    version: '1.42.0',
    date: '2026-04-28',
    title: 'Known Malicious IPs — cross-tenant threat-intel admin page',
    changes: [
      'New Settings → Known Malicious IPs page. When an analyst confirms an IP is malicious during a tenant investigation, adding it here will fire alerts on matches across every monitored tenant once the detection-side reader rule ships.',
      'Open read for everyone; Add / Edit / Delete restricted to admins.',
      'IPv4 and IPv6 supported; entries are auto-canonicalized (e.g. "2001:DB8::1" → "2001:db8::1") so a value can only exist once. Re-adding the same IP overwrites type / source / notes but keeps the original first-seen date.',
      'Type field offers eight common categories (c2, phishing, scanner, tor_exit, known_proxy, malware_drop, exfil, unknown) plus an "Other..." option for free-text labels.',
    ],
  },
  {
    version: '1.41.3',
    date: '2026-04-28',
    title: 'Auth App Policy + AiTM URL Defang Tweaks',
    changes: [
      'Authentication App Policy Evaluation: "success" entries no longer render in green. A successful policy evaluation can describe a legitimate sign-in OR an attacker who cleared MFA — green was misleading. Failures still render in red.',
      'AiTM Clicked URL defang now lands on the dot between the domain and the TLD (e.g. "applebcredentialing[.]com") instead of the first dot in the URL.',
    ],
  },
  {
    version: '1.41.2',
    date: '2026-04-28',
    title: 'AiTM Clicked URL — Defanged + Copy Button',
    changes: [
      'Clicked URLs in AiTM alerts are no longer rendered as clickable links — the first "." is replaced with "[.]" so an investigator cannot accidentally navigate to the original (potentially malicious) page. Hover the URL row to reveal a copy button that copies the original URL.',
    ],
  },
  {
    version: '1.41.1',
    date: '2026-04-28',
    title: 'AiTM Click Gap — Friendlier Wording',
    changes: [
      'AiTM click correlation panel now reads "35 seconds" / "2 minutes 15 seconds" instead of the abbreviated "35s" / "2m 15s".',
    ],
  },
  {
    version: '1.41.0',
    date: '2026-04-28',
    title: 'AiTM Click Context + Auth App Policy Detail',
    changes: [
      'Alerts and Test Run Results expanded rows now show an "AiTM Click Correlation" panel for AiTM rule matches — surfaces the clicked URL (as a clickable link), the click timestamp, and the click → sign-in delay so the technician can factor the originating click into their assessment.',
      'Same panels now also show "Authentication App Policy Evaluation" details from graph_signin events as readable phrases (e.g. "Number matching success — correct number entered"). Wording matches Microsoft Graph\'s documented enum values.',
    ],
  },
  {
    version: '1.40.4',
    date: '2026-04-28',
    title: 'AiTM Trusted Pairs — Tighter Form Alignment',
    changes: [
      'Add Trusted Pair form: every input now has the exact same height and every column the same vertical footprint, regardless of whether the column has an "Any (*)" checkbox below its input.',
    ],
  },
  {
    version: '1.40.3',
    date: '2026-04-28',
    title: 'AiTM Trusted Pairs — Add Form Polish',
    changes: [
      'Add Trusted Pair form: input heights now line up uniformly across the row, and the App Display Name / App ID separator reads "AND / OR" to make it clear that filling both is allowed.',
    ],
  },
  {
    version: '1.40.2',
    date: '2026-04-28',
    title: 'AiTM Trusted Pairs — Clearer App Match Field',
    changes: [
      'On the Add Trusted Pair form, App Display Name and App ID are now visually grouped under a single "App match *" header with an OR separator between them, making it clear that you can fill in either field or both.',
    ],
  },
  {
    version: '1.40.1',
    date: '2026-04-28',
    title: 'AiTM Trusted Pairs — Cleaner Add Form',
    changes: [
      'Add Trusted Pair form fields are now arranged in a single row matching the column order of the table below — Tenant, Host, App Display Name, App ID, Notes, Add — so adding an entry feels like filling in a row directly.',
    ],
  },
  {
    version: '1.40.0',
    date: '2026-04-28',
    title: 'AiTM Trusted Pairs — Wildcard Support',
    changes: [
      'AiTM Trusted Pairs now supports wildcards (*) on Tenant, Host, and App fields. Use "All tenants (*)" in the tenant dropdown for cross-tenant rules; use the "Any host (*)" or "Any app (*)" checkboxes for broader matching. Wildcard rows are highlighted with amber badges in the table.',
      'You cannot wildcard all three (tenant + host + app) on a single row — that would suppress every alert. Both the form and the API will reject this.',
    ],
  },
  {
    version: '1.39.2',
    date: '2026-04-28',
    title: 'AiTM Admin Pages — Inline Edit Added',
    changes: [
      'You can now edit existing entries on the AiTM Trusted Domains and AiTM Trusted Pairs pages without deleting and re-adding them. Click the pencil icon on any row to edit Notes (for both pages) plus App Display Name and App ID (Pairs only). Tenant and host stay locked since they are the matching identity — change those by deleting and re-adding.',
    ],
  },
  {
    version: '1.39.1',
    date: '2026-04-28',
    title: 'AiTM Admin Pages — Add/Delete Now Visible to Admins',
    changes: [
      'Fixed an auth wiring bug that hid the Add form and delete buttons on the new AiTM Trusted Domains and AiTM Trusted Pairs pages, even for users in the admin group.',
    ],
  },
  {
    version: '1.39.0',
    date: '2026-04-28',
    title: 'AiTM Trust Overrides + Alerts Layout Tweaks',
    changes: [
      'New Settings pages — "AiTM Trusted Domains" and "AiTM Trusted Pairs" — let security admins manage the allowlists used by the AiTM detection rule to suppress known-good B2B partner SaaS clicks. Anyone can view; only admins can add or remove entries.',
      'Alerts expanded-row reordered: Raw Event JSON now always renders at the bottom; IP Enrichment Record sits above it and uses the same gray title styling for visual consistency.',
    ],
  },
  {
    version: '1.38.2',
    date: '2026-04-27',
    title: 'Detection Rules — CTAP Toggle Copy Wrap',
    changes: [
      'CTAP suppression toggle helper text now wraps the parenthetical clarifier onto its own line for readability.',
    ],
  },
  {
    version: '1.38.1',
    date: '2026-04-27',
    title: 'Detection Rules — CTAP Toggle Copy',
    changes: [
      'Tightened the helper text on the new "Skip if CTAP Inbound Trust" toggle.',
    ],
  },
  {
    version: '1.38.0',
    date: '2026-04-27',
    title: 'Detection Rules — CTAP Inbound Trust Suppression',
    changes: [
      'New "Skip if CTAP Inbound Trust" suppression toggle for detection rules. When enabled, B2B guest alerts are suppressed when the resource tenant has explicitly configured Cross-Tenant Access Policy inbound trust (MFA, compliant device, or hybrid join) for the guest\'s home tenant — reduces false positives for known partner organizations.',
      'Suppression is self-contained: the guest\'s home tenant is read automatically from the sign-in event; no per-rule configuration required. Available alongside the existing four suppression toggles in the rule edit and create drawers.',
    ],
  },
  {
    version: '1.37.17',
    date: '2026-04-24',
    title: 'Aggregation Alerts — MRN Redaction Added',
    changes: [
      'Subject lines containing a medical record number pattern (e.g. "MRN 123456", case-insensitive) are now replaced with "XXXXX MRN XXXXXX (redacted)" alongside the existing DOB redaction.',
    ],
  },
  {
    version: '1.37.16',
    date: '2026-04-24',
    title: 'Aggregation Alerts — Collapsed Subject List + PHI Redaction',
    changes: [
      'Mailboxes with more than 3 email subjects are now collapsed by default, showing only the first 3 with a "Show N more…" toggle to reduce visual noise.',
      'Subject lines containing a date-of-birth pattern (e.g. "DOB 10/23/1967") are automatically replaced with "XXXXX DOB XX/XX/XXXX (redacted)" to prevent accidental PHI exposure in the portal.',
    ],
  },
  {
    version: '1.37.15',
    date: '2026-04-24',
    title: 'Aggregation Alerts — Repeated Emails Grouped with Access Count',
    changes: [
      'In the Mailboxes Accessed summary, emails with the same subject and size are now shown as a single row with a ×N count (e.g. "New response for Purchase Authorization Form ×8"). Rows are sorted by access count so the most-accessed emails appear first.',
    ],
  },
  {
    version: '1.37.14',
    date: '2026-04-24',
    title: 'Aggregation Alerts — Duplicate Emails Suppressed in Mailboxes Accessed',
    changes: [
      'The Mailboxes Accessed summary now correctly deduplicates emails that were accessed multiple times. Previously, the same email (same subject and size) could appear once per access event. It now appears once, regardless of how many times it was read.',
    ],
  },
  {
    version: '1.37.13',
    date: '2026-04-24',
    title: 'Aggregation Alerts — Email Subjects Now Shown Under Each Accessed Mailbox',
    changes: [
      'In the Mailboxes Accessed summary for aggregation alerts, each mailbox now shows a table of the specific emails that were read: Subject (hover for full text) and size. Items are deduplicated across multiple access events, so each email appears once even if it was accessed several times. Applies to both the Alerts page and the Test Run Results page.',
    ],
  },
  {
    version: '1.37.12',
    date: '2026-04-24',
    title: 'Aggregation Alerts — Source Event Details Now Show Raw JSON',
    changes: [
      'Under "Source Event Details" for aggregation rule alerts, each underlying event now renders as a collapsible raw JSON entry with a Copy button — the same pattern as the Raw Event JSON and IP Enrichment Record panels. The entry label shows the timestamp, operation, and target mailbox so events are easy to identify before expanding. Previously a formatted field grid was shown; the raw JSON gives investigators complete data and easy copy access for further analysis.',
    ],
  },
  {
    version: '1.37.11',
    date: '2026-04-24',
    title: 'Aggregation Alerts — Mailboxes Accessed Summary Now Immediately Visible',
    changes: [
      'For aggregation rule alerts (e.g. "Anomalous Mailbox Access"), the expanded row now prominently shows a "Mailboxes Accessed" summary listing each target mailbox, the number of access events, and the total items read — without requiring any click. The detailed per-event data (individual timestamps, folders, client info) is still available but moved into a collapsed "Source Event Details" section alongside IP Intelligence and Raw Event JSON. Investigators can immediately see which mailboxes were accessed and how many items were read, then drill down only if needed.',
    ],
  },
  {
    version: '1.37.10',
    date: '2026-04-24',
    title: 'Aggregation Alerts — Source Events Panel Now Loads Correctly',
    changes: [
      'Fixed a bug where the Source Events panel for aggregation alerts always showed "Source events blob not found." even when the blob existed. The cause was an API storage context scoping error introduced during initial implementation — the blob download code was reading a null variable instead of creating a valid Azure Storage connection. The panel now loads and displays the underlying Exchange audit events correctly.',
    ],
  },
  {
    version: '1.37.9',
    date: '2026-04-24',
    title: 'Aggregation Alerts — Exchange Fields Added to Source Events Panel',
    changes: [
      'The Source Events panel for aggregation alerts (rule-008 "Anomalous Mailbox Access", rule-014 "Mailbox Accessed by Non-Owner") now shows three additional fields for each underlying Exchange event: Folders (which mailbox folders were accessed, with item count), Access Type (Bind or Sync — distinguishes individual reads from bulk sweeps), and Logon Type (Owner / Delegate / Admin). Previously only the basic Timestamp, User, Operation, and IP fields were shown; these additions provide the primary forensic evidence investigators need to understand the scope and method of the access.',
    ],
  },
  {
    version: '1.37.8',
    date: '2026-04-24',
    title: 'Aggregation Alerts — Source Events Now Visible in Portal',
    changes: [
      'Expanding an aggregation rule alert (e.g. "Anomalous Mailbox Access") now shows a Source Events panel listing each underlying Exchange audit event that contributed to the alert. Previously the expanded row showed only an aggregation summary (user count, folder count, etc.) with no way to drill into the individual events. Each entry in the new panel is collapsible and shows timestamp, user, mailbox owner, operation, IP, client app, correlation ID, and session ID — the fields investigators need to act on the alert. Applied to both the Alerts page and the Test Run Results page.',
    ],
  },
  {
    version: '1.37.7',
    date: '2026-04-24',
    title: 'Alerts — App Name Shown for Service Principal Sign-Ins',
    changes: [
      'On the Alerts page and Test Run Results page, when an alert\'s User column would have shown an unreadable PUID (SID-shaped `S-1-8-...` string — how Exchange Online logs app-only service principal access), the column now shows `App: {app name}` (e.g. `App: Druva M365 Advanced`) so analysts can immediately see which application is the actor. Hovering reveals the raw PUID, and Copy still copies the raw identifier for technical drill-down. Non-PUID user IDs (regular UPNs, guest accounts) are unchanged.',
    ],
  },
  {
    version: '1.37.6',
    date: '2026-04-23',
    title: 'DEV Environment Tab Icon',
    changes: [
      'The DEV portal now shows an amber shield icon in the browser tab, distinct from the blue shield used in production. Makes it easy to tell at a glance which environment you\'re working in when PROD and DEV tabs are open side-by-side.',
    ],
  },
  {
    version: '1.37.5',
    date: '2026-04-23',
    title: 'Browser Tab Icon',
    changes: [
      'The browser tab for the portal now shows a blue shield-check icon (matching the sidebar brand) instead of the default browser globe, making the tab easier to spot when several tabs are open.',
    ],
  },
  {
    version: '1.37.4',
    date: '2026-04-23',
    title: 'Tenant Onboarding — Domain Required + Updated Exchange Service Principal',
    changes: [
      'The Domain field in the tenant onboarding wizard is now required — the Next button stays disabled until both a Tenant ID and a Domain are provided. Previously the label said "Domain Lookup (optional)" but the backend has always required a domain, so submitting without one produced a confusing "Failed to create tenant — domain is required" error at the end of the wizard. Now that gap is closed at the form level.',
      'Updated the Exchange Service Principal registration command shown to client admins in step 3 of onboarding. The `-AppId` GUID changed from `bfb9fefd-…` to `8bd644d1-64a1-4d4b-ae52-2e0cbf64e373`, reflecting the current Microsoft app registration for Exchange audit log collection. Client admins copying the command from the portal will now register the correct service principal.',
    ],
  },
  {
    version: '1.37.3',
    date: '2026-04-22',
    title: 'Baseline Deviation Rules — Event Types Now Visible and Editable',
    changes: [
      'Fixed a bug in the rule editor where editing a Baseline Deviation rule silently rewrote its `event_types` back to `["signin"]`, regardless of what was originally in the rule. Any rule that was configured for `graph_signin` (or both types) was being silently collapsed on every save. The editor now shows real Event Types checkboxes for Baseline Deviation rules — the same control the IP Flag Match editor already had — so the configured event types are visible and honored on save.',
    ],
  },
  {
    version: '1.37.2',
    date: '2026-04-21',
    title: 'Baseline Editor Simplified — IP Matching Mode Dropdown Removed',
    changes: [
      'Removed the "IP matching mode" dropdown from the rule editor\'s Baseline section — it overlapped with the existing "Match IPv6 Addresses by /64 Prefix" suppression toggle. Now when a rule has the 30-day window selected AND the IPv6 /64 prefix toggle on, the rule automatically reads from the 30-day baseline\'s precomputed prefix column (a small perf win). Only one control to reason about; same result.',
    ],
  },
  {
    version: '1.37.1',
    date: '2026-04-21',
    title: 'Baseline Dropdowns Now Shown Only When Relevant',
    changes: [
      'The new "Baseline window" and "IP matching mode" dropdowns from v1.37.0 now appear only when the "Skip if IP in User Baseline" suppression toggle is enabled — they have no effect unless that suppression is active, so hiding them by default keeps the rule editor tighter for rules that don\'t use user-baseline suppression. Turning off the toggle also clears the baseline settings from the rule\'s parameters on the next save.',
    ],
  },
  {
    version: '1.37.0',
    date: '2026-04-21',
    title: 'Rule Editor — 30-Day Baseline Opt-In',
    changes: [
      'Added a new "Baseline" section to the rule editor with two controls. "Baseline window" chooses how much sign-in history counts as normal for a user: 7 days (default, unchanged) or 30 days (captures occasional client sites, hotel Wi-Fi, and home-office IPs — reduces false positives on "new IP" rules). "IP matching mode" — only available when the 30-day window is selected — chooses between exact IP match (default) and prefix match (IPv4 exact, IPv6 /64 prefix). Prefix mode is useful because mobile IPv6 host bits rotate every few hours, so exact matching rarely hits.',
      'Existing rules are unaffected — the 7-day / exact-IP behavior remains the default for every rule. The new controls are a no-op in the detection pipeline until the follow-up Databricks notebook ships later this week; shipping the portal UI first means rule authors can queue up the opt-in now.',
    ],
  },
  {
    version: '1.36.3',
    date: '2026-04-21',
    title: 'Release Notes Caught Up to Deployed Version',
    changes: [
      'Release notes page now reflects every deployed version — prior micro-patches (1.36.1 CopyButton tooltip polish, 1.36.2 rule editor help-text tightening) have backfilled entries below so the page no longer lags the sidebar version. No functional changes in this release.',
    ],
  },
  {
    version: '1.36.2',
    date: '2026-04-21',
    title: 'Rule Editor — "Exclude Tenants" Help Text Simplified',
    changes: [
      'Reworded the help text under the "Exclude tenants" picker in the rule editor. The previous copy referenced the pre-1.36.0 clone-and-scope workflow and was ambiguous about which tenants were being suppressed. It now reads simply: "Matches from tenants in this list prevents the alert." Works alongside the "Include tenants" picker added in v1.36.0.',
    ],
  },
  {
    version: '1.36.1',
    date: '2026-04-21',
    title: 'Copy Button Tooltip Polish',
    changes: [
      'UI improvements — Tooltips on the small clipboard icons (rule editor, alerts, timeline, etc.) now show just the value being copied. Previously they said "Copy <value>" which duplicated the visible icon and was noisy when the value was a long identifier.',
    ],
  },
  {
    version: '1.36.0',
    date: '2026-04-21',
    title: 'Detection Rules — Per-Rule Tenant Inclusion + Cleaner Rule Chrome',
    changes: [
      'Added an "Include tenants" multi-select to the rule editor, mirroring the existing "Exclude tenants" control. Leaving it empty keeps the rule applying to all tenants (unchanged from today). Populating it narrows the rule to only those tenants — useful when spinning up a tenant-scoped variant of a noisy rule. Include and Exclude can be used together: Include narrows first, Exclude drops second. Sky-blue badges ("Scoped to N tenants") appear on the rule list when a rule has an include scope, alongside the existing amber Exclude badges.',
      'The Rule ID (now a GUID after yesterday\'s pipeline migration) has been removed from the rule list column, the rule editor header, and the Alerts suppression debug line — rule name is the only identifier shown in normal analyst workflow. For engineers who still need the GUID for debugging, every surface that used to show it now has a tiny copy icon that reveals on hover; one click copies the GUID to the clipboard. The legacy rule_id (the old rule-NNN-slug format) is also exposed in the edit drawer\'s muted debug footer when present.',
    ],
  },
  {
    version: '1.35.2',
    date: '2026-04-21',
    title: 'Rule Editor — "Specified Tenant" Renamed to "MSP Tenant"',
    changes: [
      'Suppression toggle label changed from "Skip if IP in Specified Tenant Baseline" to "Skip if IP in MSP Tenant Baseline" (and the help text below it matched). Same label updated on the expanded-row Rule suppressions panel on the Alerts and Test Run Results pages. No behavior change — the toggle still uses the MSP (Blue Alliance) tenant\'s 7-day baseline to suppress familiar-IP noise.',
    ],
  },
  {
    version: '1.35.1',
    date: '2026-04-21',
    title: 'Rule Editor — Excluded Tenants Show Names, Not GUIDs',
    changes: [
      'Fixed a bug introduced in yesterday\'s v1.35.0 release where the new "Exclude tenants" chips and dropdown in the rule editor showed tenant UUID GUIDs instead of friendly names. The underlying data is unchanged — only the display. Hovering any chip now also shows a tooltip with both the friendly name and the UUID for verification.',
    ],
  },
  {
    version: '1.35.0',
    date: '2026-04-21',
    title: 'Detection Rules — Per-Rule Tenant & IP Provider Exclusion',
    changes: [
      'Added a new "Rule Scope" section to the rule editor with two controls that let you scope a rule away from known-noisy sources without cloning. "Exclude tenants" is a multi-select tenant picker — matches from those tenants are suppressed before alert generation. "Exclude IPs from these providers" is a chip input — matches whose IP enrichment shows any of those providers (case-insensitive, e.g. "Zscaler, Inc.") are suppressed across all tenants. Both default to empty; existing rules unchanged.',
      'Typical workflow: on a strict rule like rule-004 (VPN sign-in), add tenants with legitimate VPN egress (e.g. Family Allergy) to "Exclude tenants" so the rule keeps firing for everyone else; then clone the rule with the existing single-tenant scope and loosen the parameters to create a variant tailored to the excluded tenant. Separately, sanctioned SASE/SWG providers (e.g. Zscaler) can be excluded estate-wide.',
      'Rule list view now shows small amber badges ("Excludes N tenants" / "Provider exclusions: N") under the rule name so exclusions are visible at a glance.',
      'Both controls are editable only in TEST mode, read-only in PROD (demote to TEST to change — same as the existing Suppression toggles).',
    ],
  },
  {
    version: '1.34.25',
    date: '2026-04-21',
    title: 'Alerts — Expanded-Row Layout Rebalanced',
    changes: [
      'The expanded-row bottom panels on the Alerts page no longer leave awkward empty columns. "IP Intelligence" has moved out of the bottom 3-column grid up into its own full-width collapsible block above the Parameters table — when expanded, IP / Risk / Country / Provider / Detections now spread horizontally instead of stacking in a narrow column next to taller content.',
      'The remaining bottom grid ("Correlated Sign-in" + "Anomaly / IDs") now renders as 2 columns when there\'s device or sign-in context to show, and collapses to 1 column when there isn\'t — dropping the "No correlated sign-in" stub that previously took up a full third of the row for no benefit.',
    ],
  },
  {
    version: '1.34.24',
    date: '2026-04-21',
    title: 'Test Run Results — 7-Day Baseline Snapshot Now Collapsible',
    changes: [
      'Extends yesterday\'s collapsible-panel change to the "7-Day Baseline Snapshot" section on the Test Run Results page. Both the rich baseline view and the legacy baseline view now collapse behind a chevron and are closed by default, matching the "Source Event" and "IP Intelligence" panels. The "— none available" state keeps its plain-label presentation since there\'s nothing to hide.',
    ],
  },
  {
    version: '1.34.23',
    date: '2026-04-21',
    title: 'Alerts — Source Event + IP Intelligence Panels Now Collapsible',
    changes: [
      'Expanded-row "Source Event" and "IP Intelligence" panels on both the Alerts page and the Test Run Results page are now collapsible and collapsed by default — matching the idiom of the existing "Raw Event JSON" and "IP Enrichment Record" panels. Click the chevron to expand when needed. Previously both panels were always fully expanded, which pushed the downstream panels (Correlated Sign-in, Anomaly/IDs, CA Policies, Rule Suppressions, etc.) well below the fold even though the Source Event data is usually duplicated in the row itself.',
      'The "Authorization Target" block added yesterday for role assignments / policy assignments / locks lives inside the Source Event panel and follows its expanded/collapsed state.',
    ],
  },
  {
    version: '1.34.22',
    date: '2026-04-21',
    title: 'Alerts — Clearer Azure Role Assignment / Authorization Details',
    changes: [
      'Expanded-row detail on the Alerts page and Test Run Results page now clearly surfaces the target resource when an Azure Activity event records an authorization change (role assignment, role definition, policy assignment, deny assignment, or lock). Previously the "Resource" row showed only the role-assignment GUID while the actual resource the role was granted on (e.g., the Key Vault) was buried in the middle of the Resource ID path. A new amber-accented "Authorization Target" block now pulls out the Target Type (e.g., Microsoft.KeyVault/vaults), Target Name (e.g., M365Signal-Keyvault), and the authorization Artifact (e.g., roleAssignments / {guid}).',
      'Always-on hint under that block explains an important Azure limitation: the activity log does NOT record the role definition (what access was granted) or the principal (who received it). Check Access Control (IAM) on the target resource for those details. Hint text is tailored per artifact type (role assignment, policy assignment, lock, etc.).',
    ],
  },
  {
    version: '1.34.18',
    date: '2026-04-20',
    title: 'Detection Rules — IPv6 /64 Prefix Match + Expanded-Row Polish',
    changes: [
      'Added a fifth option to the rule editor suppression section: "Match IPv6 Addresses by /64 Prefix". Unlike the four suppression toggles, this is a modifier on the IP-based tiers (Compliant Device user+IP, Specified Tenant Baseline, User Baseline) — when on, those tiers compare IPv6 addresses at /64 prefix rather than exact match, so residential rotating IPv6 addresses within the same /64 are treated as the same IP. IPv4 is unchanged. Target use case: rules like MFA rejection where an attacker on the victim\'s own /64 is not a primary concern.',
      'UX: Expanded rows on both the Alerts page and the Test Run Results page now have clear visual separation from adjacent rows — the detail content is indented with a blue left accent stripe and a firm bottom border that only appear while the row is expanded. Collapsed-row styling is unchanged.',
    ],
  },
  {
    version: '1.34.16',
    date: '2026-04-20',
    title: 'Detection Rules — User Baseline Suppression',
    changes: [
      'Added a fourth suppression toggle to the rule editor: "Skip if IP in User Baseline". When on, the pipeline suppresses a match if the event\'s client IP is already in the event user\'s own 7-day sign-in baseline — useful for user-centric rules (e.g., MFA rejection) where an alert from a familiar IP is overwhelmingly a user fumble rather than a real threat. No dependent fields (scope is the event\'s own user). Stacks with the other three suppression tiers — any tier firing suppresses the alert.',
      'Trade-off to be aware of: this tier will not catch an attacker routing through the victim\'s own network (same home router, same SOCKS proxy, session-token replay, or an insider on the LAN). Enable only on rules where same-network compromise is not a primary concern.',
      'Expanded-row "Rule suppressions" panel on both the Alerts page and the Test Run Results page now shows this fourth tier when active, alongside the existing three.',
    ],
  },
  {
    version: '1.34.15',
    date: '2026-04-20',
    title: 'Detection Rules — Tenant Baseline Suppression',
    changes: [
      'Added a third suppression toggle to the rule editor: "Skip if IP in Specified Tenant Baseline". When on, the pipeline suppresses a match if the event\'s IP already appears in the specified tenant\'s 7-day sign-in baseline. Stacks with the existing Compliant Device and DRMM suppression tiers — any tier firing suppresses the alert. Editable on TEST rules; read-only on PROD rules (demote to TEST to change, same as the other two toggles). Clones from a PROD rule carry the toggle forward automatically.',
      'The expanded row "Rule suppressions" panel on both the Alerts page and the Test Run Results page now reflects all three tiers (was previously only on the Alerts page and only showed the first two). An active suppression tier is marked with a ✓ next to its name so investigators can see at a glance which tiers are configured on the rule that produced the alert.',
    ],
  },
  {
    version: '1.34.12',
    date: '2026-04-20',
    title: 'Detection Alerts — UX Polish',
    changes: [
      'Target Resource Display Name is now emphasised on consent-to-app alerts (e.g., "Rare application consent") — the name of the app being granted access stands out from the adjacent Object ID, UPN, and Type rows so investigators can see at a glance what consent was granted.',
      'Date-range presets on the Alerts page now show an active state: when the current From / To exactly matches 24h, 7d, 30d, or All, that button is highlighted. Manually editing a date off a preset clears the highlight; dialing back onto a preset re-applies it automatically.',
      'Filter bar reorganised — "Show closed" has moved to sit beside "Show Test Alerts" so the two view-scope toggles are together. New order: Search / Date range / Show closed / Show Test Alerts / Timezone.',
    ],
  },
  {
    version: '1.34.8',
    date: '2026-04-20',
    title: 'Detection Alerts — Date Range Filter',
    changes: [
      'The "Last 24h only" checkbox on the Alerts page has been replaced with a From / To date range picker plus quick-select buttons for 24h, 7d, 30d, and All. The view still defaults to the last 1 day, but now you can scope to any window — e.g., to investigate alerts from a specific incident date or review a longer history. Filter semantics are unchanged: an alert is included if its generation time OR underlying event time falls inside the selected range.',
    ],
  },
  {
    version: '1.34.7',
    date: '2026-04-20',
    title: 'No More Bounce-Back to Home on Navigation',
    changes: [
      'Fixed an intermittent issue where clicking a nav link (most noticeably "View Alerts" from the Home page) would briefly load the target page and then jump back to Home. The portal now performs a silent iframe-based token refresh when the access token nears expiry, avoiding the full-page Microsoft login round-trip that was landing users at the home URL. When a full-page re-login is genuinely required (expired session, policy change), the user now returns to the page they were navigating to instead of being dropped at Home.',
    ],
  },
  {
    version: '1.34.6',
    date: '2026-04-18',
    title: 'Azure Activity Alerts — Resource Details',
    changes: [
      'Expanded row on Alerts and Test Run Results now surfaces Azure resource details for azure_activity alerts (e.g., rule-017-new-azure-resource). Shows Operation (human-readable name like "Create or Update Disk" plus the raw action code), Result, Resource Group, Resource Type, Resource Name, Caller (with user/app indicator), Subscription, and the full Resource ID — previously these fields were only visible in the Raw Event JSON.',
    ],
  },
  {
    version: '1.34.4',
    date: '2026-04-17',
    title: 'Refresh Buttons Actually Refresh',
    changes: [
      'Clicking Refresh on the Alerts page now pulls the latest data immediately — no more hard refresh (Ctrl+Shift+R) required to see new alerts. The underlying fix applies to every page with a Refresh button, so Cases, Tenants, Alert Rules, and the other list pages also now reliably show the newest data on demand.',
      'On the Alerts page, Refresh also reloads Test Results when "Show Test Alerts" is enabled, so one click refreshes everything visible on the page.',
    ],
  },
  {
    version: '1.34.2',
    date: '2026-04-16',
    title: 'Structured Diff for Policy Changes',
    changes: [
      'Modified Properties on alert detail now render a structured, color-coded diff when the old/new values are JSON objects (e.g., Conditional Access policy changes). Instead of two massive JSON blobs, you see only the fields that actually changed — with Added (green), Removed (red), and Changed (amber) badges next to each difference. Metadata noise like id and modifiedDateTime is filtered automatically.',
      'IP Enrichment Record panel now appears on the Alerts page — previously it was only visible on Test Run Results due to a missing field in the API.',
      'Test Run Results expanded row now shows the same Source Event header and Rule/Operation Parameters table as the Alerts page, so inbox rule details (rule name, forward address, etc.) are visible immediately without opening Raw Event JSON.',
    ],
  },
  {
    version: '1.33.5',
    date: '2026-04-15',
    title: 'Target Resource — Object ID',
    changes: [
      'Target Resource panel now shows the target\'s Object ID alongside Display Name, UPN, and Type, so you can immediately see the GUID of the service principal, group, or user that was acted upon without opening Raw Event JSON.',
      'Role.ObjectID is now hidden from Modified Properties since it\'s redundant with information available elsewhere in the event.',
    ],
  },
  {
    version: '1.33.4',
    date: '2026-04-15',
    title: 'Cleaner Source & Target Resource Panels',
    changes: [
      'Source panel now shows just UPN and IP for user-initiated events — the Display Name field was removed because the raw event\'s user displayName is often unrelated to the UPN and misleading. App-initiated events unchanged.',
      'Hid three low-signal properties from Modified Properties: Role.TemplateId, Role.WellKnownObjectName, and TargetId.ServicePrincipalNames. The full list is still in Raw Event JSON if needed. WellKnownObjectName is still used to decorate Role.DisplayName.',
    ],
  },
  {
    version: '1.33.3',
    date: '2026-04-15',
    title: 'Target Resource — Modified Properties Format',
    changes: [
      'Modified Properties in the Target Resource panel now render inline as `{name}: From "old" to "new"` instead of on separate lines, making role assignments and similar changes easier to read at a glance. Role.DisplayName additionally shows the WellKnownObjectName in parentheses (e.g. "Exchange Administrator (ExchangeServiceAdmins)") to clarify which built-in role was granted.',
    ],
  },
  {
    version: '1.33.2',
    date: '2026-04-15',
    title: 'Source Panel on Alerts & Test Run Results',
    changes: [
      'Expanded alert and test result rows now show a "Source" panel above Target Resource, surfacing the initiating user\'s Display Name, UPN, and IP address (or the initiating app\'s name and ID for app-driven actions). Combined with Target Resource, it makes it immediately clear who did what to whom for directory audit events.',
    ],
  },
  {
    version: '1.33.1',
    date: '2026-04-15',
    title: 'Target Resource Panel in Alerts & Test Run Results',
    changes: [
      'Expanded alert and test result rows now show a "Target Resource" panel for directory audit events (role assignments, app consent, group membership changes, etc.) — surfaces the target Display Name, UPN, and Type, plus a full list of Modified Properties with their new values. Makes it immediately clear who or what was granted a privileged role, for example.',
    ],
  },
  {
    version: '1.33.0',
    date: '2026-04-15',
    title: 'Home Page Hub & Alerts Filter Fix',
    changes: [
      'The portal now opens to a home page with two workspaces side by side — Investigations and Alerts. Each card shows live counts (case status breakdown; alert severity breakdown for the past 72 hours) and one-click navigation to the respective area.',
      'Detection Alerts "Last 24h only" filter now includes alerts where the alert was generated OR the underlying security event occurred within the last 24 hours — previously only the alert generation time was checked, which could exclude recently-occurred events processed with a slight delay.',
    ],
  },
  {
    version: '1.32.33',
    date: '2026-04-15',
    title: 'Detection Alerts — CA Policies & Collapsible JSON Panels',
    changes: [
      'Expanded alert rows now show which Conditional Access policies were evaluated — active policies (Applied, Blocked) are shown by default, with a toggle to reveal inactive ones (not applied, not enabled, report-only).',
      'Raw Event JSON and IP Enrichment Record in alert rows are now collapsed by default. Click the header to expand. Raw Event JSON includes a Copy button.',
      'Raw Event JSON and IP Enrichment in Test Run Results rows are also collapsed by default with the same copy button behaviour.',
    ],
  },
  {
    version: '1.32.31',
    date: '2026-04-15',
    title: 'Test Run Results — Conditional Access Policy Visibility',
    changes: [
      'Expanding a test result row now shows which Conditional Access policies were evaluated during the sign-in — including the policy name and whether it was applied, blocked, or report-only. Inactive policies (not applied, not enabled, or report-only not applied) are hidden by default, with a toggle to reveal them.',
      'The device section in test result rows is now labelled "Sign-in Device" (was "Correlated Device") to accurately reflect that it shows the Entra-registered device from the sign-in event.',
      'The compliance field in test results and alerts is now labelled "Intune Compliant" to distinguish it from rule suppression settings.',
    ],
  },
  {
    version: '1.32.30',
    date: '2026-04-14',
    title: 'Alert Rules — Clone Rule & Config Function Support',
    changes: [
      'Clone any rule directly from the Alert Rules page — a "Clone Rule" drawer opens pre-filled with the source rule\'s fields (name, description, severity, cooldown, suppression toggles, parameters). Edit anything before saving; the new rule is created as TEST with a fresh ID.',
      'Config-style Python rules (python_function_config) are now cloneable and editable in the portal. They appear as "Config Fn" in the rules table. The Python function name and raw parameters JSON are preserved through the clone flow.',
      'Fixed: promoting pipeline-created rules (rules originally written by the detection pipeline, not through the portal) to PROD mode no longer throws an error about missing version fields.',
    ],
  },
  {
    version: '1.32.16',
    date: '2026-04-14',
    title: 'Alert Rules — Test Runs Work for All Rule Types',
    changes: [
      'Fixed a second block on python_function test runs — the API was also rejecting them with a "rule type not supported" error. Both the frontend and backend restrictions are now removed; test runs work for all rule types.',
    ],
  },
  {
    version: '1.32.15',
    date: '2026-04-13',
    title: 'Alert Rules — Test Run for All Rule Types',
    changes: [
      'The Play button on the Alert Rules page now works for all rule types, including python_function rules. The on-demand test job already supported them — the portal was incorrectly blocking the button.',
    ],
  },
  {
    version: '1.32.14',
    date: '2026-04-13',
    title: 'Test Run Results — Resizable Columns',
    changes: [
      'Columns in the test run results table are now resizable — drag the divider on the right edge of any column header to adjust its width. Long values truncate with an ellipsis and show the full text on hover.',
    ],
  },
  {
    version: '1.32.13',
    date: '2026-04-13',
    title: 'Test Run Results — Tenant Name Display',
    changes: [
      'The Tenant column in the test run results table now shows the tenant\'s friendly name (e.g., "Acme Corp") instead of the raw tenant GUID — making it easy to identify which client triggered the match when running rules across all tenants. The search/filter box also matches on tenant name.',
    ],
  },
  {
    version: '1.32.12',
    date: '2026-04-13',
    title: 'Alerts — Mailbox Owner Visibility',
    changes: [
      'The expanded alert detail for Exchange events now shows a "Mailbox Owner" field (highlighted in amber) when the accessed mailbox belongs to a different user than the actor — making it immediately clear whose mailbox was accessed in rules like "Anomalous access to other users\' mailboxes"',
    ],
  },
  {
    version: '1.32.11',
    date: '2026-04-13',
    title: 'Alerts Page — Cleaner IP Display',
    changes: [
      'IP addresses on the Detection Alerts page no longer show the source port or IPv6 brackets — you now see just the raw IP (e.g. "2601:46:c97e:9b40:3c41:4517:cabb:5f39" instead of "[2601:46:c97e:9b40:3c41:4517:cabb:5f39]:56294") in the table row, expanded detail, IP Intelligence panel, and baseline deviation fields',
    ],
  },
  {
    version: '1.32.10',
    date: '2026-04-13',
    title: 'Alerts Page — Separate Alert Time and Event Time',
    changes: [
      'The Detection Alerts page now has two time columns instead of one: "Alert Time" (when the alert was generated) and "Event Time" (when the underlying audit event actually happened). Both columns are sortable — click the header to toggle ascending/descending. Default sort is Event Time, newest first, so you see the most recent activity even when test results replay older data',
    ],
  },
  {
    version: '1.32.8',
    date: '2026-04-10',
    changes: [
      'Test run modal now shows the correct results cap of 50 matches — previously displayed 500',
    ],
  },
  {
    version: '1.32.7',
    date: '2026-04-10',
    title: 'Test Alert Visibility Fix',
    changes: [
      '"Last 24hr" filter on the Detection Alerts page no longer hides test alerts that were run recently \u2014 the filter now uses the time the test was executed (alert time) instead of the source event timestamp, so test results from historical data are visible as long as the test was run within the last 24 hours',
    ],
  },
  {
    version: '1.32.6',
    date: '2026-04-10',
    title: 'Rule Suppression Toggles',
    changes: [
      'Detection rules now support two suppression toggles \u2014 "Skip if Compliant Device" suppresses alerts when the event comes from a compliant Entra ID device, and "Skip if DRMM Match" suppresses alerts when the user has a matching device in the DRMM inventory. Both toggles appear in the Suppression section of the Edit Rule and Create Rule drawers (TEST rules only)',
    ],
  },
  {
    version: '1.32.5',
    date: '2026-04-09',
    title: 'Alert Sign-In Correlation & Rule Editing Improvements',
    changes: [
      'Security alerts and risk detections now show whether the related sign-in was successful or failed \u2014 a colored "Sign-in: Success" (green) or "Sign-in: Failed" (red) badge appears on each alert row in the timeline, and the expanded detail shows "Result: Sign-in Failed/Success". Alerts with failed sign-ins are hidden by the "Failed hidden" toggle',
      'Detection rule match conditions now support filtering on fields inside raw event JSON via $.-prefixed paths (e.g. $.riskLevel, $.ClientInfoString) \u2014 previously only top-level columns were supported',
      'ResultStatus field values in the Edit Rule drawer are now workload-aware \u2014 each log type shows its correct values (e.g. signin uses "Success", exchange uses "Succeeded", directory_audit uses "success"/"failure")',
      'Test run errors now show the actual Spark error message instead of a generic failure notice \u2014 errored rules display an orange triangle on the Alert Rules page with a link to the full error detail',
      'TEST rules now have a "Scheduled Testing" toggle \u2014 when enabled, the detection pipeline automatically runs the rule every 30 minutes and writes results to the test results table',
      'Alerts page column filters (Status, Severity) now work correctly and the table header remains visible when filters return no results',
      'Test run failures now display a friendly error message for known issues (e.g. "The Databricks pipeline is already running another job")',
    ],
  },
  {
    version: '1.31.15',
    date: '2026-04-07',
    title: 'Test Run Results Improvements',
    changes: [
      'Test run results page now shows elapsed time and an estimated time remaining while a rule test is executing \u2014 estimate is based on the last completed run duration and turns amber if the job is running longer than expected',
      'Match Reason column now reads as a finding sentence \u2014 IP flag matches display as \u201cIP x.x.x.x identified as vpn + anonymous \u00b7 by Provider, Country \u00b7 Risk: N/100\u201d for faster triage',
      'Baseline Context and Correlated Sign-In evidence panels in expanded test result rows are now data-driven \u2014 any rule type that writes baseline or correlated sign-in data automatically gets the panel without additional frontend changes',
    ],
  },
  {
    version: '1.28.1',
    date: '2026-04-04',
    title: 'AI Rule Builder',
    changes: [
      'New "Create Rule" button on the Alert Rules page \u2014 describe a detection in plain English or paste a Sentinel YAML rule, and AI generates a draft detection rule for review. Edit fields before saving as a TEST rule. Supports both IP flag and log-type match rules',
      'Workload picker constrains AI generation to a specific data source (e.g., sign-in, risk detection, security alert) for more accurate rule drafts',
      'New log-type match rule type \u2014 detect events by log type with optional field/value conditions (e.g., "Operation equals LeakedCredentials" on risk_detection events). Editable in both the Create Rule drawer and the Edit Rule drawer',
      'Rule categories expanded to six options: IP Threat, Identity Risk, Security Threat, Configuration Change, Data Activity, and Operational',
    ],
  },
  {
    version: '1.27.12',
    date: '2026-04-03',
    title: 'Detection Alerts & Alert Rules',
    changes: [
      'New Detection Alerts page \u2014 view automated rule-based detections from M365 audit logs across all tenants. Alerts show severity, source event details, IP intelligence, correlated sign-in device context, and baseline deviations. Acknowledge or close alerts directly from the table',
      'Alerts support expanding rows for full source event detail, raw record viewer, IP copy buttons, and inline column filters (severity, rule, user, status)',
      'New Alert Rules management page (admin-only) \u2014 view and edit detection rule definitions that drive the alerting pipeline. Edit TEST-mode rules (name, severity, parameters, cooldown), promote rules from TEST to PROD, or demote PROD rules back to TEST. Structured parameter editor for IP flag rules; raw JSON editor for Python function rules',
      'Test rule results toggle on the Alerts page \u2014 cycle through "Test hidden" (default), "Test + Prod" (mixed view), and "Test only" to review TEST rule matches before promoting to PROD',
      'Graph API sign-in alerts now render correctly \u2014 alerts from graph_signin events display the proper fields (user, application, resource, location, error details) instead of blank M365 audit field names',
    ],
  },
  {
    version: '1.26.0',
    date: '2026-04-01',
    title: 'DLP Filter & IP Resolution Improvements',
    changes: [
      'New "DLP hidden" toggle in the timeline filter bar \u2014 DlpRuleMatch events are now hidden by default to reduce noise. Toggle the button to reveal DLP activity when needed',
      'IP resolution now caps at 10 candidate IPs per event \u2014 Exchange and SharePoint events with excessive resolved IPs (e.g., 38+) are marked as Unresolved-ServiceIP instead of displaying a noisy list',
      'Fixed IP address validation \u2014 non-IP values (e.g., "true") that occasionally appear in SharePoint audit logs are now rejected instead of being treated as IP addresses',
    ],
  },
  {
    version: '1.25.3',
    date: '2026-03-31',
    title: 'Enriched Sign-In Details & Message Trace Clarity',
    changes: [
      'Sign-in events enriched with Graph API data now display 18+ additional fields \u2014 token type, authentication protocol/requirement, interactive vs non-interactive badge, authentication methods, token issuer, device trust type, token protection status, cross-tenant access, ASN, risk state, risk event types, conditional access status, network location, correlation ID',
      'Failed sign-ins now show the error code and failure reason directly in the expanded detail',
      'Message trace "From IP" column renamed to "Sending Server IP" with tooltip clarifying it is the mail server IP, not the sender\u2019s device \u2014 envelope icon (\u2709) on message trace IPs in All Events',
      'MSFT risk levels showing "hidden" (tenant lacks Entra ID P2) now display as dimmed "(not licensed)" instead of a bold badge',
    ],
  },
  {
    version: '1.24.0',
    date: '2026-03-30',
    title: 'Exchange Configuration Differential View',
    changes: [
      'Exchange Set-* operations (e.g., Set-MailboxJunkEmailConfiguration) now show what changed \u2014 the investigation engine fetches the most recent prior occurrence and computes a diff. Expand the event row and use the "Changes" toggle to see added (green) and removed (red) entries instead of scrolling through 700+ addresses',
      'When no prior baseline exists in the tenant\u2019s history, an amber indicator explains that changes cannot be determined',
      'Timeline description for Set-* operations now includes a diff summary (e.g., "+3 blocked, -1 trusted") at a glance without expanding',
    ],
  },
  {
    version: '1.23.6',
    date: '2026-03-30',
    title: 'Safe IP Filtering & Attacker Window Fixes',
    changes: [
      'Copy-to-clipboard button on all IP address columns \u2014 hover over any IP in the timeline, IP Addresses tab, Devices tab, or Service/Safe IP Events tabs to copy it',
      'Fixed safe IP filtering for lookback sign-in events \u2014 correlation events with safe-classified IPs are now properly hidden when "Safe IPs hidden" is enabled',
      'Fixed safe IP filtering for multi-IP resolved events \u2014 after malicious IPs are stripped from post-window events, remaining all-safe events are now correctly hidden',
      'Fixed attacker window calculation \u2014 Security Alerts, Security Incidents, and Risk Detections no longer seed the attacker window. These are detection timestamps, not attacker activity, and were incorrectly extending the window',
      'SharePoint system events (empty ClientIP + UserAgent) are now filtered from results \u2014 removes backend housekeeping noise like AddedToGroup for "Limited Access System Group"',
    ],
  },
  {
    version: '1.22.2',
    date: '2026-03-20',
    title: 'Infrastructure Migration',
    changes: [
      'Migrated investigation data source to new Databricks workspace and catalog \u2014 same data, improved infrastructure reliability',
    ],
  },
  {
    version: '1.22.1',
    date: '2026-03-18',
    title: 'Security Hardening & UI Cleanup',
    changes: [
      'Added security headers (HSTS, Content-Security-Policy, Referrer-Policy) to protect against HTTPS downgrade attacks and cross-site scripting',
      'Simplified filter bar \u2014 removed the redundant "Service IPs hidden" toggle. Service IPs are now filtered through the "Safe IPs hidden" button',
    ],
  },
  {
    version: '1.22.0',
    date: '2026-03-18',
    title: 'Sign-In Lookback & Service IP Improvements',
    changes: [
      'Automatic sign-in lookback \u2014 when no sign-in events exist in the investigation date range, the runbook now searches up to 90 days prior for correlation context. Lookback sign-ins appear in Sign-In Logs, IP Addresses, and Devices tabs so their IPs can be classified',
      'Lookback events are hidden by default with a "Lookback hidden" toggle \u2014 events with classified IPs (Safe/Suspect/Malicious) stay visible regardless of toggle state',
      'Service IPs are now treated as safe and can be managed directly from the Service IPs tab within a case',
      'IP Addresses table now shows Service IP labels when ISP/Provider data is unavailable',
      'Case dashboard defaults to showing In Progress cases',
      'New Release Notes page accessible from the sidebar \u2014 documents all portal changes per version',
      'UI improvements \u2014 Baseline hours display in selected timezone, resizable columns in IP Addresses table, description tooltips on hover, wider Risk and Type columns',
    ],
  },
  {
    version: '1.21.0',
    date: '2026-03-17',
    title: 'User Behavioral Baseline',
    changes: [
      'User 7-day behavioral baseline now surfaces throughout the investigation \u2014 the case header shows a baseline summary (known IPs, OS, typical hours, location), anomaly badges appear on sign-in events indicating which risk factors were triggered, the IP Addresses table shows which IPs are in the user\'s baseline, and the Devices tab highlights baseline OS matches with time-aware coloring',
      'Service IPs are now treated as safe and can be managed directly from the Service IPs tab within a case',
      'IP Addresses table now shows Service IP labels when ISP/Provider data is unavailable',
      'Case dashboard defaults to showing In Progress cases',
      'Fixed an issue where baseline data was not loading for some tenants',
      'UI improvements \u2014 Resizable columns in IP Addresses table, wider Risk and Type columns in All Events, description tooltips on hover',
    ],
  },
  {
    version: '1.20.0',
    date: '2026-03-17',
    title: 'URL Click \u2192 Phishing Auto-Link',
    changes: [
      'Confirming a URL click as malicious now automatically flags the related email as a phishing entry point \u2014 no need to manually flag both',
      'Message Trace events can now be searched by Network Message ID',
      'Cross-mailbox URL clicks are now resolved \u2014 when a user clicks a link in a shared or delegated mailbox, the original email is retrieved',
    ],
  },
  {
    version: '1.19.0',
    date: '2026-03-17',
    title: 'Devices Tab IP Classification',
    changes: [
      'IP classification buttons now available directly in the Devices tab \u2014 cycle IP status (Default/Safe/Suspect/Malicious) from expanded device detail without switching tabs',
      'Investigation Steps updated with new "Review Devices" step and "Re-run Retrieve Logs" step for phishing investigations',
    ],
  },
  {
    version: '1.18.0',
    date: '2026-03-16',
    title: 'Email Provider IP Filtering',
    changes: [
      'Email provider IPs (gateway/filtering infrastructure) are now automatically filtered from the IP Addresses table to reduce noise',
      'New "Email IPs hidden" toggle lets you show or hide these IPs as needed',
      'URL click classification: when any URL click is confirmed as malicious, non-confirmed clicks are automatically hidden from the All Events timeline',
    ],
  },
  {
    version: '1.17.0',
    date: '2026-03-16',
    title: 'Phishing Entry Point Flagging',
    changes: [
      'Flag message trace events as phishing entry points using the red "Phish" badge in the Message Trace tab',
      'New "Related Recipients" tab appears after flagging \u2014 re-run Retrieve Logs to discover all other recipients who received the same phishing email from the flagged sender',
      'Related Recipients tab shows summary cards (Flagged Senders, Related Messages, Unique Recipients, Delivered/Filtered) with per-sender grouped tables',
    ],
  },
  {
    version: '1.16.0',
    date: '2026-03-13',
    title: 'Devices Tab',
    changes: [
      'New Devices tab aggregates sign-in events by device with compliance summary cards, DRMM inventory matching, and expandable per-device sign-in history',
      'Device names are color-coded based on IP classification status (green = all safe, amber = suspect, red = malicious)',
      'Raw Logs search now queries full raw event JSON for deeper investigation',
    ],
  },
  {
    version: '1.15.0',
    date: '2026-03-13',
    title: 'Security Alert Evidence & Directory Audit Detail',
    changes: [
      'Security Alerts now surface evidence details including URLs, IP verdicts, browser/OS information, and MITRE ATT&CK techniques',
      'Directory Audit events now show human-readable operation labels (e.g., "MFA device added" instead of "Update user") and filter out routine Azure MFA service updates',
    ],
  },
  {
    version: '1.14.0',
    date: '2026-03-13',
    title: 'Directory Audit Rich Detail',
    changes: [
      'Directory Audit events now surface rich detail including MFA device names, authentication methods (email/phone), account enable/disable transitions, and token revocations',
    ],
  },
  {
    version: '1.13.0',
    date: '2026-03-13',
    title: 'Report Improvements',
    changes: [
      'Client Report "Attacker Origin" table now only shows IPs with confirmed malicious activity (excludes IPs with only failed logins)',
      'Investigation results are now organized by tenant in storage for better data management',
    ],
  },
  {
    version: '1.12.0',
    date: '2026-03-12',
    title: 'Create Story Reports',
    changes: [
      'New Create Story feature with two report types: Client Report (executive-friendly summary with key findings and remediation actions) and Technical Report (full investigation tables with color-coded IPs, URLs, and risk scores)',
      'Reports open in a new browser tab as printable HTML with PDF export',
      'Attacker activity window now spans all event types and recalculates when events are dismissed',
      'Attacker Window panel shows first/last attacker activity timestamps with IP attribution',
    ],
  },
  {
    version: '1.11.0',
    date: '2026-03-12',
    title: 'Expandable IP Rows & DRMM Integration',
    changes: [
      'Click any IP address row to see device details from sign-in logs \u2014 hostname, OS, browser, apps, compliance status, and DRMM inventory match',
      'DRMM device inventory integration now working \u2014 managed devices are matched by hostname, username, and IP',
    ],
  },
  {
    version: '1.10.0',
    date: '2026-03-12',
    title: 'Investigation Instructions',
    changes: [
      'New Investigation Instructions page provides a 9-step post-case-creation workflow guide accessible from the sidebar',
    ],
  },
  {
    version: '1.9.0',
    date: '2026-03-12',
    title: 'URL Click Classification',
    changes: [
      'Classify URL click events as Confirmed (red), Suspected (amber), or Benign (green) to track which links the user may have clicked',
      'Classifications are saved per case and persist across page reloads and log re-runs',
    ],
  },
  {
    version: '1.8.0',
    date: '2026-03-12',
    title: 'Event Dismissal',
    changes: [
      'Dismiss individual events in the All Events timeline to focus on what matters \u2014 dismissed events can be reviewed or restored at any time',
      'Three-state toggle: hide dismissed events, show all events, or show only dismissed events',
    ],
  },
  {
    version: '1.7.0',
    date: '2026-03-11',
    title: 'Raw Log Viewer & Multi-IP Fix',
    changes: [
      'View full raw JSON for any event via the code icon \u2014 includes copy-to-clipboard for sharing with other analysts',
      'Fixed excessive IP count in multi-IP resolved events \u2014 Microsoft infrastructure IPs are now properly filtered from session resolution',
    ],
  },
  {
    version: '1.6.0',
    date: '2026-03-11',
    title: 'Failed Event Filtering & IP Table Improvements',
    changes: [
      'New "Failed events" toggle to hide or show failed sign-in events across all event types',
      'IP Addresses table now supports column-level dropdown filters and full sorting on all columns',
      'Failed sign-in events are clearly labeled with [FAILED] in the timeline',
    ],
  },
  {
    version: '1.5.0',
    date: '2026-03-11',
    title: 'Attacker Activity Window',
    changes: [
      'Automatic attacker activity window calculation \u2014 determines the post-compromise token expiry cutoff based on malicious IP sign-ins, remediation events, and per-tenant token lifetime settings',
      'Collapsible panel shows attacker sign-in count, remediation events, and global cutoff time',
      'Optional post-window filter hides non-malicious activity after the attack window closes',
      'Per-tenant token lifetime setting available in admin tenant configuration (default 90 minutes)',
    ],
  },
  {
    version: '1.4.0',
    date: '2026-03-11',
    title: 'Missing IP Inference',
    changes: [
      'Events with no source IP address now show an inferred IP based on the nearest same-user event \u2014 helps identify the source of actions in Exchange, SharePoint, and other services that don\'t always record the client IP',
      'Inferred IPs are visually distinct with confidence indicators: high (green), medium (amber), low (gray)',
    ],
  },
  {
    version: '1.3.0',
    date: '2026-03-10',
    title: 'Service IP Resolution & Enterprise Apps',
    changes: [
      'Exchange and SharePoint events from Microsoft service IPs are now resolved to the user\'s real IP address via session correlation',
      'Enterprise app names (e.g., "Druva M365 Advanced") now appear in timeline event descriptions',
      'Azure Service Tag IP classification identifies Microsoft backend, shared infrastructure, and user-compute IPs',
      'ESTS service-initiated directory audit events are flagged with an unattributable IP indicator',
      'New 4-state IP classification system: Default, Safe, Suspect, Malicious \u2014 replaces the old binary safe/not-safe model',
      'DST-aware timezone selector for all timeline timestamps',
    ],
  },
  {
    version: '1.2.0',
    date: '2026-03-07',
    title: 'Session IP Resolution & Timezone Support',
    changes: [
      'Session IP resolution for Exchange and SharePoint events \u2014 resolves Microsoft service-proxy IPs to the user\'s real IP via AADSessionId correlation',
      'Timezone-aware timestamp display with selector (UTC, Eastern, Central, Mountain, Arizona, Pacific, Hawaii)',
      'Compact case header layout with inline secure score, date pickers, and action buttons',
    ],
  },
  {
    version: '1.1.0',
    date: '2026-02-28',
    title: 'Multi-Log Investigation',
    changes: [
      'Investigation engine now queries 15 log types: sign-in, Exchange, SharePoint, directory audit, message trace, URL clicks, risk detections, risky users, DLP, general audit, Azure activity, security alerts, security incidents, and secure score',
      'IP enrichment powered by ProxyCheck.io threat intelligence \u2014 VPN, proxy, TOR, compromised, and anonymous IP detection',
      'Structured Exchange event view with access type classification (Read, Sync, App Access) and inbox rule parameter extraction',
    ],
  },
  {
    version: '1.0.0',
    date: '2026-02-15',
    title: 'Initial Release',
    changes: [
      'Case management with investigation workflow \u2014 create cases, run automated log retrieval, and review results',
      'Sign-in and Exchange event investigation with risk scoring engine',
      'IP address analysis with geolocation, VPN/proxy detection, and DRMM device inventory matching',
      'Enterprise apps analysis \u2014 identify and flag known vs unknown applications',
      'Tenant onboarding and management for administrators',
      'MSAL.js authentication with Microsoft Entra ID (single-tenant)',
    ],
  },
];

export default releaseNotes;
